import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import WINNING_SCRIPTS, { SCRIPTS_VERSION } from './scripts';
import ExportTab from './ExportTab';
import LocalizeTab from './LocalizeTab';
import VoiceSection from './components/VoiceSection';
import RenderBar from './components/RenderBar';
import MusicLibrary from './components/MusicLibrary';
import VOICE_SHORTLIST from './voiceShortlist.json';
import FeatureBar from './FeatureBar';
import FeatureManager, { loadFeatureConfigs, loadConcepts } from './FeatureManager';
import MusicTrimmer from './MusicTrimmer';
import PREDEFINED_TITLES from './predefinedTitles';
import {
  getLocalTracks, addLocalTrack, removeLocalTrack,
  getLocalTrackB64,
  localMusicUsedBytes, LOCAL_MUSIC_QUOTA
} from './localMusicBank';
import './App.css';
import { useTranslation } from './i18n';
import './LogoExtra.css';
import { SectionLabel } from './StudioWidgets';
import ProgressRail from './ProgressRail';
import ExportQueue from './ExportQueue';
import HOVTab from './HOVTab';
import './ExportQueue.css';
import PerformanceTab from './PerformanceTab';
import SettingsTab from './SettingsTab';
import ServiceBanner from './ServiceBanner';
import BatchStudioPanel from './BatchStudioPanel';
import TabErrorBoundary from './components/TabErrorBoundary';
import './studioStatus.css';
import { makeProgressReporter, clearUploadProgress } from './uploadProgress';
import UploadBar from './components/UploadBar';
import StudioRecap from './components/StudioRecap';
import { videoBreakdown, studioSummary, overNudge } from './studioCounts';
import { STATUS_LABEL, STATUS_COLOR, emptyRun, outcomeEvent, mergeRun, partialLabel, transition, inQueue, inBar, allQueueReady, takeSnapshot, applySnapshot } from './studioStatus';
// signOut / currentUser ont migré dans SettingsTab.js (seuls consommateurs).

const API = process.env.REACT_APP_API_URL || '';

// ── Feature flags (§5) — Overlay & Localize hidden by default. Code kept
//    fully intact and reactivatable by flipping a flag to true. ──
const FEATURE_FLAGS = { overlay: false, localize: false };

// ── Fuzzy search utilities ──────────────────────────────────────────
function normalizeText(s) {
  return (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}
function fuzzyMatch(query, text) {
  const q = normalizeText(query), t = normalizeText(text);
  if (!q) return 0;
  if (t === q) return 0;
  if (t.startsWith(q)) return 1;
  if (t.includes(q)) return 2;
  const words = t.split(/[\s\-_]+/);
  for (const w of words) {
    const sub = w.slice(0, q.length + 2);
    const m = q.length, n = sub.length;
    const d = Array.from({ length: m + 1 }, (_, i) => { const r = new Array(n + 1); r[0] = i; return r; });
    for (let j = 1; j <= n; j++) d[0][j] = j;
    for (let i = 1; i <= m; i++)
      for (let j = 1; j <= n; j++)
        d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (q[i - 1] !== sub[j - 1] ? 1 : 0));
    if (d[m][n] <= Math.max(1, Math.floor(q.length * 0.3))) return 3 + d[m][n];
  }
  return -1;
}

const FEATURES = [
  { key: 'animated_design',  label: 'Animated Design' },
  { key: 'ghost_mannequin',  label: 'Ghost Mannequin' },
  { key: 'product_staging',  label: 'Product Staging' },
  { key: 'virtual_models',   label: 'Virtual Models'  },
  { key: 'ai_backgrounds',   label: 'AI Backgrounds'  },
  { key: 'flat_lay',         label: 'Flat Lay'        },
];

const COPY_STYLES = [
  { key: 'classic', label: 'Classic Winning', icon: '🏆', desc: 'Reference script',           fixed: true  },
  { key: 'twist',   label: 'Twist Copy',      icon: '🌀', desc: 'Same angle, new tone',       fixed: false },
  { key: 'wild',    label: 'Wild Voice',       icon: '🤪', desc: 'Unexpected, viral-first',    fixed: false },
  { key: 'story',   label: 'Storytelling',     icon: '📖', desc: 'Problem → action → result',  fixed: false },
];

  // eslint-disable-next-line
const EMOJI_BANK = [
  { emoji: '👀', label: 'Curiosity' },
  { emoji: '🤯', label: 'Shock'    },
  { emoji: '🔥', label: 'Hype'     },
  { emoji: '💡', label: 'Insight'  },
  { emoji: '⚡', label: 'Urgency'  },
  { emoji: '😍', label: 'Desire'   },
  { emoji: '🎯', label: 'Focus'    },
  { emoji: '✨', label: 'Magic'    },
  { emoji: '💰', label: 'Value'    },
  { emoji: '🚀', label: 'Growth'   },
];

const TITLE_EMOJI_OPTIONS = [
  { value: '',   label: '— none —' },
  { value: '👀', label: '👀 Eyes'       },
  { value: '🔥', label: '🔥 Fire'       },
  { value: '🤯', label: '🤯 Mind blown' },
  { value: '💡', label: '💡 Idea'       },
  { value: '⚡', label: '⚡ Flash'      },
  { value: '😍', label: '😍 Love'       },
  { value: '🎯', label: '🎯 Target'     },
  { value: '✨', label: '✨ Magic'       },
  { value: '💰', label: '💰 Money'      },
  { value: '🚀', label: '🚀 Rocket'     },
  { value: '❌', label: '❌ Cross'       },
  { value: '✅', label: '✅ Check'       },
  { value: '⚠️', label: '⚠️ Warning'   },
  { value: '🎬', label: '🎬 Clap'       },
  { value: '📸', label: '📸 Camera'     },
];

const BANNER_SIZES = { S: 55, M: 70, L: 82, XL: 95 };
const TEXT_SIZES   = { S: 40, M: 68, L: 76, XL: 96 };

const LOGO_POSITIONS = [
  { key: 'tl',  label: '↖ Top L'    },
  { key: 'tc',  label: '↑ Top C'    },
  { key: 'tr',  label: '↗ Top R'    },
  { key: 'bl',  label: '↙ Bottom L' },
  { key: 'bc',  label: '↓ Bottom C' },
  { key: 'br',  label: '↘ Bottom R' },
  { key: 'sub', label: '📺 Subtitles — bottom bar', fullRow: true },
];

const APP_LOGO_SIZES = [
  { key: 'xs', label: 'XS', height: 24 },
  { key: 's',  label: 'S',  height: 32 },
  { key: 'm',  label: 'M',  height: 40 },
  { key: 'l',  label: 'L',  height: 52 },
  { key: 'xl', label: 'XL', height: 68 },
];

const OVERLAY_DIRS = {
  nw: { sym: '↖', rot: -45,  dx: -1, dy: -1 },
  n:  { sym: '↑', rot: 0,    dx:  0, dy: -1 },
  ne: { sym: '↗', rot: 45,   dx:  1, dy: -1 },
  w:  { sym: '←', rot: -90,  dx: -1, dy:  0 },
  _:  { sym: '·', rot: 0,    dx:  0, dy:  0 },
  e:  { sym: '→', rot: 90,   dx:  1, dy:  0 },
  sw: { sym: '↙', rot: -135, dx: -1, dy:  1 },
  s:  { sym: '↓', rot: 180,  dx:  0, dy:  1 },
  se: { sym: '↘', rot: 135,  dx:  1, dy:  1 },
};
const OVERLAY_DIR_ORDER = ['nw','n','ne','w','_','e','sw','s','se'];

// ── Overlay SVG renderers ──────────────────────────────────────────
const OVERLAY_COLORS = [
  { key: 'white',  hex: '#ffffff', label: 'White'  },
  { key: 'red',    hex: '#e53935', label: 'Red'    },
  { key: 'blue',   hex: '#1e88e5', label: 'Blue'   },
  { key: 'yellow', hex: '#fdd835', label: 'Yellow' },
  { key: 'green',  hex: '#43a047', label: 'Green'  },
  { key: 'black',  hex: '#0a0a0a', label: 'Black'  },
];

const OVERLAY_ANIMS = [
  { key: 'static', label: 'Static' },
  { key: 'bounce', label: 'Bounce' },
  { key: 'pulse',  label: 'Pulse'  },
];

function ArrowSVG({ color = '#ffffff', size = 64 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.6))' }}>
      {/* Tige */}
      <rect x="26" y="28" width="12" height="30" rx="4" fill={color} />
      {/* Tête de flèche large et nette */}
      <polygon points="32,2 58,34 42,34 42,34 22,34 6,34" fill={color} />
    </svg>
  );
}

// CrossSVG — vraie croix ✕ (deux barres à 45°), épaisseur grasse
function CrossSVG({ color = '#ffffff', size = 64 }) {
  const s = size / 64;
  const strokeW = 20 * s;
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.6))' }}>
      <line x1="8" y1="8" x2="56" y2="56" stroke={color} strokeWidth={strokeW} strokeLinecap="round" />
      <line x1="56" y1="8" x2="8" y2="56" stroke={color} strokeWidth={strokeW} strokeLinecap="round" />
    </svg>
  );
}

let overlayIdCounter = 1;

const LS_SCRIPTS    = 'timbo_scripts_v2'; // legacy fallback
const LS_VERSION    = 'timbo_scripts_version'; // legacy
const LS_MUSIC_SEL  = 'timbo_music_selected';
const LS_APP_LOGO   = 'timbo_app_logo';
const LS_WATERMARK  = 'timbo_logo';
const LS_BRAND      = 'timbo_brand';
const LS_TITLE      = 'timbo_title';

const DEFAULT_MIX = { useVoice: true, useMusic: true };

const DEFAULT_TITLE = {
  enabled: true, text: '', emoji: '👀', fontSize: 64, padding: 28,
  x: 50, y: 8, timeIn: 0, timeOut: null, bgColor: '#ffffff', textColor: '#0a0a0a',
  mode: 'text', emojiSize: 72, bannerSize: 'L', textSize: 'M', maxLines: 2,
};

function loadTitlePrefs() {
  try { const s = JSON.parse(localStorage.getItem(LS_TITLE)); return s ? { ...DEFAULT_TITLE, ...s } : DEFAULT_TITLE; }
  catch { return DEFAULT_TITLE; }
}
function saveTitlePrefs(d) { try { localStorage.setItem(LS_TITLE, JSON.stringify(d)); } catch {} }


  // eslint-disable-next-line
function extractTitle(script) {
  if (!script) return '';
  const m = script.match(/^[^.!?]+[.!?]/);
  return m ? m[0].trim() : script.slice(0, 60).trim();
}

// ── renderTitleCard ────────────────────────────────────────────────────────
function renderTitleCard(text, fontSize = 64, titlePadding = 28, bgColor = '#ffffff', textColor = '#0a0a0a', bannerWidthPct = 82, maxLines = 2) {
  const CANVAS_W  = 1080;
  const PAD_X     = 48;
  const PAD_Y     = titlePadding;
  const RADIUS    = Math.round(fontSize * 0.35);
  const MAX_TEXT_W = Math.round(CANVAS_W * bannerWidthPct / 100) - PAD_X * 2;
  const WEIGHT    = '600';

  function makeFont(sz) {
    return `${WEIGHT} ${sz}px 'DM Sans', -apple-system, Arial, sans-serif`;
  }

  if (maxLines === 1) {
    const tmp = document.createElement('canvas'); tmp.width = CANVAS_W; tmp.height = 400;
    const tc  = tmp.getContext('2d');
    let sz = fontSize;
    while (sz > 18) {
      tc.font = makeFont(sz);
      if (tc.measureText(text).width <= MAX_TEXT_W) break;
      sz -= 2;
    }
    const FONT   = makeFont(sz);
    const LINE_H = sz * 1.3;
    tc.font = FONT;
    const textW  = tc.measureText(text).width;
    const bubbleW = Math.ceil(textW + PAD_X * 2);
    const bubbleH = Math.ceil(LINE_H + PAD_Y * 2);
    const canvas  = document.createElement('canvas');
    canvas.width  = bubbleW;
    canvas.height = bubbleH;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, bubbleW, bubbleH);
    ctx.fillStyle = bgColor;
    ctx.beginPath();
    ctx.roundRect(0, 0, bubbleW, bubbleH, Math.round(sz * 0.35));
    ctx.fill();
    ctx.fillStyle    = textColor;
    ctx.font         = FONT;
    ctx.textAlign    = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, bubbleW / 2, bubbleH / 2);
    return { png: canvas.toDataURL('image/png'), width: bubbleW, height: bubbleH };
  }

  const FONT   = makeFont(fontSize);
  const LINE_H = fontSize * 1.3;

  function wrapText(ctx, txt, maxW) {
    const MAX_LINES = maxLines;
    function tryWrap(font, w) {
      ctx.font = font;
      const ws = txt.split(' '); const ls = []; let c = '';
      for (const word of ws) {
        const test = c ? c + ' ' + word : word;
        if (ctx.measureText(test).width > w && c) {
          ls.push(c); c = word;
          if (ls.length >= MAX_LINES) { c = ''; break; }
        } else c = test;
      }
      if (c && ls.length < MAX_LINES) ls.push(c);
      return ls;
    }
    let lines = tryWrap(FONT, maxW);
    if (lines.join(' ').trim() !== txt.trim()) {
      let sz = fontSize - 4;
      while (sz >= 28) {
        const f = makeFont(sz);
        const w2 = maxW + Math.round((fontSize - sz) * 0.5);
        const tryLines = tryWrap(f, w2);
        if (tryLines.join(' ').trim() === txt.trim()) { ctx.font = f; return tryLines; }
        sz -= 4;
      }
      ctx.font = makeFont(28);
      return tryWrap(makeFont(28), maxW + Math.round((fontSize - 28) * 0.5));
    }
    return lines;
  }

  const tmp = document.createElement('canvas'); tmp.width = CANVAS_W; tmp.height = 400;
  const tc  = tmp.getContext('2d');
  const lines = wrapText(tc, text, MAX_TEXT_W);

  tc.font = FONT;
  let maxLineW = 0;
  for (const l of lines) maxLineW = Math.max(maxLineW, tc.measureText(l).width);
  const bubbleW = Math.ceil(maxLineW + PAD_X * 2);
  const bubbleH = Math.ceil(lines.length * LINE_H + PAD_Y * 2);

  const canvas  = document.createElement('canvas');
  canvas.width  = bubbleW;
  canvas.height = bubbleH;
  const ctx     = canvas.getContext('2d');
  ctx.clearRect(0, 0, bubbleW, bubbleH);

  ctx.fillStyle = bgColor;
  ctx.beginPath();
  ctx.roundRect(0, 0, bubbleW, bubbleH, RADIUS);
  ctx.fill();

  ctx.fillStyle    = textColor;
  ctx.font         = FONT;
  ctx.textAlign    = 'center';
  ctx.textBaseline = 'middle';
  const textStartX = bubbleW / 2;
  const textStartY = bubbleH / 2 - (lines.length - 1) * LINE_H / 2;
  for (let i = 0; i < lines.length; i++) {
    ctx.fillText(lines[i], textStartX, textStartY + i * LINE_H);
  }

  return { png: canvas.toDataURL('image/png'), width: bubbleW, height: bubbleH };
}

// ── renderEmojiCard ────────────────────────────────────────────────────────
function renderEmojiCard(emoji, emojiPx = 72) {
  const PAD   = Math.round(emojiPx * 0.2);
  const SIZE  = emojiPx + PAD * 2;
  const canvas = document.createElement('canvas');
  canvas.width  = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, SIZE, SIZE);
  ctx.font         = `${emojiPx}px serif`;
  ctx.textAlign    = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(emoji, SIZE / 2, SIZE / 2);
  return { png: canvas.toDataURL('image/png'), width: SIZE, height: SIZE };
}

// ── renderOverlayItemToPng ─────────────────────────────────────────────────
function renderOverlayItemToPng(item) {
  const dirInfo  = OVERLAY_DIRS[item.dir] || OVERLAY_DIRS['ne'];
  const colorHex = (OVERLAY_COLORS.find(c => c.key === item.color) || OVERLAY_COLORS[0]).hex;
  const sz       = Math.max(16, item.size);
  const PAD      = Math.round(sz * 0.15);
  const totalSz  = sz + PAD * 2;

  const canvas  = document.createElement('canvas');
  canvas.width  = totalSz;
  canvas.height = totalSz;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, totalSz, totalSz);

  const rad = (dirInfo.rot * Math.PI) / 180;
  ctx.save();
  ctx.translate(totalSz / 2, totalSz / 2);
  ctx.rotate(rad);
  ctx.translate(-sz / 2, -sz / 2);

  if (item.type === 'arrow') {
    const s = sz / 64;
    ctx.fillStyle = colorHex;
    ctx.beginPath();
    ctx.roundRect(26 * s, 28 * s, 12 * s, 30 * s, 4 * s);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(32 * s,  2 * s);
    ctx.lineTo(58 * s, 34 * s);
    ctx.lineTo( 6 * s, 34 * s);
    ctx.closePath();
    ctx.fill();
  } else {
    // Cross ✕ — two diagonal lines at 45°, épaisseur grasse (matches CrossSVG)
    const strokeW = (20 / 64) * sz;
    ctx.strokeStyle = colorHex;
    ctx.lineWidth   = strokeW;
    ctx.lineCap     = 'round';
    const m = sz * (8 / 64); // margin matching SVG x1=8,y1=8
    ctx.beginPath();
    ctx.moveTo(m, m);
    ctx.lineTo(sz - m, sz - m);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(sz - m, m);
    ctx.lineTo(m, sz - m);
    ctx.stroke();
  }

  ctx.restore();

  return {
    png:    canvas.toDataURL('image/png'),
    width:  totalSz,
    height: totalSz,
  };
}

// ── computeTitlePx ─────────────────────────────────────────────────────────
function computeTitlePx({ cardW, cardH, vidW, vidH, titleX, titleY }) {
  const CANVAS_W = 1080;
  const scale = vidW / CANVAS_W;
  const titlePxW = Math.round(cardW * scale);
  const titlePxH = Math.round(cardH * scale);
  const cx = Math.round(vidW * titleX / 100);
  const cy = Math.round(vidH * titleY / 100);
  return {
    titlePxX: cx - Math.round(titlePxW / 2),
    titlePxY: cy - Math.round(titlePxH / 2),
    titlePxW,
    titlePxH,
  };
}

function formatTime(sec) {
  if (sec == null || isNaN(sec) || !isFinite(sec)) return '--:--';
  const m = Math.floor(sec / 60); const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function loadSavedScripts() { try { return JSON.parse(localStorage.getItem(LS_SCRIPTS)) || {}; } catch { return {}; } }
function persistScripts(d) { try { localStorage.setItem(LS_SCRIPTS, JSON.stringify(d)); } catch {} }
function buildInitialScripts() {
  // timbo_feature_configs_v2 is primary source, timbo_scripts_v2 is legacy fallback
  const savedVersion = parseInt(localStorage.getItem(LS_VERSION) || '0', 10);
  if (savedVersion < SCRIPTS_VERSION) localStorage.setItem(LS_VERSION, String(SCRIPTS_VERSION));
  const legacy = loadSavedScripts();
  let fmConfigs = {};
  try { fmConfigs = JSON.parse(localStorage.getItem('timbo_feature_configs_v2') || '{}'); } catch {}
  const result = {};
  for (const fk of Object.keys(WINNING_SCRIPTS)) {
    const fm = fmConfigs[fk];
    const s  = legacy[fk] || {};
    const classicFM = fm?.validations?.classic_winning && fm?.scripts?.classic_winning;
    const wildFM    = fm?.validations?.wild_voice      && fm?.scripts?.wild_voice;
    const storyFM   = fm?.validations?.storytelling    && fm?.scripts?.storytelling;
    result[fk] = {
      classic: classicFM || ((savedVersion < SCRIPTS_VERSION || !s.classic) ? WINNING_SCRIPTS[fk].base : s.classic),
      twist:   s.twist || '',
      wild:    wildFM  || s.wild  || '',
      story:   storyFM || s.story || s.green || '',
    };
  }
  return result;
}
function loadMusicPref()     { try { return JSON.parse(localStorage.getItem(LS_MUSIC_SEL))  || {}; } catch { return {}; } }
function saveMusicPref(f,t)  { try { localStorage.setItem(LS_MUSIC_SEL, JSON.stringify({ filename: f, startTime: t })); } catch {} }
function loadAppLogo()       { try { return JSON.parse(localStorage.getItem(LS_APP_LOGO))   || null; } catch { return null; } }
function saveAppLogo(d)      { try { localStorage.setItem(LS_APP_LOGO, JSON.stringify(d)); } catch {} }
function clearAppLogo()      { try { localStorage.removeItem(LS_APP_LOGO); } catch {} }
function loadWatermarkPref() { try { return JSON.parse(localStorage.getItem(LS_WATERMARK))  || null; } catch { return null; } }
function saveWatermarkPref(d){ try { localStorage.setItem(LS_WATERMARK, JSON.stringify(d)); } catch {} }
function clearWatermarkPref(){ try { localStorage.removeItem(LS_WATERMARK); } catch {} }
function loadBrand()         { try { return JSON.parse(localStorage.getItem(LS_BRAND)) || { name: '', subtitle: 'VIDEO REMIX STUDIO' }; } catch { return { name: '', subtitle: 'VIDEO REMIX STUDIO' }; } }
function saveBrand(d)        { try { localStorage.setItem(LS_BRAND, JSON.stringify(d)); } catch {} }

function logoFFmpegPos(pos, pad = 24) {
  const x = pos.includes('r') ? `W-w-${pad}` : pos.includes('c') || pos === 'sub' ? '(W-w)/2' : String(pad);
  const y = pos === 'sub' ? `H-h-${pad}` : pos.startsWith('b') ? `H-h-${pad}` : String(pad);
  return { ox: x, oy: y };
}

class AudioMixPreview {
  constructor() {
    this.ctx = null; this.musicSource = null; this.voiceSource = null;
    this.gainMusic = null; this.gainVoice = null; this.playing = false;
  }
  _ensureCtx() {
    if (!this.ctx) this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (this.ctx.state === 'suspended') this.ctx.resume();
  }
  stop() {
    try { this.musicSource?.stop(); } catch {}
    try { this.voiceSource?.stop(); } catch {}
    this.musicSource = null; this.voiceSource = null; this.playing = false;
  }
  async _loadBuffer(url, b64) {
    if (b64) {
      const [, data] = b64.split(',');
      const bin = atob(data); const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      return this.ctx.decodeAudioData(bytes.buffer);
    }
    const resp = await fetch(url); const ab = await resp.arrayBuffer();
    return this.ctx.decodeAudioData(ab);
  }
  async playMix({ musicUrl, musicB64, musicStart = 0, musicVol = 0.3, voiceUrl = null, voiceVol = 1.0, duration = 8 }) {
    this.stop(); this._ensureCtx();
    const ctx = this.ctx; const promises = [];
    if (musicUrl || musicB64) {
      try {
        const buffer = await this._loadBuffer(musicUrl, musicB64);
        const src = ctx.createBufferSource(); src.buffer = buffer;
        this.gainMusic = ctx.createGain(); this.gainMusic.gain.value = musicVol;
        src.connect(this.gainMusic); this.gainMusic.connect(ctx.destination);
        const offset = Math.min(musicStart, Math.max(0, buffer.duration - 1));
        src.start(ctx.currentTime, offset, duration); src.stop(ctx.currentTime + duration);
        this.musicSource = src; this.playing = true;
        promises.push(new Promise(resolve => { src.onended = resolve; }));
      } catch (e) { console.warn('[AudioMixPreview] music error', e); }
    }
    if (voiceUrl) {
      try {
        const buffer = await this._loadBuffer(voiceUrl, null);
        const src = ctx.createBufferSource(); src.buffer = buffer;
        this.gainVoice = ctx.createGain(); this.gainVoice.gain.value = voiceVol;
        src.connect(this.gainVoice); this.gainVoice.connect(ctx.destination);
        src.start(ctx.currentTime, 0, duration); src.stop(ctx.currentTime + duration);
        this.voiceSource = src; this.playing = true;
        promises.push(new Promise(resolve => { src.onended = resolve; }));
      } catch (e) { console.warn('[AudioMixPreview] voice error', e); }
    }
    if (promises.length > 0) await Promise.race(promises);
    this.playing = false;
  }
  updateMusicVolume(vol) { if (this.gainMusic && this.ctx) this.gainMusic.gain.setTargetAtTime(vol, this.ctx.currentTime, 0.05); }
  updateVoiceVolume(vol) { if (this.gainVoice && this.ctx) this.gainVoice.gain.setTargetAtTime(vol, this.ctx.currentTime, 0.05); }
}

const audioPreview = new AudioMixPreview();
let nextId = 1;
let nextJobId = 1;

// ── ComboDetailModal ──────────────────────────────────────────────────────
function ComboDetailModal({ combos, totalCount, onClose, onConfirm, generating }) {
  return (
    <div className="combo-modal-overlay" onClick={onClose}>
      <div className="combo-modal" onClick={e => e.stopPropagation()}>
        <div className="combo-modal-header">
          <span className="combo-modal-title">📦 {totalCount} remixes to generate</span>
          <button className="combo-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="combo-modal-body">
          {combos.map((c, i) => {
            const audioTag = c.voiceLabel
              ? { cls: 'voice',   txt: `🎤 ${c.voiceLabel}` }
              : (c.keepOrig || !c.script)
                ? { cls: 'orig',  txt: '🔊 Original audio' }
                : { cls: 'muted', txt: '🔇 Muted' };
            return (
              <div key={i} className="combo-modal-row">
                <span className="combo-modal-num">{i + 1}</span>
                <div className="combo-modal-info">
                  <span className="combo-modal-script">{c.scriptIcon} {c.scriptLabel}</span>
                  <span className="combo-modal-tags">
                    {c.hookName && <span className="combo-tag hook">🎬 {c.hookName.slice(0, 18)}{c.hookName.length > 18 ? '…' : ''}</span>}
                    {c.hookType && <span className="combo-tag" style={{ background: c.hookType === 'talking' ? '#e8f5e9' : '#e3f2fd', color: c.hookType === 'talking' ? '#1b5e20' : '#0d47a1', border: 'none' }}>{c.hookType === 'talking' ? 'Talking' : 'Visual'}</span>}
                    <span className={`combo-tag ${audioTag.cls}`}>{audioTag.txt}</span>
                    {c.useMusic && <span className="combo-tag music">🎵 + Music</span>}
                    {c.hasTitle && (
                      <span className="combo-tag title">
                        📝 {c.titleOverride ? c.titleOverride.slice(0, 22) + (c.titleOverride.length > 22 ? '…' : '') : 'title'}
                      </span>
                    )}
                  </span>
                  <span className="combo-modal-vid">{c.vidName}</span>
                </div>
              </div>
            );
          })}
        </div>
        <div className="combo-modal-footer">
          <button className="combo-modal-cancel" onClick={onClose}>Cancel</button>
          <button className="combo-modal-confirm" onClick={onConfirm} disabled={generating}>
            {generating ? '⏳ Generating...' : `▶ Generate ${totalCount} remixes`}
          </button>
        </div>
      </div>
    </div>
  );
}

function TitleTestModal({ jpeg, onClose }) {
  return (
    <div className="combo-modal-overlay" onClick={onClose} style={{ zIndex: 9999 }}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          position: 'relative',
          maxWidth: '92vw',
          maxHeight: '88vh',
          borderRadius: 12,
          overflow: 'hidden',
          boxShadow: '0 8px 40px rgba(0,0,0,0.7)',
          background: '#000',
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: 10, right: 12,
            background: 'rgba(0,0,0,0.6)', border: 'none', color: '#fff',
            fontSize: 14, borderRadius: '50%', width: 30, height: 30,
            cursor: 'pointer', zIndex: 2, lineHeight: 1,
          }}
        >✕</button>
        <img
          src={jpeg}
          alt="Title position preview"
          style={{ display: 'block', maxWidth: '92vw', maxHeight: '88vh', objectFit: 'contain' }}
        />
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          background: 'rgba(0,0,0,0.55)', color: '#fff',
          fontSize: 11, padding: '6px 12px', textAlign: 'center',
        }}>
          Canvas preview · position titre sur frame actuel · Cliquer pour fermer
        </div>
      </div>
    </div>
  );
}

// ── OverlayItemPreview — SVG draggable sur la preview vidéo ──────────────
function OverlayItemPreview({ item, containerRef, onMove, onSelect, isSelected }) {
  const dragRef = useRef(null);

  const handleMouseDown = useCallback((e) => {
    e.stopPropagation();
    onSelect(item.id);
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const startPctX = item.x;
    const startPctY = item.y;

    const onMove_ = (me) => {
      const dx = ((me.clientX - startX) / rect.width)  * 100;
      const dy = ((me.clientY - startY) / rect.height) * 100;
      onMove(item.id, {
        x: Math.max(0, Math.min(100, startPctX + dx)),
        y: Math.max(0, Math.min(100, startPctY + dy)),
      });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove_);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove_);
    window.addEventListener('mouseup', onUp);
  }, [item, containerRef, onMove, onSelect]);

  const dirInfo = OVERLAY_DIRS[item.dir] || OVERLAY_DIRS['ne'];
  const sizePx  = item.size * 0.38;
  const animClass = item.animation !== 'static' ? `overlay-anim-${item.animation}` : '';
  const colorHex  = (OVERLAY_COLORS.find(c => c.key === item.color) || OVERLAY_COLORS[0]).hex;
  const rotation = item.type === 'cross' ? 0 : dirInfo.rot;

  return (
    <div
      ref={dragRef}
      onMouseDown={handleMouseDown}
      className={`overlay-item-preview ${animClass} ${isSelected ? 'overlay-item-selected' : ''}`}
      style={{
        position: 'absolute',
        left: `${item.x}%`,
        top:  `${item.y}%`,
        transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
        width:  sizePx,
        height: sizePx,
        cursor: 'grab',
        zIndex: 30,
        userSelect: 'none',
      }}
    >
      {item.type === 'arrow'
        ? <ArrowSVG color={colorHex} size={sizePx} />
        : <CrossSVG color={colorHex} size={sizePx} />
      }
      {isSelected && (
        <div style={{
          position: 'absolute', inset: -3,
          border: '2px dashed rgba(255,255,255,0.8)',
          borderRadius: 4, pointerEvents: 'none',
        }} />
      )}
    </div>
  );
}

export default function App() {
  const [hovState, setHovState] = useState(null);
  // settingsPanel retiré : la sous-nav Settings a disparu (refonte en blocs, SettingsTab.js).
  const [showAddHookModal, setShowAddHookModal] = useState(false);
  const [newHookName, setNewHookName] = useState('');
  const [newHookFile, setNewHookFile] = useState(null);
  const [newHookFiles, setNewHookFiles] = useState([]);
  const [newHookType, setNewHookType] = useState(null);
  const [newHookFeatures, setNewHookFeatures] = useState(['Universal']);
  const [hooksEnabled, setHooksEnabled] = useState(true);
  const [hookUploadProgress, setHookUploadProgress] = useState(null); // { name, percent }
  const [hookPopoverId, setHookPopoverId] = useState(null);
  // Concepts state
  const [selectedConcepts, setSelectedConcepts] = useState(() => { try { const s = localStorage.getItem('timbo_concept_sel'); if (!s) return []; const p = JSON.parse(s); return Array.isArray(p) ? p : [p]; } catch { return []; } });
  const toggleConcept = (key) => setSelectedConcepts(prev => { const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]; localStorage.setItem('timbo_concept_sel', JSON.stringify(next)); return next; });
  const [selectedConceptAudio, setSelectedConceptAudio] = useState(() => {
    try { return JSON.parse(localStorage.getItem('timbo_concept_audio_sel') || '{}'); } catch { return {}; }
  });
  const [savedVOs, setSavedVOs] = useState([]); // populated from FM concepts only
  const [raps, setRaps] = useState([]);
  const [selectedRap, setSelectedRap] = useState(() => localStorage.getItem('timbo_selected_rap') || null);
  const [showAddRapModal, setShowAddRapModal] = useState(false);
  const [rapUploadProgress, setRapUploadProgress] = useState(null);
  const [appTheme, setAppTheme] = useState(() => localStorage.getItem('timbo_theme') || 'light');
  const [appLang, setAppLang] = useState(() => localStorage.getItem('timbo_lang') || 'en');
  const t = useTranslation(appLang);
  const isOwnerUser = (() => { try { return (localStorage.getItem('timbo_user_role') || 'owner') === 'owner'; } catch { return true; } })();
    const [activeTab, setActiveTab] = useState(() => {
  const path = window.location.pathname.replace('/', '') || 'studio';
  const valid = ['studio', 'export', 'scan', ...(FEATURE_FLAGS.localize ? ['localize'] : []), 'settings'];
  return valid.includes(path) ? path : 'studio';
});
useEffect(() => {
  window.history.pushState(null, '', '/' + activeTab);
}, [activeTab]);

  const brandInit = loadBrand();
  const [brandName,     setBrandName]     = useState(brandInit.name     || '');
  const [brandSubtitle, setBrandSubtitle] = useState(brandInit.subtitle || 'VIDEO REMIX STUDIO');
  const [brandDraft,    setBrandDraft]    = useState({ name: brandInit.name || '', subtitle: brandInit.subtitle || 'VIDEO REMIX STUDIO' });
  useEffect(() => {
    document.body.className = appTheme === 'dark' ? 'theme-dark' : '';
    localStorage.setItem('timbo_theme', appTheme);
  }, [appTheme]);

  useEffect(() => {
    localStorage.setItem('timbo_lang', appLang);
  }, [appLang]);

    useEffect(() => { saveBrand({ name: brandName, subtitle: brandSubtitle }); }, [brandName, brandSubtitle]);

  const [apiKey,         setApiKey]         = useState((localStorage.getItem('el_api_key') || '').replace(/\s+/g, ''));
  const [anthropicKey,   setAnthropicKey]   = useState((localStorage.getItem('anthropic_key') || '').replace(/\s+/g, ''));

  // Charger les clés API depuis Railway au démarrage (override localStorage si présentes)
  useEffect(() => {
    fetch((process.env.REACT_APP_API_URL || 'https://timbo-production.up.railway.app') + '/api/config')
      .then(r => r.json())
      .then(cfg => {
        const _role = (() => { try { return localStorage.getItem('timbo_user_role') || 'owner'; } catch { return 'owner'; } })();
        if (_role !== 'guest' && cfg.elApiKey)      { const k = cfg.elApiKey.replace(/\s+/g, '');      setApiKey(k);         localStorage.setItem('el_api_key', k); }
        if (_role !== 'guest' && cfg.anthropicKey)  { const k = cfg.anthropicKey.replace(/\s+/g, ''); setAnthropicKey(k);  localStorage.setItem('anthropic_key', k); }
        if (cfg.brandSubtitle) { setBrandSubtitle(cfg.brandSubtitle); }
      })
      .catch(() => {});
  }, []);
  const [apiKeyDraft,    setApiKeyDraft]    = useState('');
  const [anthropicDraft, setAnthropicDraft] = useState('');
  const [savedFeedback,  setSavedFeedback]  = useState(false);

  const appLogoInit = loadAppLogo();
  const [appLogoBase64,    setAppLogoBase64]    = useState(appLogoInit?.base64 || null);
  const [appLogoSize,      setAppLogoSize]      = useState(appLogoInit?.size   || 'm');
  const [appLogoAlign,     setAppLogoAlign]     = useState(appLogoInit?.align  || 'left');

  const [appLogoDragging,  setAppLogoDragging]  = useState(false);
  const [appLogoUploading, setAppLogoUploading] = useState(false);
  useEffect(() => {
    if (appLogoBase64) saveAppLogo({ base64: appLogoBase64, size: appLogoSize, align: appLogoAlign });
    else clearAppLogo();
  }, [appLogoBase64, appLogoSize]);
  const handleAppLogoFile = useCallback((file) => {
    if (!file) return; setAppLogoUploading(true);
    const reader = new FileReader();
    reader.onload = ev => { setAppLogoBase64(ev.target.result); setAppLogoUploading(false); };
    reader.readAsDataURL(file);
  }, []);
  const removeAppLogo = useCallback(() => { setAppLogoBase64(null); clearAppLogo(); }, []);

  const watermarkInit = loadWatermarkPref();
  const [logoBase64,    setLogoBase64]    = useState(watermarkInit?.base64   || null);
  const [logoScale,     setLogoScale]     = useState(watermarkInit?.scale    ?? 20);
  const [logoPosition,  setLogoPosition]  = useState(watermarkInit?.position || 'tr');
  const [logoEnabled,   setLogoEnabled]   = useState(!!watermarkInit?.base64);
  const [logoDragging,  setLogoDragging]  = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  useEffect(() => { if (logoBase64) saveWatermarkPref({ base64: logoBase64, scale: logoScale, position: logoPosition }); else clearWatermarkPref(); }, [logoBase64, logoScale, logoPosition]);
  useEffect(() => {
    if (!logoBase64) axios.get(`${API}/api/logo`).then(r => { if (r.data.exists) { setLogoBase64(r.data.base64); setLogoEnabled(true); } }).catch(() => {});
  // eslint-disable-next-line
  }, []);
  const handleLogoFile = useCallback(async (file) => {
    if (!file) return; setLogoUploading(true);
    const reader = new FileReader();
    reader.onload = async ev => {
      const b64 = ev.target.result; setLogoBase64(b64); setLogoEnabled(true);
      try { const f = new FormData(); f.append('logo', file); await axios.post(`${API}/api/upload-logo`, f, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 30000 }); } catch {}
      setLogoUploading(false);
    }; reader.readAsDataURL(file);
  }, []);
  const removeLogo = useCallback(async () => {
    setLogoBase64(null); setLogoEnabled(false); clearWatermarkPref();
    try { await axios.delete(`${API}/api/logo`); } catch {}
  }, []);

  const saveApiKeys = useCallback(() => {
    const elKey = apiKeyDraft.replace(/\s+/g, ''); const antKey = anthropicDraft.replace(/\s+/g, '');
    setApiKey(elKey); setAnthropicKey(antKey);
    localStorage.setItem('el_api_key', elKey);
    localStorage.setItem('anthropic_key', antKey);
    setSavedFeedback(true); setTimeout(() => setSavedFeedback(false), 2000);
  }, [apiKeyDraft, anthropicDraft]);

  const openSettings = useCallback((tab = 'keys') => {
    setApiKeyDraft(localStorage.getItem('el_api_key') || '');
    setAnthropicDraft(localStorage.getItem('anthropic_key') || '');
    setBrandDraft({ name: brandName, subtitle: brandSubtitle });
    setSavedFeedback(false);
    setActiveTab('settings');
    setSettingsSection(tab);
  }, [brandName, brandSubtitle]);

  const [settingsSection, setSettingsSection] = useState('brand');
  const [backendHealth, setBackendHealth] = useState(null);
  useEffect(() => {
    fetch((process.env.REACT_APP_API_URL || 'https://timbo-production.up.railway.app') + '/api/health')
      .then(r => r.json()).then(d => setBackendHealth(d)).catch(() => {});
  }, []);

  // ── Hooks library ──
  const [hooks, setHooks] = useState(() => {
    try { return JSON.parse(localStorage.getItem('timbo_hooks') || '[]'); } catch { return []; }
  });
  const saveHooks = (updater) => {
    if (typeof updater === 'function') {
      setHooks(prev => {
        const next = updater(prev);
        localStorage.setItem('timbo_hooks', JSON.stringify(next));
        return next;
      });
    } else {
      setHooks(updater);
      localStorage.setItem('timbo_hooks', JSON.stringify(updater));
    }
  };
  // hookChecked: flat { hookId: bool } — hook is universal, checked = active for ALL remixes
  const [hookChecked, setHookChecked] = useState(() => {
    try { return JSON.parse(localStorage.getItem('timbo_hook_checked') || '{}'); } catch { return {}; }
  });
  const toggleHookCheck = (hookId) => {
    setHookChecked(prev => {
      const next = { ...prev, [hookId]: !prev[hookId] };
      localStorage.setItem('timbo_hook_checked', JSON.stringify(next));
      return next;
    });
  };
  const [hookPreviewId, setHookPreviewId] = useState(null);
  const [hookNameDrafts, setHookNameDrafts] = useState({});
  const hookVideoSrc = (h) => h.blobUrl || (h.serverFilename ? `${API}/api/hook/stream/${encodeURIComponent(h.serverFilename)}` : null);

  const [queue,    setQueue]    = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [reeditTarget, setReeditTarget] = useState(null);
  const [renderQueue, setRenderQueue] = useState([]);
  const [exportRun, setExportRun] = useState(null);             // { ids } of the videos in the current 'Export all' run
  const [validateModal, setValidateModal] = useState(null);
  const canceledJobIds = useRef(new Set());
  const [showGoExportModal, setShowGoExportModal] = useState(false);
  const [selectedQueueIds, setSelectedQueueIds] = useState(new Set());
  const [queueSelectMode, setQueueSelectMode]   = useState(false);

  const [feature,        setFeature]        = useState('animated_design');
  const [featureConfigs, setFeatureConfigs] = useState(() => loadFeatureConfigs());
  const [featureConcepts, setFeatureConcepts] = useState({});
  // Hydrate concepts from IDB on mount
  useEffect(() => {
    loadConcepts().then(c => setFeatureConcepts(c)).catch(() => {});
  }, []); // eslint-disable-line
  const [allScripts,     setAllScripts]     = useState(buildInitialScripts);
  const [selectedStyles, setSelectedStyles] = useState(['classic']);


  const scriptMix = { classic: { ...DEFAULT_MIX }, twist: { ...DEFAULT_MIX }, wild: { ...DEFAULT_MIX }, story: { ...DEFAULT_MIX } };

  const [showComboModal, setShowComboModal] = useState(false);
  const [showFeatureManager, setShowFeatureManager] = useState(false);
  const [scriptEnabled, setScriptEnabled] = useState(true);
  const [conceptsEnabled, setConceptsEnabled] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [musicEnabled, setMusicEnabled] = useState(true);

  const [voices,           setVoices]           = useState([]);
  // Service health surfaced app-wide (ServiceBanner): browser-side ElevenLabs failure, worst severity, Settings card to focus.
  const [elSyncIssue,      setElSyncIssue]      = useState(null);
  const [healthWorst,      setHealthWorst]      = useState(null);
  const [settingsFocus,    setSettingsFocus]    = useState(null);
  const [selectedVoiceIds, setSelectedVoiceIds] = useState([]);
  const lastVoiceoverFilenameRef = useRef(null);

  const fetchVoices = useCallback(async (elApiKey) => {
    try {
      // If ElevenLabs key available, sync real voices from EL API
      const key = elApiKey || apiKey;
      let data;
      if (key) {
        try {
          const r = await axios.get(`${API}/api/voices/sync?apiKey=${encodeURIComponent(key)}`, { timeout: 10000 });
          data = r.data;
          setElSyncIssue(null);
        } catch (e) {
          // No more silent fallback: report ElevenLabs' real answer in the global banner.
          const b = e?.response?.data || {};
          const parts = [b.upstream || e?.response?.status || 'failed', b.elStatus].filter(Boolean).join(' · ');
          console.warn('[voices/sync] ElevenLabs rejected the key stored in this browser:', b);
          setElSyncIssue({
            key: 'elevenlabs-client', service: 'elevenlabs', label: 'ElevenLabs', status: 'error', impact: 'side',
            blocks: 'the API key in this browser is rejected — voiceovers and voice list unavailable',
            check: `voices/sync → ${parts}${b.elMessage ? ` · ${String(b.elMessage).slice(0, 140)}` : ''}`,
          });
          const r = await axios.get(`${API}/api/voices`); data = r.data;
        }
      } else {
        const r = await axios.get(`${API}/api/voices`); data = r.data;
      }
      // Merge with localStorage custom voices (survives Railway redeploy)
      let localCustom = [];
      try { localCustom = JSON.parse(localStorage.getItem('timbo_custom_voices') || '[]'); } catch {}
      const ids = new Set(data.map(v => v.id));
      const merged = [...data, ...localCustom.filter(v => !ids.has(v.id))];
      // Re-push missing custom voices to server silently
      for (const cv of localCustom.filter(v => !ids.has(v.id))) {
        try { await axios.post(`${API}/api/voices`, { id: cv.id, label: cv.label, gender: cv.gender || 'female', category: cv.category || 'premade_female' }); } catch {}
      }
      setVoices(merged);
      setSelectedVoiceIds(prev => {
        if (prev.length && merged.some(v => prev.includes(v.id))) return prev;
        const first = VOICE_SHORTLIST.voices.map(sv => merged.find(v => v.id === sv.id)).find(Boolean) || merged.find(v => v.category === 'premade') || merged[0];
        return first ? [first.id] : [];
      });
    } catch {}
  }, [apiKey]);
  useEffect(() => { fetchVoices(); }, [fetchVoices]);

  // Restore hooks from server on mount
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/hooks`);
        const serverHooks = r.data || [];
        if (!serverHooks.length) return;
        setHooks(prev => {
          const merged = serverHooks.map(sh => {
            const local = prev.find(lh => lh.serverFilename === sh.serverFilename);
            // ALWAYS use serverFilename as id — stable across sessions, matches hookChecked keys
            return { ...sh, id: sh.serverFilename, blobUrl: local?.blobUrl || null, uploading: false, uploadError: false };
          });
          localStorage.setItem('timbo_hooks', JSON.stringify(merged));
          return merged;
        });
      } catch (e) { console.warn('[hooks] server fetch failed', e.message); }
    })();
  }, []); // eslint-disable-line

  // Step 4: FM concepts -> Studio cards
  useEffect(() => {
    const fc = featureConcepts[feature] || {};
    const fmAsmr = (fc.asmr || []).filter(a => a.validated);
    setSavedVOs(prev => {
      const clean = prev.filter(v => !(v.fromFM && v.origin === 'asmr'));
      const inject = fmAsmr.map(a => ({ key: a.id, filename: null, name: a.label, origin: 'asmr', fromFM: true, data: a.data }));
      return [...clean, ...inject];
    });
    const fmWA = (fc.whatsapp || []).filter(a => a.validated);
    setSavedVOs(prev => {
      const clean = prev.filter(v => !(v.fromFM && v.origin === 'whatsapp'));
      const inject = fmWA.map(a => ({ key: a.id, filename: null, name: a.label, origin: 'whatsapp', fromFM: true, data: a.data }));
      return [...clean, ...inject];
    });
    const fmRap = (fc.rap || []).filter(a => a.validated);
    setRaps(prev => {
      const clean = prev.filter(r => !r.fromFM);
      const inject = fmRap.map(a => ({ serverFilename: null, name: a.label, fromFM: true, data: a.data, tags: ['all'] }));
      return [...clean, ...inject];
    });
  }, [feature, featureConcepts]); // eslint-disable-line


  // Restore raps from server on mount
  useEffect(() => {
    (async () => {
      try { const r = await axios.get(`${API}/api/raps`); setRaps(prev => { const fmRaps = prev.filter(rp => rp.fromFM); return [...(r.data || []), ...fmRaps]; }); } catch {}
    })();
  }, []); // eslint-disable-line

  const addVoice = useCallback(async (voiceId, voiceName, gender = 'female') => {
    if (!voiceId.trim()) return;
    try {
      const category = gender === 'female' ? 'premade_female' : 'premade_male';
      const newVoice = { id: voiceId.trim(), label: voiceName.trim() || voiceId.trim(), gender, category };
      const r = await axios.post(`${API}/api/voices`, newVoice);
      setVoices(r.data.voices); setSelectedVoiceIds(prev => [...prev, voiceId.trim()]);
      // Persist to localStorage
      try {
        const localCustom = JSON.parse(localStorage.getItem('timbo_custom_voices') || '[]');
        if (!localCustom.some(v => v.id === newVoice.id)) { localCustom.push(newVoice); localStorage.setItem('timbo_custom_voices', JSON.stringify(localCustom)); }
      } catch {}
    } catch {}
  }, []);

  const moveVoice = useCallback(async (id, newGender) => {
    setVoices(prev => prev.map(v => v.id === id ? { ...v, category: newGender } : v));
    try { await axios.patch(`${API}/api/voices/${id}`, { gender: newGender }); } catch {}
  }, []);

  const removeVoice = useCallback(async (id) => {
    try {
      const r = await axios.delete(`${API}/api/voices/${id}`);
      setVoices(r.data.voices); setSelectedVoiceIds(prev => prev.filter(v => v !== id));
      // Remove from localStorage too
      try { const lc = JSON.parse(localStorage.getItem('timbo_custom_voices') || '[]'); localStorage.setItem('timbo_custom_voices', JSON.stringify(lc.filter(v => v.id !== id))); } catch {}
    } catch {}
  }, []);

  const [replaceAudio,   setReplaceAudio]   = useState(true);
  const [musicBank,      setMusicBank]      = useState([]);
  const [musicStorage,   setMusicStorage]   = useState(null);
  const [localTracks,    setLocalTracks]    = useState(getLocalTracks);
  const [musicSearch,    setMusicSearch]    = useState('');
  const [musicUploading, setMusicUploading] = useState(false);
  const conceptAudioRef = useRef(null);
  const [musicUploadProgress, setMusicUploadProgress] = useState({ active: false, filename: '', pct: 0, error: null });
  const musicScrollRef = useRef(null);
  const scrollThumbRef = useRef(null);
  const handleMusicScroll = useCallback(() => {
    const el = musicScrollRef.current;
    const thumb = scrollThumbRef.current;
    if (!el || !thumb) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight <= clientHeight) {
      thumb.style.display = 'none';
      return;
    }
    thumb.style.display = 'block';
    const trackHeight = clientHeight - 8;
    const thumbHeight = Math.max(20, (clientHeight / scrollHeight) * trackHeight);
    const maxScroll = scrollHeight - clientHeight;
    const top = maxScroll > 0 ? (scrollTop / maxScroll) * (trackHeight - thumbHeight) : 0;
    thumb.style.height = thumbHeight + 'px';
    thumb.style.top = top + 'px';
  }, []);
  // Re-evaluate scroll indicator when the filtered list changes
  useEffect(() => { handleMusicScroll(); });
  const [musicBatchProgress, setMusicBatchProgress] = useState(null);
  const [liveMusicVol,   setLiveMusicVol]   = useState(0.20);
  const [liveVoiceVol,   setLiveVoiceVol]   = useState(1.0);
  const [previewPlaying,  setPreviewPlaying]  = useState(false);
  const [previewProgress, setPreviewProgress] = useState(0);
  const previewTimerRef = useRef(null);
  const PREVIEW_DURATION = 8;
  const musicPrefInit = loadMusicPref();
  const [selectedMusic,  setSelectedMusic]  = useState(musicPrefInit.filename  || 'none');
  const [musicStartTime, setMusicStartTime] = useState(musicPrefInit.startTime || 0);
  const [musicMissing,   setMusicMissing]   = useState(false);
  useEffect(() => { saveMusicPref(selectedMusic, musicStartTime); }, [selectedMusic, musicStartTime]);

  const [endscreenInfo,      setEndscreenInfo]      = useState(null);
  const [endscreenEnabled,   setEndscreenEnabled]   = useState(true);
  const [endscreenUploading, setEndscreenUploading] = useState(false); // eslint-disable-line

  const titleInit = loadTitlePrefs();
  const [titleEnabled,   setTitleEnabled]   = useState(titleInit.enabled);
  const [titleText,      setTitleText]      = useState(titleInit.text);
  const [titleEmoji,     setTitleEmoji]     = useState(titleInit.emoji);
  const [titleFontSize]  = useState(titleInit.fontSize);
  const [titlePadding]   = useState(titleInit.padding);
  const [titleX,         setTitleX]         = useState(titleInit.x);
  const [titleY,         setTitleY]         = useState(titleInit.y);
  const [titleTimeIn,    setTitleTimeIn]    = useState(titleInit.timeIn);
  const [titleTimeOut,   setTitleTimeOut]   = useState(titleInit.timeOut);
  const [titleBgColor]   = useState(titleInit.bgColor);
  const [titleTextColor] = useState(titleInit.textColor);
  const [titleMode,      setTitleMode]      = useState(titleInit.mode || 'text');
  const [bannerSize,     setBannerSize]     = useState(titleInit.bannerSize || 'L');
  const [selectedTitles, setSelectedTitles] = useState([]);
  const [textSize,       setTextSize]       = useState(titleInit.textSize || 'M');
  const [emojiSize,      setEmojiSize]      = useState(titleInit.emojiSize || 72);
  const [titleMaxLines,  setTitleMaxLines]  = useState(titleInit.maxLines || 2);

  const [previewTitle, setPreviewTitle] = useState(null);

  const [titleTestLoading, setTitleTestLoading] = useState(false);
  const [titleTestJpeg,    setTitleTestJpeg]    = useState(null);

  const [customTitleDraft, setCustomTitleDraft] = useState('');
  const [winningTitleDraft, setWinningTitleDraft] = useState('');
  const [showTitleManager, setShowTitleManager] = useState(false);
  const [titleManagerDraft, setTitleManagerDraft] = useState('');
  const [wordingPhrases, setWordingPhrasesState] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem('timbo_wording_v1')); if (Array.isArray(s) && s.length) return s; } catch {}
    return ['Timbo is cooking…', 'Rendering your remixes…', 'Mixing the magic…', 'Almost there…'];
  });
  const saveWordingPhrases = (arr) => { const clean = arr.map(s => String(s)); setWordingPhrasesState(clean); try { localStorage.setItem('timbo_wording_v1', JSON.stringify(clean)); } catch {} };
  const [winningTitles, setWinningTitlesState] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('timbo_winning_v1') || '{}');
      const base = [...(PREDEFINED_TITLES[feature] || []), ...(PREDEFINED_TITLES._universal || [])];
      return saved[feature] !== undefined ? saved[feature] : base;
    } catch { return [...(PREDEFINED_TITLES[feature] || []), ...(PREDEFINED_TITLES._universal || [])]; }
  });
  const saveWinningTitles = useCallback((list) => {
    setWinningTitlesState(list);
    try {
      const saved = JSON.parse(localStorage.getItem('timbo_winning_v1') || '{}');
      saved[feature] = list;
      localStorage.setItem('timbo_winning_v1', JSON.stringify(saved));
    } catch {}
  }, [feature]);
  const removeWinningTitle = useCallback((t) => {
    saveWinningTitles(winningTitles.filter(x => x !== t));
    // Aussi retirer des pills selectedTitles et reset previewTitle
    setSelectedTitles(prev => prev.filter(x => x !== t));
    setPreviewTitle(prev => prev === t ? null : prev);
  }, [winningTitles, saveWinningTitles]); // eslint-disable-line

  // ── OVERLAY states (multi-items) ────────────────────────────────────────
  const [overlayEnabled,   setOverlayEnabled]   = useState(false);
  const [overlayItems,     setOverlayItems]     = useState([]);
  const [selectedOverlay,  setSelectedOverlay]  = useState(null);
  // Brush settings for next item to place
  const [overlayType,      setOverlayType]      = useState('arrow');
  const [overlayDir,       setOverlayDir]       = useState('ne');
  const [overlaySize,      setOverlaySize]      = useState(120);
  const [overlayColor,     setOverlayColor]     = useState('white');
  const [overlayAnimation, setOverlayAnimation] = useState('static');
  // Overlay timing (same as title timing)
  const [overlayTimeIn,    setOverlayTimeIn]    = useState(0);
  const [overlayTimeOut,   setOverlayTimeOut]   = useState(null);

  // ── Overlay undo stack ───────────────────────────────────────────────────
  const overlayHistoryRef = useRef([]); // stack of snapshots

  const pushOverlaySnapshot = useCallback((items) => {
    overlayHistoryRef.current = [...overlayHistoryRef.current.slice(-30), items];
  }, []);

  const undoOverlay = useCallback(() => {
    const history = overlayHistoryRef.current;
    if (history.length < 2) {
      // Only one state or empty — restore to empty
      if (history.length === 1) {
        overlayHistoryRef.current = [];
        setOverlayItems([]);
        setSelectedOverlay(null);
      }
      return;
    }
    const newHistory = history.slice(0, -1);
    overlayHistoryRef.current = newHistory;
    const prev = newHistory[newHistory.length - 1];
    setOverlayItems(prev);
    setSelectedOverlay(null);
  }, []);

  // Cmd+Z / Ctrl+Z global handler for overlay undo
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        // Only intercept if overlay is enabled and not typing in an input/textarea
        const tag = document.activeElement?.tagName?.toLowerCase();
        if (tag === 'input' || tag === 'textarea') return;
        if (!overlayEnabled) return;
        e.preventDefault();
        undoOverlay();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [overlayEnabled, undoOverlay]);

  const previewInnerRef = useRef(null);
  const titleTimelineRef = useRef(null);
  const [draggingTitleHandle, setDraggingTitleHandle] = useState(null);

  // Place a new overlay on click on the preview
  // If click hits an existing item, select it instead of placing a new one
  const handlePreviewClick = useCallback((e) => {
    if (!overlayEnabled) return;
    const container = previewInnerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const xPct = ((e.clientX - rect.left) / rect.width)  * 100;
    const yPct = ((e.clientY - rect.top)  / rect.height) * 100;

    // Check if the click is within ~10% radius of any existing item centre
    const HIT_THRESHOLD = 8; // percent
    const hit = overlayItems.findLast(item => {
      const dist = Math.sqrt(Math.pow(item.x - xPct, 2) + Math.pow(item.y - yPct, 2));
      return dist < HIT_THRESHOLD;
    });

    if (hit) {
      // Select existing item — don't place a new one
      setSelectedOverlay(prev => prev === hit.id ? null : hit.id);
      return;
    }

    // Push current state before adding
    pushOverlaySnapshot(overlayItems);

    const newItem = {
      id:        overlayIdCounter++,
      type:      overlayType,
      dir:       overlayDir,
      x:         Math.max(0, Math.min(100, xPct)),
      y:         Math.max(0, Math.min(100, yPct)),
      size:      overlaySize,
      color:     overlayColor,
      animation: 'static',
      timeIn:    0,
      timeOut:   null,
    };
    const next = [...overlayItems, newItem];
    setOverlayItems(next);
    pushOverlaySnapshot(next);
    setSelectedOverlay(newItem.id);
  }, [overlayEnabled, overlayItems, overlayType, overlayDir, overlaySize, overlayColor, overlayAnimation, pushOverlaySnapshot]);

  const moveOverlayItem = useCallback((id, patch) => {
    setOverlayItems(prev => prev.map(it => it.id === id ? { ...it, ...patch } : it));
  }, []);

  const removeOverlayItem = useCallback((id) => {
    setOverlayItems(prev => {
      const next = prev.filter(it => it.id !== id);
      pushOverlaySnapshot(next);
      return next;
    });
    setSelectedOverlay(prev => prev === id ? null : prev);
  }, [pushOverlaySnapshot]);

  const updateSelectedOverlay = useCallback((patch) => {
    if (!selectedOverlay) return;
    setOverlayItems(prev => prev.map(it => it.id === selectedOverlay ? { ...it, ...patch } : it));
  }, [selectedOverlay]);

  const updateOverlayItem = useCallback((id, patch) => {
    setOverlayItems(prev => prev.map(it => it.id === id ? { ...it, ...patch } : it));
  }, []);

  const selectedItem = overlayItems.find(it => it.id === selectedOverlay) || null;

  const [exportJobs,      setExportJobs]      = useState([]);
  const [showExportQueue, setShowExportQueue] = useState(false);

  useEffect(() => {
    saveTitlePrefs({
      enabled: titleEnabled, text: titleText, emoji: titleEmoji,
      fontSize: titleFontSize, padding: titlePadding,
      x: titleX, y: titleY, timeIn: titleTimeIn, timeOut: titleTimeOut,
      bgColor: titleBgColor, textColor: titleTextColor,
      mode: titleMode, emojiSize, bannerSize, textSize, maxLines: titleMaxLines,
    });
  }, [titleEnabled, titleText, titleEmoji, titleFontSize, titlePadding, titleX, titleY, titleTimeIn, titleTimeOut, titleBgColor, titleTextColor, titleMode, emojiSize, bannerSize, textSize, titleMaxLines]);

  const videoRef = useRef(null);
  const [videoCurrentTime,   setVideoCurrentTime]   = useState(0);
  const [, setVideoDurationState] = useState(0); // eslint-disable-line

  const [generating, setGenerating] = useState(false);
  const [lastGenTotal, setLastGenTotal] = useState(0);
  const [logs,       setLogs]       = useState([]);
  const [results,    setResults]    = useState([]);

  // `logs` state is not rendered anywhere — mirror every entry to the browser
  // console so silent render failures (❌ …) are diagnosable via devtools /
  // read_console_messages, without touching the UI.
  const log = useCallback((msg) => {
    // eslint-disable-next-line no-console
    console.log('[timbo]', msg);
    setLogs(p => [...p, `${new Date().toLocaleTimeString()} — ${msg}`]);
  }, []);
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('timbo_winning_v1') || '{}');
      const base = [...(PREDEFINED_TITLES[feature] || []), ...(PREDEFINED_TITLES._universal || [])];
      setWinningTitlesState(saved[feature] !== undefined ? saved[feature] : base);
    } catch {}
  }, [feature]); // eslint-disable-line

  const getScript = useCallback((k) => (allScripts[feature] || {})[k] || '', [allScripts, feature]);
  const setScript = useCallback((k, val) => {
    setAllScripts(prev => { const next = { ...prev, [feature]: { ...prev[feature], [k]: val } }; persistScripts(next); return next; });
  }, [feature]);

  // Sync FM validated scripts into Studio when feature changes or FM saves
  useEffect(() => {
    const fmScripts = featureConfigs[feature]?.scripts;
    const fmValid   = featureConfigs[feature]?.validations;
    if (!fmScripts || !fmValid) return;
    if (fmValid.classic_winning && fmScripts.classic_winning) setScript('classic', fmScripts.classic_winning);
    if (fmValid.wild_voice      && fmScripts.wild_voice)      setScript('wild',    fmScripts.wild_voice);
    if (fmValid.storytelling    && fmScripts.storytelling)     setScript('story',   fmScripts.storytelling);
  }, [feature, featureConfigs]); // eslint-disable-line


  const resetClassic = useCallback(() => {
    setAllScripts(prev => { const next = { ...prev, [feature]: { ...prev[feature], classic: WINNING_SCRIPTS[feature]?.base || '' } }; persistScripts(next); return next; });
  }, [feature]);



  const toggleStyle = useCallback((key) => {
    setSelectedStyles(p => p.includes(key) ? (p.length > 1 ? p.filter(k => k !== key) : p) : [...p, key]);
  }, []);

  // ASMR concept — generate whisper voiceover




  const selectedMusicRef = useRef(selectedMusic);
  useEffect(() => { selectedMusicRef.current = selectedMusic; }, [selectedMusic]);

  const fetchMusicBank = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/music`); setMusicBank(r.data);
      const cur = selectedMusicRef.current;
      if (cur !== 'none') setMusicMissing(!r.data.some(t => t.filename === cur));
      else setMusicMissing(false);
    } catch {}
    try { const s = await axios.get(`${API}/api/music/storage`); setMusicStorage(s.data); } catch {}
  }, []); // stable — reads selectedMusic via ref
  useEffect(() => { fetchMusicBank(); }, [fetchMusicBank]);

  const handleSelectMusic = useCallback((filename) => {
    setSelectedMusic(filename); setMusicMissing(false);
    if (filename !== selectedMusic) setMusicStartTime(0);
  }, [selectedMusic]);

  const fetchEndscreen = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/endscreen`); setEndscreenInfo(r.data); } catch {}
  }, []);
  useEffect(() => { fetchEndscreen(); }, [fetchEndscreen]);

  // Stabilize selectedMusicInfo across bank refetches (so TRIM doesn't flash off during uploads)
  const selectedMusicInfoRef = useRef(null);
  const _liveInfo = musicBank.find(t => t.filename === selectedMusic) || null;
  if (_liveInfo && selectedMusic !== 'none') selectedMusicInfoRef.current = _liveInfo;
  else if (selectedMusic === 'none') selectedMusicInfoRef.current = null;
  const selectedMusicInfo = _liveInfo || selectedMusicInfoRef.current;
  const activeItem        = queue.find(v => v && v.id === activeId && inQueue(v.studio)) || queue.find(v => v && inQueue(v.studio)) || null;
  const videoDuration     = activeItem ? (activeItem.endTime - activeItem.startTime) : 20;

  const stopPreview = useCallback(() => {
    audioPreview.stop();
    setPreviewPlaying(false); setPreviewProgress(0);
    if (previewTimerRef.current) { clearInterval(previewTimerRef.current); previewTimerRef.current = null; }
  }, []);

  const playPreview = useCallback(async () => {
    if (previewPlaying) { stopPreview(); return; }
    const hasMusic = selectedMusic !== 'none' && selectedMusicInfo;
    let hasVoice = !!lastVoiceoverFilenameRef.current;
    if (hasVoice) {
      try {
        const checkUrl = `${API}/api/download/${lastVoiceoverFilenameRef.current}`;
        const resp = await fetch(checkUrl, { method: 'HEAD' });
        if (!resp.ok) { hasVoice = false; lastVoiceoverFilenameRef.current = null; }
      } catch { hasVoice = false; lastVoiceoverFilenameRef.current = null; }
    }
    if (!hasMusic && !hasVoice) return;
    const localB64 = hasMusic ? getLocalTrackB64(selectedMusic) : null;
    const musicUrl = hasMusic ? `${API}/api/music/stream/${encodeURIComponent(selectedMusic)}` : null;
    const voiceUrl = hasVoice ? `${API}/api/download/${lastVoiceoverFilenameRef.current}` : null;
    setPreviewPlaying(true); setPreviewProgress(0);
    const startAt = Date.now();
    previewTimerRef.current = setInterval(() => {
      const elapsed = (Date.now() - startAt) / 1000;
      setPreviewProgress(Math.min(100, (elapsed / PREVIEW_DURATION) * 100));
      if (elapsed >= PREVIEW_DURATION) stopPreview();
    }, 80);
    try {
      await audioPreview.playMix({ musicUrl, musicB64: localB64, musicStart: musicStartTime, musicVol: liveMusicVol, voiceUrl, voiceVol: liveVoiceVol, duration: PREVIEW_DURATION });
    } catch (e) {
      console.warn('[playPreview] playMix error', e);
    }
    stopPreview();
  }, [selectedMusic, selectedMusicInfo, musicStartTime, liveMusicVol, liveVoiceVol, previewPlaying, stopPreview]);

  const handleLiveMusicVol = useCallback((val) => { setLiveMusicVol(val); audioPreview.updateMusicVolume(val); }, []);
  const handleLiveVoiceVol = useCallback((val) => { setLiveVoiceVol(val); audioPreview.updateVoiceVolume(val); }, []);

  useEffect(() => { stopPreview(); }, [selectedMusic]); // eslint-disable-line
  useEffect(() => () => stopPreview(), []);             // eslint-disable-line

  // I / O keyboard shortcuts for Title timing mark-in / mark-out
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
      if (!titleEnabled) return;
      const vid = document.querySelector('video');
      if (!vid) return;
      const t = parseFloat(vid.currentTime.toFixed(1));
      if (e.key === 'i' || e.key === 'I') {
        e.preventDefault();
        setTitleTimeIn(t);
        const el = document.querySelector('.title-timing-in');
        if (el) { el.classList.remove('timing-flash'); void el.offsetWidth; el.classList.add('timing-flash'); }
      }
      if (e.key === 'o' || e.key === 'O') {
        e.preventDefault();
        setTitleTimeOut(t);
        const el = document.querySelector('.title-timing-out');
        if (el) { el.classList.remove('timing-flash'); void el.offsetWidth; el.classList.add('timing-flash'); }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [titleEnabled]);

  const ensureMusicOnServer = useCallback(async (filename) => {
    if (!filename || filename === 'none') return true;
    try { const r = await axios.get(`${API}/api/music`); if (r.data.some(t => t.filename === filename)) return true; } catch { return true; }
    const b64 = getLocalTrackB64(filename);
    if (!b64) { log(`⚠ Missing file, no local cache for ${filename}`); return false; }
    log(`🔄 Auto re-uploading ${filename} from local cache…`);
    try { await axios.post(`${API}/api/upload-music-b64`, { filename, b64 }, { timeout: 60000 }); await fetchMusicBank(); log(`✅ Re-upload OK: ${filename}`); return true; }
    catch (err) { log(`❌ Re-upload failed: ${err.message}`); return false; }
  }, [fetchMusicBank, log]);

  const fullTitle = titleEnabled
    ? (titleMode === 'emoji' ? (titleEmoji || null) : (titleText + (titleEmoji ? ' ' + titleEmoji : '')).trim() || null)
    : null;

  const activePreviewTitle = previewTitle !== null ? previewTitle : fullTitle;

  const allTitleOptions = titleEnabled && titleMode === 'text'
    ? [...(PREDEFINED_TITLES[feature] || []), ...(PREDEFINED_TITLES._universal || [])].map(t => t + (titleEmoji ? ' ' + titleEmoji : ''))
    : [];
  // eslint-disable-next-line
  const titlesForExport = titleEnabled && titleMode !== 'emoji' && selectedTitles.length > 0 ? selectedTitles : (fullTitle ? [fullTitle] : []);

  const isTitleVisibleAt = useCallback((t) => {
    if (!activePreviewTitle) return false;
    const startOff = activeItem?.startTime || 0;
    const tIn  = startOff + (titleTimeIn || 0);
    const tOut = titleTimeOut != null ? startOff + titleTimeOut : Infinity;
    return t >= tIn && t <= tOut;
  }, [activePreviewTitle, titleTimeIn, titleTimeOut, activeItem]);

  const titleVisible = isTitleVisibleAt(videoCurrentTime);

  const isOverlayVisibleAt = useCallback((t) => {
    if (!overlayEnabled || overlayItems.length === 0) return false;
    const tIn  = overlayTimeIn || 0;
    const tOut = overlayTimeOut != null ? overlayTimeOut : Infinity;
    return t >= tIn && t <= tOut;
  }, [overlayEnabled, overlayItems.length, overlayTimeIn, overlayTimeOut]);

  const overlayVisible = isOverlayVisibleAt(videoCurrentTime);

  const titlePreviewData = useMemo(() => {
    if (!titleEnabled || !activePreviewTitle || titleMode === 'emoji') return null;
    try {
      const result = renderTitleCard(
        activePreviewTitle,
        TEXT_SIZES[textSize] || 68,
        titlePadding,
        titleBgColor,
        titleTextColor,
        BANNER_SIZES[bannerSize] || 82,
        titleMaxLines
      );
      return { png: result.png, cardW: result.width, cardH: result.height };
    } catch { return null; }
  }, [titleEnabled, activePreviewTitle, titleMode, textSize, titlePadding, titleBgColor, titleTextColor, bannerSize, titleMaxLines]);
  const titlePreviewPng = titlePreviewData?.png || null;

  const handleTitleTest = useCallback(() => {
    if (!fullTitle || titleMode === 'emoji') { alert('Enable and fill in the text title'); return; }
    const video = videoRef.current;
    if (!video) { alert('No video loaded'); return; }

    setTitleTestLoading(true);

    const { png, width: cardW, height: cardH } = renderTitleCard(
      activePreviewTitle || fullTitle,
      TEXT_SIZES[textSize] || 68,
      titlePadding,
      titleBgColor,
      titleTextColor,
      BANNER_SIZES[bannerSize] || 82,
      titleMaxLines
    );

    const vidW = video.videoWidth  || 1080;
    const vidH = video.videoHeight || 1920;

    const { titlePxX, titlePxY, titlePxW, titlePxH } = computeTitlePx({
      cardW, cardH, vidW, vidH, titleX, titleY,
    });

    const canvas = document.createElement('canvas');
    canvas.width  = vidW;
    canvas.height = vidH;
    const ctx = canvas.getContext('2d');

    ctx.drawImage(video, 0, 0, vidW, vidH);

    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, titlePxX, titlePxY, titlePxW, titlePxH);
      setTitleTestJpeg(canvas.toDataURL('image/jpeg', 0.92));
      setTitleTestLoading(false);
    };
    img.onerror = () => { setTitleTestLoading(false); alert('Erreur rendu titre'); };
    img.src = png;
  }, [fullTitle, titleMode, textSize, titlePadding, titleBgColor, titleTextColor, bannerSize, titleX, titleY, titleMaxLines, activePreviewTitle]);

  const updateItem = useCallback((id, patch) => setQueue(prev => prev.map(v => v.id === id ? { ...v, ...patch } : v)), []);
  const removeItem = useCallback((id) => {
    setQueue(prev => { const next = prev.filter(v => v.id !== id); if (activeId === id) setActiveId(next[0]?.id || null); return next; });
    setSelectedQueueIds(prev => { const next = new Set(prev); next.delete(id); return next; });
  }, [activeId]);

  const addVideosToQueue = useCallback(async (files) => {
    const newItems = Array.from(files).map(file => ({
      id: nextId++, file, localUrl: URL.createObjectURL(file),
      filename: '', status: 'pending', duration: 0, startTime: 0, endTime: 20,
      videoWidth: 1080, videoHeight: 1920, uploadPct: 0, studio: 'uploading',
    }));
    setQueue(prev => [...prev, ...newItems]);
    setActiveId(prev => prev || newItems[0]?.id || null);
    for (const item of newItems) {
      updateItem(item.id, { status: 'uploading', uploadPct: 0 });
      const form = new FormData(); form.append('video', item.file);
      try {
        const res = await axios.post(`${API}/api/upload-video`, form, {
          headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000,
          // Throttled and kept out of the queue state: see uploadProgress.js.
          onUploadProgress: makeProgressReporter(item.id),
        });
        updateItem(item.id, { filename: res.data.filename, status: 'done', uploadPct: 100, studio: 'ready-edit' }); clearUploadProgress(item.id); log(`✅ ${item.file.name}`);
      } catch (err) {
        const detail = err?.response?.data?.error || err?.message || 'Unknown error';
        const isTimeout = err.code === 'ECONNABORTED' || detail.toLowerCase().includes('timeout');
        if (isTimeout) {
          // Retry once on timeout (backend cold start)
          updateItem(item.id, { status: 'uploading' });
          log(`⏳ Upload timeout — retrying ${item.file.name}…`);
          try {
            const form2 = new FormData(); form2.append('video', item.file);
            const res2 = await axios.post(`${API}/api/upload-video`, form2, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 });
            updateItem(item.id, { filename: res2.data.filename, status: 'done', studio: 'ready-edit' }); log(`✅ ${item.file.name} (retry ok)`);
          } catch (err2) {
            updateItem(item.id, { status: 'error' });
            log(`❌ Upload failed: ${item.file.name} — ${err2?.response?.data?.error || err2?.message || 'Unknown'}`);
          }
        } else {
          updateItem(item.id, { status: 'error' });
          log(`❌ Upload failed: ${item.file.name} — ${detail}`);
        }
      }
    }
  }, [updateItem, log]);

  const [dragging, setDragging] = useState(false);
  const dragCounter = useRef(0);
  const onDragEnter = useCallback(e => { e.preventDefault(); dragCounter.current++; setDragging(true); }, []);
  const onDragLeave = useCallback(e => { e.preventDefault(); dragCounter.current--; if (!dragCounter.current) setDragging(false); }, []);
  const onDragOver  = useCallback(e => e.preventDefault(), []);
  const onDrop      = useCallback(e => {
    e.preventDefault(); dragCounter.current = 0; setDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('video/'));
    if (files.length) addVideosToQueue(files);
  }, [addVideosToQueue]);

  const handleMusicUpload = useCallback(async (e) => {
    const file = e.target.files[0]; if (!file) return; setMusicUploading(true);
    setMusicUploadProgress({ active: true, filename: file.name, pct: 0, error: null });
    const form = new FormData(); form.append('music', file); form.append('mood', 'mid');
    try {
      const res = await axios.post(`${API}/api/upload-music`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000,
        onUploadProgress: (ev) => {
          if (ev.total) {
            const pct = Math.round((ev.loaded / ev.total) * 100);
            setMusicUploadProgress(p => ({ ...p, pct }));
          }
        },
      });
      setMusicUploadProgress(p => ({ ...p, pct: 100 }));
      const result = await addLocalTrack(file, { filename: res.data.filename, label: res.data.label, mood: res.data.mood, duration: res.data.duration });
      if (result.ok) { setLocalTracks(getLocalTracks()); log(`🎵 ${res.data.label} — saved locally`); }
      else { log(`🎵 ${res.data.label} — uploaded (local cache: ${result.error})`); }
      await fetchMusicBank(); handleSelectMusic(res.data.filename);
      setTimeout(() => setMusicUploadProgress({ active: false, filename: '', pct: 0, error: null }), 500);
    } catch (err) {
      log('❌ Music upload failed');
      setMusicUploadProgress({ active: true, filename: file.name, pct: 0, error: err?.response?.data?.error || err?.message || 'Upload failed' });
    }
    finally { setMusicUploading(false); e.target.value = ''; }
  }, [fetchMusicBank, log, handleSelectMusic]);

  const handleMusicBatchUpload = useCallback(async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    if (files.length === 1) { return handleMusicUpload(e); }
    setMusicBatchProgress({ done: 0, total: files.length });
    let lastFilename = null;
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const form = new FormData(); form.append('music', file); form.append('mood', 'mid');
      try {
        const res = await axios.post(`${API}/api/upload-music`, form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
        await addLocalTrack(file, { filename: res.data.filename, label: res.data.label, mood: res.data.mood, duration: res.data.duration });
        lastFilename = res.data.filename; log(`🎵 ${res.data.label}`);
      } catch { log(`❌ Failed: ${file.name}`); }
      setMusicBatchProgress({ done: i + 1, total: files.length });
    }
    setLocalTracks(getLocalTracks()); await fetchMusicBank();
    if (lastFilename) handleSelectMusic(lastFilename);
    setMusicBatchProgress(null); e.target.value = '';
  }, [handleMusicUpload, fetchMusicBank, log, handleSelectMusic]);

  // eslint-disable-next-line
  const handleEndscreenUpload = useCallback(async (e) => {
    const file = e.target.files[0]; if (!file) return; setEndscreenUploading(true);
    const form = new FormData(); form.append('endscreen', file);
    try { await axios.post(`${API}/api/upload-endscreen`, form, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }); await fetchEndscreen(); setEndscreenEnabled(true); log('🎥 Endscreen uploaded'); }
    catch { log('❌ Endscreen upload failed'); }
    finally { setEndscreenUploading(false); }
  }, [fetchEndscreen, log]);

  // eslint-disable-next-line
  const handleEndscreenDelete = useCallback(async () => {
    try { await axios.delete(`${API}/api/endscreen`); setEndscreenInfo(null); setEndscreenEnabled(false); log('🗑 Endscreen removed'); } catch { log('❌ Delete failed'); }
  }, [log]);

  const deleteMusic = useCallback(async (filename) => {
    try { await axios.delete(`${API}/api/music/${encodeURIComponent(filename)}`); await fetchMusicBank(); if (selectedMusic === filename) handleSelectMusic('none'); } catch { log('❌ Delete failed'); }
  }, [fetchMusicBank, selectedMusic, log, handleSelectMusic]);

  // eslint-disable-next-line
  const deleteLocalTrack = useCallback((filename) => {
    removeLocalTrack(filename); setLocalTracks(getLocalTracks());
    if (selectedMusic === filename) handleSelectMusic('none');
  }, [selectedMusic, handleSelectMusic]);

  const readyVideos = queue.filter(v => v.status === 'done');
  const videosForBatch = queueSelectMode && selectedQueueIds.size > 0
    ? readyVideos.filter(v => selectedQueueIds.has(v.id))
    : readyVideos;

  const buildCombos = useCallback(() => {
    if (!scriptEnabled) {
      const titles = titlesForExport.length > 0 ? titlesForExport : [null];
      const combos = [];
      for (const vid of videosForBatch) {
        for (const t of titles) {
          combos.push({
            scriptKey: null, scriptLabel: t ? 'Title only' : 'No title', scriptIcon: '📝',
            vidName: vid.file.name.replace(/\.[^.]+$/, ''), vidFilename: vid.filename,
            vidStartTime: vid.startTime, vidEndTime: vid.endTime,
            voiceId: null, voiceLabel: null,
            useMusic: musicEnabled && selectedMusic !== 'none',
            keepOrig: true, hasTitle: !!t, script: null,
            titleOverride: t,
          });
        }
      }
      return combos;
    }
    const scriptsToRun = selectedStyles
      .map(key => ({ key, label: COPY_STYLES.find(s => s.key === key)?.label, icon: COPY_STYLES.find(s => s.key === key)?.icon, script: getScript(key) }))
      .filter(s => s.script.trim());
    const combos = [];
    for (const vid of videosForBatch) {
      for (const styleEntry of scriptsToRun) {
        const mix       = scriptMix[styleEntry.key] || DEFAULT_MIX;
        const useVoice  = soundEnabled && mix.useVoice;
        const useMusic  = musicEnabled && selectedMusic !== 'none' && mix.useMusic;
        const voiceLoop = useVoice ? selectedVoiceIds : [null];
        for (const voiceId of voiceLoop) {
          const voiceLabel = voiceId ? (voices.find(v => v.id === voiceId)?.label || voiceId) : null;
          const titleLoop = titlesForExport.length > 1 ? titlesForExport : [fullTitle];
          for (const titleText of titleLoop) {
            const checkedHooks = hooks.filter(h => hookChecked[h.id]);
            const hookLoop2 = checkedHooks.length > 0 ? [null, ...checkedHooks] : [null];
            for (const hk of hookLoop2) {
              combos.push({ scriptKey: styleEntry.key, scriptLabel: styleEntry.label, scriptIcon: styleEntry.icon,
                vidName: vid.file.name.replace(/\.[^.]+$/, ''), vidFilename: vid.filename,
                vidStartTime: vid.startTime, vidEndTime: vid.endTime,
                voiceId, voiceLabel, useMusic, keepOrig: false, hasTitle: !!titleText, script: styleEntry.script,
                titleOverride: titleText,
                hookName: hk?.name || null,
                hookType: hk?.hookType || null,
              });
            }
          }
        }
      }
    }
    // Add concept treatments
    const fc = featureConcepts[feature] || {};
    for (const cKey of (conceptsEnabled ? selectedConcepts : [])) {
      const pool = cKey === 'rap' ? (fc.rap || []) : cKey === 'asmr' ? (fc.asmr || []) : (fc.whatsapp || []);
      const pick = pool.filter(a => a.validated)[0] || pool[0];
      if (pick?.data) {
        for (const vid of videosForBatch) {
          combos.push({
            scriptKey: null, scriptLabel: `${cKey.toUpperCase()} concept`, scriptIcon: cKey === 'asmr' ? '\uD83C\uDFA4' : cKey === 'rap' ? '\uD83C\uDFB5' : '\uD83D\uDC75',
            vidName: vid.file.name.replace(/\.[^.]+$/, ''), vidFilename: vid.filename,
            vidStartTime: vid.startTime, vidEndTime: vid.endTime,
            voiceId: null, voiceLabel: pick.label || cKey, useMusic: false, keepOrig: false,
            hasTitle: false, script: null, isConcept: true, conceptKey: cKey, conceptAsset: pick,
          });
        }
      }
    }
    return combos;
  }, [videosForBatch, selectedStyles, getScript, scriptMix, soundEnabled, selectedVoiceIds, selectedMusic, musicEnabled, voices, fullTitle, scriptEnabled, titlesForExport, hooks, hookChecked, selectedConcepts, featureConcepts, feature]);

  const recapLines = useCallback(() => {
    if (!scriptEnabled) {
      const tCount = titlesForExport.length || 1;
      const total = videosForBatch.length * tCount;
      if (total === 0) return [];
      const parts = [];
      if (tCount > 1) parts.push('📝 ' + tCount + ' titles');
      else if (fullTitle) parts.push('📝 ' + fullTitle);
      if (musicEnabled && selectedMusic !== 'none') parts.push('🎵');
      return [{ icon: '📝', label: 'Title only', total, parts }];
    }
    const scriptsToRun = selectedStyles
      .map(key => ({ key, label: COPY_STYLES.find(s => s.key === key)?.label, icon: COPY_STYLES.find(s => s.key === key)?.icon, script: getScript(key) }))
      .filter(s => s.script.trim());
    return scriptsToRun.map(s => {
      const mix        = scriptMix[s.key] || DEFAULT_MIX;
      const useVoice   = soundEnabled && mix.useVoice;
      const voiceCount = useVoice ? selectedVoiceIds.length : 0;
      const titleMult  = titlesForExport.length > 1 ? titlesForExport.length : 1;
      const total      = videosForBatch.length * (useVoice ? voiceCount : 1) * titleMult;
      const parts = [];
      if (useVoice && voiceCount > 0) {
        const names = selectedVoiceIds.map(id => voices.find(v => v.id === id)?.label || id);
        parts.push('🎤 ' + names.join(' · '));
      } else { parts.push('🔇 muted'); }
      if (musicEnabled && selectedMusic !== 'none' && mix.useMusic) parts.push('🎵');
      if (titlesForExport.length > 1) parts.push('📝 ×' + titlesForExport.length);
      else if (fullTitle) parts.push('📝');
      return { icon: s.icon, label: s.label, total, parts };
    });
  }, [selectedStyles, getScript, scriptMix, soundEnabled, selectedVoiceIds, videosForBatch, voices, selectedMusic, musicEnabled, fullTitle, scriptEnabled, titlesForExport]);

  const generate = useCallback(async ({ videosOverride = null, append = false, silentUI = false, onJobProgress = null, onlyKeys = null } = {}) => {
    const vids = videosOverride || videosForBatch;
    if (!vids.length) return emptyRun(0);
    const scriptsToRun = scriptEnabled
      ? selectedStyles.map(key => ({ key, label: COPY_STYLES.find(s => s.key === key)?.label, script: getScript(key) })).filter(s => s.script.trim())
      : [{ key: null, label: 'Title only', script: null }];
    if (scriptEnabled && !scriptsToRun.length) { alert('No scripts available'); return emptyRun(1); }
    const anyNeedsVoice = scriptEnabled && scriptsToRun.some(s => s.key && soundEnabled && (scriptMix[s.key]?.useVoice ?? true));
    if (anyNeedsVoice && !apiKey) { openSettings('keys'); alert('Add your ElevenLabs API key in Settings'); return emptyRun(1); }
    if (anyNeedsVoice && !selectedVoiceIds.length) { alert('Select at least one voice'); return emptyRun(1); }
    const sendLogo = logoEnabled && !!logoBase64;
    const { ox, oy } = logoFFmpegPos(logoPosition);
    const effectiveLogoScale = logoPosition === 'sub' ? 60 : logoScale;
    if (!silentUI) stopPreview();

    if (musicEnabled && selectedMusic !== 'none') await ensureMusicOnServer(selectedMusic);
    if (!silentUI) setGenerating(true); if (!append) setResults([]); if (!silentUI) setShowComboModal(false);

    // ── Rasterise overlay items once (shared across all jobs) ──
    const overlayImages = [];
    if (overlayEnabled && overlayItems.length > 0) {
      for (const item of overlayItems) {
        try {
          const { png } = renderOverlayItemToPng(item);
          overlayImages.push({
            png,
            xPct:    item.x,
            yPct:    item.y,
            sizePct: item.size,
          });
        } catch (err) {
          console.warn('[overlay rasterise]', err);
        }
      }
    }

    // Every variant has a stable key, so "Retry failed only" can re-run exactly the ones that failed and nothing else.
    const wants = (key) => !onlyKeys || onlyKeys.has(key);
    const variantKey = (vid, styleEntry, voiceId, hookEntry, comboTitle) => [vid.id, styleEntry.key || 'title', voiceId || '-', hookEntry ? hookEntry.id : '-', comboTitle || '-'].join('|');
    const okKeys = [], failedKeys = [];
    const initJobs = [];
    for (const vid of vids) {
      const sToRun = scriptEnabled
        ? scriptsToRun
        : [{ key: null, label: 'Title only', script: null }];
      for (const styleEntry of sToRun) {
        const mix      = styleEntry.key ? (scriptMix[styleEntry.key] || DEFAULT_MIX) : DEFAULT_MIX;
        const useVoice = scriptEnabled && soundEnabled && mix.useVoice;
        const voiceLoop = useVoice ? selectedVoiceIds : [null];
        const titleLoop = titlesForExport.length > 1 ? titlesForExport : [fullTitle];
        // Hooks: flat checked, universal — gated on hooksEnabled like the render
        // loop (~1839) & countVersions so initJobs.length (the onJobProgress
        // denominator) can't over-count and stall the progress bar.
        const styleHooks = hooksEnabled ? hooks.filter(h => h.serverFilename && hookChecked[h.id]) : [];
        const hookLoop = styleHooks.length > 0 ? [null, ...styleHooks] : [null]; // null = no hook
        for (const hookEntry of hookLoop) {
          for (const comboTitle of titleLoop) {
            for (const voiceId of voiceLoop) {
              const voiceLabel = voiceId ? (voices.find(v => v.id === voiceId)?.label || voiceId) : null;
              const titleSnip = comboTitle && titlesForExport.length > 1 ? ` · ${comboTitle.slice(0, 20)}` : '';
              const hookSnip = hookEntry ? ` · 🎬 ${hookEntry.name}` : '';
              const label = `${vid.file.name.replace(/\.[^.]+$/, '')} · ${styleEntry.label}${voiceId ? ` · ${voiceLabel}` : ''}${hookSnip}${titleSnip}`;
              initJobs.push({ id: nextJobId++, label, status: 'running', progress: 0, key: variantKey(vid, styleEntry, voiceId, hookEntry, comboTitle) });
            }
          }
        }
      }
    }
    // Add concept jobs
    const fcGen = featureConcepts[feature] || {};
    for (const cKey of (conceptsEnabled ? selectedConcepts : [])) {
      const pool = cKey === 'rap' ? (fcGen.rap || []) : cKey === 'asmr' ? (fcGen.asmr || []) : (fcGen.whatsapp || []);
      const pick = pool.filter(a => a.validated)[0] || pool[0];
      if (pick?.data) {
        for (const vid of vids) {
          const label = `${vid.file.name.replace(/\.[^.]+$/, '')} · ${cKey.toUpperCase()} · ${pick.label || cKey}`;
          initJobs.push({ id: nextJobId++, label, status: 'running', progress: 0, isConcept: true, conceptKey: cKey, conceptAsset: pick, key: `${vid.id}|concept|${cKey}` });
        }
      }
    }
    // What this run is expected to produce: the variants, plus a titled original per video when a title is on.
    const titledOrigKey = (vid) => `${vid.id}|orig-title`;
    const titledOrigPlanned = (vid) => !!(titleEnabled && (titlesForExport[0] || fullTitle) && vid.filename);
    const plannedKeys = [...initJobs.map(j => j.key), ...vids.filter(titledOrigPlanned).map(titledOrigKey)].filter(wants);
    const plannedVariants = initJobs.filter(j => wants(j.key)).length;
    // An ingredient that failed to render (title card, overlay) must fail the variant, never ship it quietly without it.
    const overlayFailed = overlayEnabled && overlayItems.length > 0 && overlayImages.length < overlayItems.length;
    if (!silentUI) { setExportJobs(initJobs); setShowExportQueue(true); }

    const allResults = [];
    let jobCursor = 0;

    // silentUI: track completed jobs only via onJobProgress; never write
    // to exportJobs (so the classic ProgressRail stays dark).
    let silentDoneCount = 0;   // combos that produced a result
    let silentErrCount  = 0;   // combos that failed (silent try/catch)
    const updateJob = (idx, patch) => {
      if (silentUI) {
        if (patch.status === 'done')  silentDoneCount++;
        if (patch.status === 'error') silentErrCount++;
        // Progress advances on both so the bar completes, but successes and
        // failures are reported separately — errors are no longer counted as done.
        if ((patch.status === 'done' || patch.status === 'error') && onJobProgress)
          onJobProgress(silentDoneCount, plannedVariants, silentErrCount);
        return;
      }
      setExportJobs(prev => prev.map((j, i) => i === idx ? { ...j, ...patch } : j));
    };

    for (const vid of vids) {
      const vidW = vid.videoWidth  || 1080;
      const vidH = vid.videoHeight || 1920;

      // ORIGINAL with titling: same video, same audio (-c:a copy), only the title added. No titling => pass-through.
      const origTitle = titlesForExport[0] || fullTitle; let origPushed = false;
      if (titleEnabled && origTitle && vid.filename && wants(titledOrigKey(vid))) {
        try {
          let png, cardW, cardH;
          if (titleMode === 'emoji') { const r = renderEmojiCard(origTitle, emojiSize); png = r.png; cardW = r.width; cardH = r.height; }
          else { const r = renderTitleCard(origTitle, TEXT_SIZES[textSize] || 68, titlePadding, titleBgColor, titleTextColor, BANNER_SIZES[bannerSize] || 82, titleMaxLines); png = r.png; cardW = r.width; cardH = r.height; }
          const px = computeTitlePx({ cardW, cardH, vidW: 1080, vidH: 1920, titleX, titleY });
          const origRes = await axios.post(`${API}/api/generate-remix`, {
            videoFilename: vid.filename, voiceoverFilename: null, hookFilename: null,
            startTime: vid.startTime, endTime: vid.endTime,
            audioMode: 'original', replaceAudio: false, musicFilename: null,
            titleImageBase64: png, titlePxX: px.titlePxX, titlePxY: px.titlePxY, titlePxW: px.titlePxW,
            titleTimeIn: Math.max(0, (titleTimeIn || 0) - vid.startTime),
            titleTimeOut: titleTimeOut != null ? Math.max(0, titleTimeOut - vid.startTime) : -1,
          });
          allResults.push({
            label: vid.file.name + ' · Original + title',
            url: `${API}${origRes.data.url}`, filename: origRes.data.filename,
            isOriginal: true, titled: true,
            ext: vid.file.name.split('.').pop() || 'mp4',
            hook: null, script: null, concept: null,
            sourceQueueId: vid.id,
          });
          if (!origRes.data || !origRes.data.url) throw new Error('the server returned no file');
          log(`✅ Original + title · ${vid.file.name}`);
          origPushed = true; okKeys.push(titledOrigKey(vid));
        } catch (err) {
          // The untitled original still ships (it is the source file), but the title that was asked for is missing: that is a failure, not a success.
          failedKeys.push(titledOrigKey(vid));
          log(`⚠ Original + title failed (${err.response?.data?.details || err.response?.data?.error || err.message}) — exporting the untitled original instead`);
        }
      }
      if (!origPushed && !onlyKeys) allResults.push({
        label: vid.file.name + ' · Original',
        localUrl: vid.localUrl,
        // Stored upload (R2 key vid_<file>) — export falls back to it when the blob: URL is dead
        storedUrl: vid.filename ? `${API}/api/stream/vid_${vid.filename}` : null,
        isOriginal: true,
        ext: vid.file.name.split('.').pop() || 'mp4',
        hook: null, script: null, concept: null,
        sourceQueueId: vid.id,
      });

      for (const styleEntry of scriptsToRun) {
        const mix      = styleEntry.key ? (scriptMix[styleEntry.key] || DEFAULT_MIX) : DEFAULT_MIX;
        const useVoice = scriptEnabled && soundEnabled && mix.useVoice;
        const useMusic = musicEnabled && selectedMusic !== 'none' && (scriptEnabled ? mix.useMusic : true);
        const voiceLoop = useVoice ? selectedVoiceIds : [null];
        const titleLoop = titlesForExport.length > 1 ? titlesForExport : [fullTitle];
        // Hooks: flat checked, universal — skip entirely if toggle is off
        const styleHooks = hooksEnabled ? hooks.filter(h => h.serverFilename && hookChecked[h.id]) : [];
        const hookLoop = styleHooks.length > 0 ? [null, ...styleHooks] : [null];
        for (const hookEntry of hookLoop) {
        for (const comboTitle of titleLoop) {
          let titleImageBase64 = null;
          let titlePxX = undefined, titlePxY = undefined, titlePxW = undefined;
          if (comboTitle) {
            try {
              let cardW, cardH, png;
              if (titleMode === 'emoji') {
                const result = renderEmojiCard(comboTitle, emojiSize);
                png = result.png; cardW = result.width; cardH = result.height;
              } else {
                const result = renderTitleCard(
                  comboTitle, TEXT_SIZES[textSize] || 68, titlePadding,
                  titleBgColor, titleTextColor, BANNER_SIZES[bannerSize] || 82,
                  titleMaxLines
                );
                png = result.png; cardW = result.width; cardH = result.height;
              }
              titleImageBase64 = png;
              const px = computeTitlePx({ cardW, cardH, vidW: 1080, vidH: 1920, titleX, titleY });
              titlePxX = px.titlePxX;
              titlePxY = px.titlePxY;
              titlePxW = px.titlePxW;
            } catch {}
          }
          for (const voiceId of voiceLoop) {
            const voiceLabel = voiceId ? (voices.find(v => v.id === voiceId)?.label || voiceId) : 'no voice';
            const titleSnip = comboTitle && titlesForExport.length > 1 ? ` · ${comboTitle.slice(0, 25)}` : '';
            const currentJobIdx = jobCursor++;
            const comboKey = variantKey(vid, styleEntry, voiceId, hookEntry, comboTitle);
            if (!wants(comboKey)) continue;   // "Retry failed only": the variants that already came out are left alone
            const hookLog = hookEntry ? ` · 🎬 ${hookEntry.name}` : '';
            log(`🎬 ${styleEntry.label}${voiceId ? ` · ${voiceLabel}` : ''}${hookLog}${titleSnip}`);
            try {
              if (titleEnabled && comboTitle && !titleImageBase64) throw new Error('the title card could not be rendered');
              if (overlayFailed) throw new Error('an overlay could not be rendered');
              let voiceoverFilename = null;
              if (useVoice && voiceId && styleEntry.script) {
                const voRes = await axios.post(`${API}/api/generate-voiceover`, { text: styleEntry.script, voiceId, apiKey });
                if (!voRes.data || !voRes.data.filename) throw new Error('the voiceover came back without a file');
                voiceoverFilename = voRes.data.filename;
                lastVoiceoverFilenameRef.current = voiceoverFilename;
              }
              updateJob(currentJobIdx, { progress: 40 });
              const effectiveReplace = useVoice ? replaceAudio : false;
              const remixRes = await axios.post(`${API}/api/generate-remix`, {
                videoFilename: vid.filename, voiceoverFilename,
                hookFilename: hooksEnabled && hookEntry?.serverFilename ? hookEntry.serverFilename : null,
                startTime: vid.startTime, endTime: vid.endTime,
                replaceAudio: effectiveReplace,
                musicFilename: useMusic ? selectedMusic : null,
                musicStartTime, musicVolume: liveMusicVol, voiceVolume: liveVoiceVol,
                titleImageBase64: titleEnabled ? titleImageBase64 : null,
                titlePxX: titleEnabled ? titlePxX : undefined, titlePxY: titleEnabled ? titlePxY : undefined, titlePxW: titleEnabled ? titlePxW : undefined,
                titleTimeIn:  titleEnabled ? Math.max(0, (titleTimeIn  || 0) - vid.startTime)                             : 0,
                titleTimeOut: titleEnabled ? (titleTimeOut != null ? Math.max(0, titleTimeOut - vid.startTime) : -1) : -1,
                endscreenEnabled: endscreenEnabled && !!(endscreenInfo?.exists),
                logoBase64: sendLogo ? logoBase64 : null,
                logoScale: effectiveLogoScale, logoPosition, logoOx: ox, logoOy: oy,
                overlayImages: overlayImages.length > 0 ? overlayImages.map(ov => ({
                  png:    ov.png,
                  pxX: Math.round((ov.xPct    / 100) * vidW),
                  pxY: Math.round((ov.yPct    / 100) * vidH),
                  pxSize: Math.round((ov.sizePct / 1080) * vidW),
                })) : [],
                // Overlay timing
                overlayTimeIn:  overlayEnabled && overlayItems.length > 0 ? Math.max(0, (overlayTimeIn || 0) - vid.startTime) : 0,
                overlayTimeOut: overlayEnabled && overlayItems.length > 0 ? (overlayTimeOut != null ? Math.max(0, overlayTimeOut - vid.startTime) : -1) : -1,
              });
              if (!remixRes.data || !remixRes.data.url || !remixRes.data.filename) throw new Error('the server returned no file');
              const hookSnip2 = hookEntry ? ` · 🎬 ${hookEntry.name}` : '';
              const resultLabel = `${vid.file.name.replace(/\.[^.]+$/, '')} · ${styleEntry.label}${voiceId ? ` · ${voiceLabel}` : ''}${hookSnip2}${titleSnip}`;
              log(`✅ ${styleEntry.label}${voiceId ? ` · ${voiceLabel}` : ''}${titleSnip}`);
              const resultUrl = `${API}${remixRes.data.url}`;
              allResults.push({
                label: resultLabel,
                url: resultUrl, filename: remixRes.data.filename,
                style: styleEntry.key, voice: voiceLabel,
                hook: hookEntry?.name || null,
                script: styleEntry.label || null,
                concept: null,
                hasMusic: !!useMusic,
                sourceQueueId: vid.id,
              });
              updateJob(currentJobIdx, { status: 'done', progress: 100, url: resultUrl, filename: remixRes.data.filename });
              okKeys.push(comboKey);
            } catch (err) {
              log(`❌ ${err.response?.data?.details || err.response?.data?.error || err.message}`);
              updateJob(currentJobIdx, { status: 'error', progress: 0 });
              failedKeys.push(comboKey);
            }
          }
        } // hookLoop
        }
      }
    }
    // ── Concept treatments ──
    for (const cKey of (conceptsEnabled ? selectedConcepts : [])) {
      const pool = cKey === 'rap' ? (fcGen.rap || []) : cKey === 'asmr' ? (fcGen.asmr || []) : (fcGen.whatsapp || []);
      const pick = pool.filter(a => a.validated)[0] || pool[0];
      if (!pick?.data) continue;
      if (!vids.some(v => wants(`${v.id}|concept|${cKey}`))) continue;
      // Upload concept asset to server
      let conceptFilename = null;
      const isRap = cKey === 'rap';
      try {
        const endpoint = isRap ? `${API}/api/upload-music-b64` : `${API}/api/upload-vo-b64`;
        const res = await axios.post(endpoint, { filename: `fm_${cKey}_${feature}_${Date.now()}.mp3`, b64: pick.data }, { timeout: 60000 });
        conceptFilename = res.data.filename;
        if (isRap) await fetchMusicBank();
      } catch(e) {
        // The concept variants were planned: not producing them is a failure to report, not something to skip silently.
        log(`\u26A0 ${cKey} upload failed: ${e.message}`);
        for (const v of vids) if (wants(`${v.id}|concept|${cKey}`)) { updateJob(jobCursor++, { status: 'error', progress: 0 }); failedKeys.push(`${v.id}|concept|${cKey}`); }
        continue;
      }
      if (!conceptFilename) {
        for (const v of vids) if (wants(`${v.id}|concept|${cKey}`)) { updateJob(jobCursor++, { status: 'error', progress: 0 }); failedKeys.push(`${v.id}|concept|${cKey}`); }
        continue;
      }

      for (const vid of vids) {
        const currentJobIdx = jobCursor++;
        const conceptKey = `${vid.id}|concept|${cKey}`;
        if (!wants(conceptKey)) continue;
        log(`\uD83C\uDFA4 ${cKey.toUpperCase()} · ${pick.label || cKey}`);
        try {
          updateJob(currentJobIdx, { progress: 40 });
          // Compute title for concept — same as scriptsToRun path
          let cTitleB64 = null, cTitlePxX, cTitlePxY, cTitlePxW;
          if (titleEnabled && fullTitle) {
            try {
              const cVidW = vid.videoWidth || 1080, cVidH = vid.videoHeight || 1920;
              let cardW, cardH, png;
              if (titleMode === 'emoji') {
                const r = renderEmojiCard(fullTitle, emojiSize);
                png = r.png; cardW = r.width; cardH = r.height;
              } else {
                const r = renderTitleCard(fullTitle, TEXT_SIZES[textSize] || 68, titlePadding, titleBgColor, titleTextColor, BANNER_SIZES[bannerSize] || 82, titleMaxLines);
                png = r.png; cardW = r.width; cardH = r.height;
              }
              cTitleB64 = png;
              const px = computeTitlePx({ cardW, cardH, vidW: 1080, vidH: 1920, titleX, titleY });
              cTitlePxX = px.titlePxX; cTitlePxY = px.titlePxY; cTitlePxW = px.titlePxW;
            } catch {}
          }
          if (titleEnabled && fullTitle && !cTitleB64) throw new Error('the title card could not be rendered');
          const remixRes = await axios.post(`${API}/api/generate-remix`, {
            videoFilename: vid.filename,
            voiceoverFilename: isRap ? null : conceptFilename,
            hookFilename: null,
            startTime: vid.startTime, endTime: vid.endTime,
            replaceAudio: true,
            musicFilename: isRap ? conceptFilename : (musicEnabled && selectedMusic !== 'none' ? selectedMusic : null),
            musicStartTime: isRap ? 0 : musicStartTime,
            musicVolume: isRap ? 1.0 : liveMusicVol,
            voiceVolume: isRap ? 0 : liveVoiceVol,
            titleImageBase64: cTitleB64,
            titlePxX: cTitlePxX, titlePxY: cTitlePxY, titlePxW: cTitlePxW,
            titleTimeIn:  titleEnabled ? Math.max(0, (titleTimeIn  || 0) - vid.startTime)                             : 0,
            titleTimeOut: titleEnabled ? (titleTimeOut != null ? Math.max(0, titleTimeOut - vid.startTime) : -1) : -1,
            endscreenEnabled: endscreenEnabled && !!(endscreenInfo?.exists),
            logoBase64: sendLogo ? logoBase64 : null,
            logoScale: effectiveLogoScale, logoPosition, logoOx: ox, logoOy: oy,
            overlayImages: [],
          });
          if (!remixRes.data || !remixRes.data.url || !remixRes.data.filename) throw new Error('the server returned no file');
          const resultUrl = `${API}${remixRes.data.url}`;
          const resultLabel = `${vid.file.name.replace(/\.[^.]+$/, '')} · ${cKey.toUpperCase()} · ${pick.label || cKey}`;
          allResults.push({ label: resultLabel, url: resultUrl, filename: remixRes.data.filename, style: cKey, voice: pick.label, hook: null, script: null, concept: cKey === 'rap' ? 'Musical VO' : cKey === 'asmr' ? 'ASMR' : cKey === 'whatsapp' ? 'WhatsApp' : cKey.toUpperCase(), sourceQueueId: vid.id });
          updateJob(currentJobIdx, { status: 'done', progress: 100, url: resultUrl, filename: remixRes.data.filename });
          okKeys.push(conceptKey);
          log(`\u2705 ${cKey.toUpperCase()} done`);
        } catch (err) {
          log(`\u274C ${cKey} failed: ${err.response?.data?.error || err.message}`);
          updateJob(currentJobIdx, { status: 'error', progress: 0 });
          failedKeys.push(conceptKey);
        }
      }
    }

    const doneCount = allResults.filter(r => !r.isOriginal).length;
    if (!silentUI) setLastGenTotal(doneCount);
    setResults(prev => append ? [...prev, ...allResults] : allResults); if (!silentUI) setGenerating(false);
    log(`\uD83C\uDFC1 ${allResults.length} video(s) done`);
    // What the run actually produced, by key. The caller decides success from this, never from "it resolved".
    return { ok: okKeys.length, failed: failedKeys.length, total: plannedKeys.length, okKeys, failedKeys };
  // eslint-disable-next-line
  }, [videosForBatch, apiKey, soundEnabled, selectedVoiceIds, selectedStyles, getScript, scriptMix, fullTitle, titleFontSize, titlePadding, titleBgColor, titleTextColor, titleTimeIn, titleTimeOut, replaceAudio, selectedMusic, musicEnabled, musicStartTime, liveMusicVol, liveVoiceVol, titleX, titleY, voices, log, endscreenEnabled, endscreenInfo, logoEnabled, logoBase64, logoScale, logoPosition, stopPreview, ensureMusicOnServer, scriptEnabled, titlesForExport, textSize, bannerSize, titleEnabled, titleMode, emojiSize, titleMaxLines, overlayEnabled, overlayItems, overlayTimeIn, overlayTimeOut]);

  const combos      = buildCombos();
  const totalCombos = combos.length;
  const lines       = recapLines();
  const readyStylesWithScript = selectedStyles.filter(k => getScript(k).trim());
  // Batch Studio — single source of truth for the version count. Mirrors
  // generate()'s render loop EXACTLY (scripts × voices × titles × hooks
  // + validated concepts) for `videoCount` source videos, so the button
  // label, the validate modal and renderQueue.total never diverge again.
  // Hooks gated on hooksEnabled && serverFilename like the render loop (~1836).
  // The numbers on screen (recap, button, queue badges, cards, strip) all come from studioCounts.js; this builds its input from the
  // panel. It mirrors generate()'s render loop (scripts × voices × titles × hooks + concepts).
  const liveBreakdown = (() => {
    const styleHooks = hooksEnabled ? hooks.filter(h => h.serverFilename && hookChecked[h.id]) : [];
    const fc = featureConcepts[feature] || {};
    const labelOf = (c) => (c === 'rap' ? 'Musical VO' : c === 'asmr' ? 'ASMR' : c === 'whatsapp' ? 'WhatsApp' : String(c).toUpperCase());
    const concepts = (conceptsEnabled ? selectedConcepts : []).filter((cKey) => {
      const pool = cKey === 'rap' ? (fc.rap || []) : cKey === 'asmr' ? (fc.asmr || []) : (fc.whatsapp || []);
      const pick = pool.filter(a => a.validated)[0] || pool[0];
      return !!(pick && pick.data);
    }).map(labelOf);
    return videoBreakdown({
      scripts: scriptEnabled
        ? selectedStyles.map(k => ({ key: k, script: getScript(k) })).filter(s => s.script.trim())
            .map(s => ({ key: s.key, voices: (scriptEnabled && soundEnabled && (scriptMix[s.key] || DEFAULT_MIX).useVoice) ? Math.max(1, selectedVoiceIds.length) : 1 }))
        : null,
      titleMult: titlesForExport.length > 1 ? titlesForExport.length : 1,
      hookMult: styleHooks.length > 0 ? styleHooks.length + 1 : 1,
      concepts,
    });
  })();
  const countVersions = useCallback((videoCount = 1) => Math.max(1, liveBreakdown.variants * videoCount), [liveBreakdown.variants]);
  // ── Studio statuses: one state machine (studioStatus.js), one status per video ───────────────
  // uploading → ready-edit → ready-export → exporting → exported | failed. The queue is the workspace
  // (uploading, ready-edit, ready-export); the bottom bar holds only videos whose export started (exporting, exported, failed).
  // Every change goes through moveItem(), so an event that is not allowed from the current status is refused, not applied.
  const moveItem = useCallback((id, event, patch = {}) => {
    setQueue(prev => prev.map(v => {
      if (!v || v.id !== id) return v;
      const next = transition(v.studio, event);
      if (!next) { console.warn(`[studio] "${event}" refused on a ${v.studio} video`); return v; }
      return { ...v, ...patch, studio: next };
    }));
  }, []);

  // Edit settings recorded when a video is marked "Done editing" and restored when it is picked again or re-edited.
  // (The render itself uses the generate() closure captured at the same moment, so it can't drift either.)
  const editEntries = [
    ['selectedStyles', selectedStyles, setSelectedStyles], ['allScripts', allScripts, setAllScripts],
    ['scriptEnabled', scriptEnabled, setScriptEnabled], ['soundEnabled', soundEnabled, setSoundEnabled],
    ['selectedVoiceIds', selectedVoiceIds, setSelectedVoiceIds],
    ['selectedMusic', selectedMusic, setSelectedMusic], ['musicEnabled', musicEnabled, setMusicEnabled],
    ['musicStartTime', musicStartTime, setMusicStartTime], ['liveMusicVol', liveMusicVol, setLiveMusicVol], ['liveVoiceVol', liveVoiceVol, setLiveVoiceVol],
    ['replaceAudio', replaceAudio, setReplaceAudio], ['endscreenEnabled', endscreenEnabled, setEndscreenEnabled], ['logoEnabled', logoEnabled, setLogoEnabled],
    ['titleEnabled', titleEnabled, setTitleEnabled], ['titleText', titleText, setTitleText], ['titleEmoji', titleEmoji, setTitleEmoji],
    ['titleMode', titleMode, setTitleMode], ['selectedTitles', selectedTitles, setSelectedTitles],
    ['titleX', titleX, setTitleX], ['titleY', titleY, setTitleY], ['textSize', textSize, setTextSize], ['emojiSize', emojiSize, setEmojiSize],
    ['bannerSize', bannerSize, setBannerSize], ['titleMaxLines', titleMaxLines, setTitleMaxLines],
    ['titleTimeIn', titleTimeIn, setTitleTimeIn], ['titleTimeOut', titleTimeOut, setTitleTimeOut],
    ['hooksEnabled', hooksEnabled, setHooksEnabled], ['hookChecked', hookChecked, setHookChecked],
    ['conceptsEnabled', conceptsEnabled, setConceptsEnabled], ['selectedConcepts', selectedConcepts, setSelectedConcepts],
    ['overlayEnabled', overlayEnabled, setOverlayEnabled], ['overlayItems', overlayItems, setOverlayItems],
    ['overlayTimeIn', overlayTimeIn, setOverlayTimeIn], ['overlayTimeOut', overlayTimeOut, setOverlayTimeOut],
  ];
  // "Done editing" is only possible once something has actually been set.
  const somethingSet = readyStylesWithScript.length > 0 || titlesForExport.length > 0
    || (musicEnabled && !!selectedMusic && selectedMusic !== 'none')
    || (conceptsEnabled && selectedConcepts.length > 0)
    || (hooksEnabled && hooks.some(h => h.serverFilename && hookChecked[h.id]));

  // Every count on screen: one pass, one function (studioCounts.js). Recap, button, queue badges, card badges, footer and strip read this.
  const jobsById = {}; renderQueue.forEach(j => { if (j) jobsById[j.queueItemId] = j; });
  const studioSum = studioSummary({ items: queue, liveTotal: liveBreakdown.total, jobs: jobsById, runIds: exportRun ? exportRun.ids : [] });

  const pickItem = (item) => {
    if (!item || item.studio === 'uploading') return;
    setActiveId(item.id);
    if (item.snapshot) applySnapshot(editEntries, item.snapshot);
  };
  // Done editing: ready-edit → ready-export. The video stays in the queue; the editor moves to the next clip to edit.
  const doneEditing = () => {
    if (!activeItem || activeItem.studio !== 'ready-edit' || !somethingSet) return;
    const id = activeItem.id;
    moveItem(id, 'doneEditing', { snapshot: takeSnapshot(editEntries), runner: generate, versions: liveBreakdown.variants, counts: liveBreakdown });
    const next = queue.find(v => v && v.id !== id && v.studio === 'ready-edit' && v.status === 'done');
    if (next) pickItem(next);
  };
  const backToEditing = (id) => { moveItem(id, 'backToEditing'); setActiveId(id); };

  // Export all: one action for the whole queue. Jobs run one after the other; a video's card lands in the bar when ITS export starts.
  const exportAll = () => {
    const ready = queue.filter(v => v && v.studio === 'ready-export');
    if (!ready.length || !allQueueReady(queue)) return;
    setExportRun({ ids: ready.map(v => v.id), startedAt: Date.now() });
    setRenderQueue(prev => [...prev.filter(j => j && !ready.some(v => v.id === j.queueItemId)),
      ...ready.map(v => ({ queueItemId: v.id, fileName: v.file.name, status: 'queued', done: 0, err: 0, total: v.versions || 1, run: null }))]);
  };
  // Retry on a failed card: drop its partial results and run that video again; the rest of the run is unaffected.
  const retryExport = (id) => {
    const item = queue.find(v => v && v.id === id);
    if (!item || item.studio !== 'failed') return;
    setResults(prev => prev.filter(r => !r || r.sourceQueueId !== id));
    moveItem(id, 'retry');
    setExportRun(prev => ({ ids: [...new Set([...((prev && prev.ids) || []), id])], startedAt: (prev && prev.startedAt) || Date.now() }));
    setRenderQueue(prev => [...prev.filter(j => j && j.queueItemId !== id), { queueItemId: id, fileName: item.file.name, status: 'queued', done: 0, err: 0, total: item.versions || 1 }]);
  };

  // Retry failed only (amber card): re-run just the variants that failed, keep the ones that came out, merge the new ones in.
  const retryFailedOnly = (id) => {
    const item = queue.find(v => v && v.id === id);
    const job = renderQueue.find(j => j && j.queueItemId === id);
    if (!item || item.studio !== 'partial' || !job || !job.run || !job.run.failedKeys.length) return;
    moveItem(id, 'retryFailed');
    setExportRun(prev => ({ ids: [...new Set([...((prev && prev.ids) || []), id])], startedAt: (prev && prev.startedAt) || Date.now() }));
    setRenderQueue(prev => prev.map(j => j && j.queueItemId === id ? { ...j, status: 'queued', done: 0, err: 0, retry: true, onlyKeys: job.run.failedKeys } : j));
  };

  // Re-edit an exported (or failed) video: back to the queue as "Ready to edit" with every previous setting; it leaves the bar.
  const confirmReedit = useCallback(() => {
    if (!reeditTarget) return;
    const id = reeditTarget.id;
    setResults(prev => prev.filter(r => !r || r.sourceQueueId !== id));
    setRenderQueue(prev => prev.filter(j => j && j.queueItemId !== id));
    setExportRun(prev => (prev ? { ...prev, ids: prev.ids.filter(x => x !== id) } : prev));
    moveItem(id, 'reedit');
    if (reeditTarget.snapshot) applySnapshot(editEntries, reeditTarget.snapshot);
    setActiveId(id);
    setReeditTarget(null);
  }, [reeditTarget, moveItem]); // eslint-disable-line react-hooks/exhaustive-deps

  // Drop what the renders produced (Export tab input) and the run state that goes with it. Exported cards go with them
  // (they were sent to Export); a run still in flight is interrupted: its video turns into a failed card with a Retry.
  const clearRenderResults = useCallback(() => {
    const live = new Set(renderQueue.filter(j => j && (j.status === 'queued' || j.status === 'rendering')).map(j => j.queueItemId));
    live.forEach(id => canceledJobIds.current.add(id));
    setResults([]); setRenderQueue([]); setExportRun(null); setLastGenTotal(0); setExportJobs([]);
    setQueue(prev => prev
      .filter(v => !v || v.studio !== 'exported')
      .map(v => (v && v.studio === 'exporting' && live.has(v.id)) ? { ...v, studio: transition('exporting', 'exportFail') } : v));
  }, [renderQueue]);
  // Studio "Clear": empty the queue AND the render results (Export's Clear calls this too, otherwise its tiles come back).
  const clearStudioSession = useCallback(() => {
    renderQueue.forEach(j => { if (j && (j.status === 'queued' || j.status === 'rendering')) canceledJobIds.current.add(j.queueItemId); });
    setResults([]); setRenderQueue([]); setExportRun(null); setLastGenTotal(0); setExportJobs([]);
    setQueue([]); setActiveId(null); setSelectedQueueIds(new Set()); setQueueSelectMode(false);
  }, [renderQueue]);

  // Worker: runs the export jobs serially. A job starts → its video moves ready-export → exporting (the card lands in the bar),
  // and ends in exported or failed. "It resolved" is not "it worked": generate() reports how many files it produced.
  useEffect(() => {
    const queued = renderQueue.find(j => j && j.status === 'queued');
    const rendering = renderQueue.find(j => j && j.status === 'rendering');
    if (!queued || rendering) return;
    const item = queue.find(v => v && v.id === queued.queueItemId);
    if (!item) {
      setRenderQueue(prev => prev.filter(j => !j || j.queueItemId !== queued.queueItemId));
      return;
    }
    const jobId = item.id;
    if (item.studio === 'ready-export') moveItem(jobId, 'startExport');
    setRenderQueue(prev => prev.map(j => j && j.queueItemId === jobId ? { ...j, status: 'rendering' } : j));
    // `run` = what this video has produced so far, by variant key. A "Retry failed only" run merges into it.
    const settle = (event, run) => {
      if (canceledJobIds.current.has(jobId)) { canceledJobIds.current.delete(jobId); return false; }
      setRenderQueue(prev => prev.map(j => j && j.queueItemId === jobId
        ? { ...j, status: event === 'exportFail' ? 'error' : 'ready', run, err: run ? run.failed : j.err, retry: false, onlyKeys: null } : j));
      moveItem(jobId, event);
      return true;
    };
    (item.runner || generate)({
      videosOverride: [item],
      append: true,
      silentUI: true,
      onlyKeys: queued.retry && queued.onlyKeys ? new Set(queued.onlyKeys) : null,
      onJobProgress: (done, total, err = 0) => {
        if (canceledJobIds.current.has(jobId)) return;
        setRenderQueue(prev => prev.map(j => j && j.queueItemId === jobId ? { ...j, done, total, err } : j));
      },
    }).then((res) => {
      if (canceledJobIds.current.has(jobId)) {
        // Canceled mid-render → strip any appended results, drop the cancel flag.
        setResults(prev => prev.filter(r => !r || r.sourceQueueId !== jobId));
        canceledJobIds.current.delete(jobId);
        return;
      }
      const merged = queued.retry && queued.run ? mergeRun(queued.run, res) : res;
      // The titled original came out on a retry: the untitled fallback that stood in for it goes away.
      if (queued.retry && res.okKeys.includes(`${jobId}|orig-title`)) setResults(prev => prev.filter(r => !(r && r.sourceQueueId === jobId && r.isOriginal && !r.titled)));
      settle(outcomeEvent(merged), merged);
    }).catch(() => {
      // Nothing new came out: keep what the video already had (amber stays amber), or fail if it had nothing.
      const keep = queued.retry && queued.run && queued.run.ok > 0 ? queued.run : null;
      settle(keep ? 'exportPartial' : 'exportFail', keep);
    });
  }, [renderQueue, queue, generate, moveItem]);

  const canGenerate = videosForBatch.length > 0 && !generating && (scriptEnabled ? readyStylesWithScript.length > 0 : true);

  const logoPreviewStyle = logoPosition === 'sub'
    ? { width: '70%', bottom: '2%', left: '50%', transform: 'translateX(-50%)' }
    : { width: `${Math.round(logoScale * 0.55)}%` };

  const usedBytes  = localMusicUsedBytes();
  const usedPct    = Math.min(100, Math.round((usedBytes / LOCAL_MUSIC_QUOTA) * 100));
  // eslint-disable-next-line
  const quotaColor = usedPct > 90 ? '#ff4444' : usedPct > 70 ? '#ff9900' : '#22c55e';

  const clipDuration = (videoDuration && videoDuration > 0) ? videoDuration : (activeItem?.duration || 30);
  const titleOutVal  = titleTimeOut != null ? titleTimeOut : clipDuration;

  // Drag global pour la timeline titre (monte apres clipDuration/titleOutVal)
  useEffect(() => {
    if (!draggingTitleHandle) return;
    const onMove = (e) => {
      if (!titleTimelineRef.current || clipDuration <= 0) return;
      const rect = titleTimelineRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const t = Math.round(x * clipDuration * 10) / 10;
      if (draggingTitleHandle === 'in') {
        setTitleTimeIn(Math.min(t, (titleTimeOut ?? clipDuration) - 0.1));
      } else {
        const v = Math.max(t, titleTimeIn + 0.1);
        setTitleTimeOut(v >= clipDuration ? null : v);
      }
    };
    const onUp = () => setDraggingTitleHandle(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [draggingTitleHandle, clipDuration, titleTimeIn, titleTimeOut]); // eslint-disable-line

  const appLogoSizePx = APP_LOGO_SIZES.find(s => s.key === appLogoSize)?.height || 48;


  const canPreviewMix = selectedMusic !== 'none' || !!lastVoiceoverFilenameRef.current;

  const toggleQueueSelect = useCallback((id, e) => {
    if (e) e.stopPropagation();
    setSelectedQueueIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);
  const selectAllQueue = useCallback(() => { setSelectedQueueIds(new Set(readyVideos.map(v => v.id))); }, [readyVideos]);
  const clearQueueSelection = useCallback(() => { setSelectedQueueIds(new Set()); }, []);

  const addTitleTag = useCallback((text) => {
    const t = text.trim();
    if (t && !selectedTitles.includes(t)) setSelectedTitles(prev => [...prev, t]);
  }, [selectedTitles]);

  const addCurrentTitleToPills = useCallback(() => {
    const t = (titleText.trim() + (titleEmoji ? ' ' + titleEmoji : '')).trim();
    if (t && !selectedTitles.includes(t)) setSelectedTitles(prev => [...prev, t]);
  }, [titleText, titleEmoji, selectedTitles]);

  const handlePredefinedSelect = useCallback((val) => {
    if (!val) return;
    const withoutEmoji = val.replace(/\s*[\u{1F300}-\u{1FFFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]\s*$/u, '').trim();
    setTitleText(withoutEmoji);
    const full = (withoutEmoji + (titleEmoji ? ' ' + titleEmoji : '')).trim();
    if (full && !selectedTitles.includes(full)) {
      setSelectedTitles(prev => [...prev, full]);
    }
  }, [titleEmoji, selectedTitles]);

  const titleOverlayStyle = {
    position: 'absolute',
    top: `${titleY}%`,
    left: `${titleX}%`,
    transform: 'translate(-50%, -50%)',
    width: `${BANNER_SIZES[bannerSize] || 82}%`,
    textAlign: 'center',
    pointerEvents: 'none',
  };

  // Overlay timing derived values
  const overlayOutVal = overlayTimeOut != null ? overlayTimeOut : clipDuration;

  // The bottom of the Studio, rendered in two places: part 'foot' = recap + validation actions (footer of the editing card),
  // part 'bar' = the full-width export bar. Same computation, one source (studioSum), two spots in the layout.
  const renderStudioBottom = (part) => {
    // About how long is left, from the pace of this run so far (videos done / time elapsed).
    const etaText = () => {
      const started = exportRun && exportRun.startedAt, done = studioSum.run.doneVideos, total = studioSum.run.totalVideos;
      if (!started || !done) return 'starting…';
      const left = Math.round(((Date.now() - started) / 1000 / done) * Math.max(0, total - done));
      return left > 0 ? `about ${left}s left` : 'almost done';
    };
              const jobTotal = exportJobs.filter(j => !j.isOriginal).length;
              const jobDone  = exportJobs.filter(j => j.status === 'done' && !j.isOriginal).length;
              const jobError = exportJobs.filter(j => j.status === 'error' && !j.isOriginal).length;
              const showRail = (generating || lastGenTotal > 0 || jobError > 0) && renderQueue.length === 0;
              if (!showRail) {
                // Studio statuses. The editor row (Done editing / Back to editing / the "all ready" banner) sits under the editor;
                // the fixed bar below holds only videos whose export started. Never both mixed up.
                const queueItems = queue.filter(v => v && inQueue(v.studio));
                const barItems   = queue.filter(v => v && inBar(v.studio));
                const everyReady = allQueueReady(queue);
                const runIds     = exportRun ? new Set(exportRun.ids) : new Set();
                const runJobs    = renderQueue.filter(j => j && runIds.has(j.queueItemId));
                const running    = runJobs.some(j => j.status === 'queued' || j.status === 'rendering');
                const exportedN  = barItems.filter(v => v.studio === 'exported').length;
                const failedN    = barItems.filter(v => v.studio === 'failed').length;
                const partialN   = barItems.filter(v => v.studio === 'partial').length;
                const cur        = activeItem;
                const curBreakdown = cur && cur.studio === 'ready-export' && cur.counts ? cur.counts : liveBreakdown;   // what that clip produces
                const curCount   = cur && studioSum.perClip[cur.id] ? studioSum.perClip[cur.id].count : liveBreakdown.total;
                const jobOf      = (id) => renderQueue.find(j => j && j.queueItemId === id);
                const cards = barItems.map(v => {
                  const j = jobOf(v.id), run = j && j.run;
                  const partial = v.studio === 'partial';
                  return {
                    id: v.id, src: v.localUrl, state: v.studio,
                    label: partial && run ? partialLabel(run.okKeys.length, run.total) : null,
                    title: v.studio === 'failed' ? `${v.file.name} — nothing was produced`
                      : partial && run ? `${v.file.name} — ${run.okKeys.length} of ${run.total} variants out, ${run.failed} failed` : v.file.name,
                    onClick: (v.studio === 'exported' || v.studio === 'partial' || v.studio === 'failed') ? () => setReeditTarget(v) : null,
                    badge: studioSum.perClip[v.id] ? studioSum.perClip[v.id].count : null,   // videos in that clip
                    retryLabel: partial ? 'Retry failed only' : 'Retry',
                    onRetry: () => (partial ? retryFailedOnly(v.id) : retryExport(v.id)),
                  };
                });
                if (part === 'foot') return (
                  <>
                    {/* how many videos: visible at every step, moves as the settings are ticked */}
                    {!running && cur && (cur.studio === 'ready-edit' || cur.studio === 'ready-export') && <StudioRecap breakdown={curBreakdown} compact />}
                    {!running && cur && cur.studio === 'ready-edit' && !everyReady && (
                      <div className="studio-actions">
                        <button className="rbar-go" disabled={!somethingSet} onClick={doneEditing}>Done editing · {curCount} video{curCount === 1 ? '' : 's'}</button>
                        <span className="studio-hint">{somethingSet ? 'Marks it ready to export — it stays in the queue.' : 'Pick a script, a title, a music or a concept first.'}</span>
                      </div>
                    )}
                    {!running && cur && cur.studio === 'ready-export' && !everyReady && (
                      <div className="studio-actions">
                        <button className="rbar-back" onClick={() => backToEditing(cur.id)}>Back to editing</button>
                        <span className="studio-hint">Ready — {curCount} video{curCount === 1 ? '' : 's'}. {queueItems.filter(v => v.studio !== 'ready-export').length} clip(s) still to set before you can export everything.</span>
                      </div>
                    )}
                    {!running && everyReady && (
                      <div className="studio-allready">
                        <div>
                          <div className="studio-allready-title">Ready to export all your videos</div>
                          <div className="studio-hint">{queueItems.length} clip{queueItems.length === 1 ? '' : 's'} · <b>{studioSum.videosInQueue} videos</b> — export them in one go.</div>
                        </div>
                        <div className="studio-allready-actions">
                          {cur && cur.studio === 'ready-export' && <button className="rbar-back" onClick={() => backToEditing(cur.id)}>Back to editing</button>}
                          <button className="rbar-go" onClick={exportAll}>Export all · {studioSum.videosInQueue} videos</button>
                        </div>
                      </div>
                    )}
                  </>
                );
                return (
                  <RenderBar zone stack={cards.length ? cards : null}
                      running={running ? { label: `Exporting ${studioSum.run.doneVideos} / ${studioSum.run.totalVideos} videos`, sub: etaText(), done: studioSum.run.doneVideos, total: studioSum.run.totalVideos } : null}
                      confirmation={!running && exportedN > 0 && failedN === 0 && partialN === 0 ? `\u2713 ${studioSum.clipsExported} clip${studioSum.clipsExported === 1 ? '' : 's'} · ${studioSum.videosExported} videos` : null}
                      status={!cards.length ? 'Nothing exported yet — the cards land here as each export starts'
                        : (failedN > 0 || partialN > 0) ? <span className="rbar-bad"><b>{exportedN}</b> exported{partialN > 0 && <> · <b>{partialN}</b> partial</>}{failedN > 0 && <> · <b>{failedN}</b> failed</>} — use Retry on the amber / red cards</span> : null}
                      actions={(exportedN + partialN) > 0 && !running ? <button className="rbar-go rbar-go--accent" onClick={() => setActiveTab('export')}>Send to Export →</button> : null}
                    />
                );
              }
              const total   = generating ? jobTotal : (lastGenTotal || jobTotal);
              const done    = generating ? (jobDone + jobError) : (jobError > 0 ? (total - jobError) : lastGenTotal);
              const errMsg  = (!generating && jobError > 0)
                ? (jobError === 1 ? '1 generation failed' : `${jobError} generations failed`)
                : null;
              if (part === 'foot') return null;
              return (
                <ProgressRail
                  done={done}
                  total={total}
                  error={errMsg}
                  type="generate"
                  onAction={() => { setActiveTab('export'); setLastGenTotal(0); }}
                  onRetry={() => { setLastGenTotal(0); setExportJobs([]); if (canGenerate) setShowComboModal(true); }}
                />
              );
  };

  return (
    <div className="app" onDragEnter={onDragEnter} onDragLeave={onDragLeave} onDragOver={onDragOver} onDrop={onDrop}>
      {dragging && <div className="drag-overlay"><div className="drag-overlay-inner">⊕ Drop videos here</div></div>}

      {showAddHookModal && (
        <div className="modal-overlay" onClick={() => { setShowAddHookModal(false); setNewHookFiles([]); }}>
          <div className="modal-box" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
            <div className="modal-box-header">
              <span className="modal-box-title">+ Hook</span>
              <button className="modal-box-close" onClick={() => { setShowAddHookModal(false); setNewHookFiles([]); }}>✕</button>
            </div>
            <div className="modal-box-body">

              {/* Step 1 — File picker */}
              <label className="modal-field-label">Select hook videos</label>
              <label className="upload-music-btn" style={{ display: 'inline-block', cursor: 'pointer', marginBottom: 10, padding: '6px 14px', fontSize: 11 }}>
                Choose files
                <input type="file" accept="video/*" multiple
                  onChange={e => {
                    const files = Array.from(e.target.files).map(f => ({
                      file: f,
                      name: f.name.replace(/\.[^.]+$/, ''),
                      type: null,
                      features: ['Universal'],
                    }));
                    setNewHookFiles(files);
                  }}
                  style={{ display: 'none' }} />
              </label>
              {newHookFiles.length > 0 && (
                <span style={{ fontSize: 10, color: 'var(--text-dim)', marginLeft: 8 }}>{newHookFiles.length} file{newHookFiles.length > 1 ? 's' : ''} selected</span>
              )}

              {newHookFiles.length > 0 && (() => {
                const [cfgIdx, setCfgIdx] = window._hookCfgIdx !== undefined
                  ? [window._hookCfgIdx, i => { window._hookCfgIdx = i; }]
                  : (() => { window._hookCfgIdx = 0; return [0, i => { window._hookCfgIdx = i; }]; })();

                const FEATS = FEATURES.map(f => f.key);
                const h = newHookFiles[cfgIdx];

                const updateFile = (idx, patch) => setNewHookFiles(prev => prev.map((x, i) => i === idx ? { ...x, ...patch } : x));

                return (
                  <>
                    {/* Vignettes row */}
                    <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 6, marginBottom: 10 }}>
                      {newHookFiles.map((f, i) => (
                        <div key={i} onClick={() => { window._hookCfgIdx = i; setNewHookFiles(p => [...p]); }}
                          style={{ flex: '0 0 88px', borderRadius: 8, overflow: 'hidden', cursor: 'pointer',
                            border: `1.5px solid ${i === cfgIdx ? 'var(--accent)' : 'var(--border)'}`,
                            background: 'var(--surface)', position: 'relative' }}>
                          <video src={URL.createObjectURL(f.file)}
                            style={{ width: '100%', aspectRatio: '9/16', objectFit: 'cover', display: 'block' }} muted />
                          {f.type && (
                            <div style={{ position: 'absolute', top: 3, left: 3, fontSize: '8px', padding: '2px 5px',
                              borderRadius: 3, fontWeight: 600, background: f.type === 'visual' ? '#1e88e5' : '#43a047', color: '#fff' }}>
                              {f.type === 'visual' ? 'Visual' : 'Talking'}
                            </div>
                          )}
                          {f.type && (
                            <div style={{ position: 'absolute', top: 3, right: 3, width: 14, height: 14, borderRadius: '50%',
                              background: 'var(--accent)', color: '#fff', fontSize: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</div>
                          )}
                          <div style={{ fontSize: '9px', padding: '3px 5px', color: 'var(--text-dim)',
                            borderTop: '0.5px solid var(--border)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {f.name}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Config for current file */}
                    <div style={{ background: 'var(--bg-secondary, #f5f5f5)', borderRadius: 8, padding: '10px 12px' }}>
                      <div style={{ fontSize: '10px', color: 'var(--text-dim)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.06em' }}>
                        Hook name
                      </div>
                      <input className="modal-field-input"
                        value={h.name}
                        onChange={e => updateFile(cfgIdx, { name: e.target.value })}
                        style={{ marginBottom: 10 }} />

                      <div style={{ fontSize: '10px', color: 'var(--text-dim)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.06em' }}>Type</div>
                      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                        {['visual','talking'].map(tp => (
                          <button key={tp} onClick={() => updateFile(cfgIdx, { type: tp })}
                            style={{ flex: 1, padding: '6px 0', fontSize: '11px', borderRadius: 6, cursor: 'pointer',
                              border: `1.5px solid ${h.type === tp ? (tp === 'visual' ? '#1e88e5' : '#43a047') : 'var(--border)'}`,
                              background: h.type === tp ? (tp === 'visual' ? '#e3f2fd' : '#e8f5e9') : 'none',
                              color: h.type === tp ? (tp === 'visual' ? '#0d47a1' : '#1b5e20') : 'var(--text-muted)',
                              fontWeight: h.type === tp ? 600 : 400 }}>
                            {tp === 'visual' ? 'Visual hook' : 'Talking hook'}
                          </button>
                        ))}
                      </div>

                      <div style={{ fontSize: '10px', color: 'var(--text-dim)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.06em' }}>Feature</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
                        {['Universal', ...FEATS].map(f => {
                          const on = h.features.includes(f);
                          return (
                            <button key={f} onClick={() => {
                              if (f === 'Universal') { updateFile(cfgIdx, { features: ['Universal'] }); }
                              else {
                                const cur = h.features.filter(x => x !== 'Universal');
                                const next = cur.includes(f) ? cur.filter(x => x !== f) : [...cur, f];
                                updateFile(cfgIdx, { features: next.length ? next : ['Universal'] });
                              }
                            }}
                            style={{ fontSize: '10px', padding: '3px 8px', borderRadius: 3, cursor: 'pointer',
                              border: `0.5px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
                              background: on ? '#fff3e0' : 'none',
                              color: on ? '#e65100' : 'var(--text-muted)', fontWeight: on ? 600 : 400 }}>
                              {f === 'Universal' ? 'Universal' : (FEATURES.find(x => x.key === f)?.label || f)}
                            </button>
                          );
                        })}
                      </div>

                      {newHookFiles.length > 1 && (
                        <button onClick={() => {
                          const cur = newHookFiles[cfgIdx];
                          setNewHookFiles(prev => prev.map((x, i) => i === cfgIdx ? x : { ...x, type: cur.type, features: [...cur.features] }));
                        }}
                        style={{ fontSize: '10px', color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                          Apply type + feature to all hooks
                        </button>
                      )}
                    </div>
                  </>
                );
              })()}

            </div>
            <div className="modal-box-footer">
              <button className="modal-box-cancel" onClick={() => { setShowAddHookModal(false); setNewHookFiles([]); }}>Cancel</button>
              <button className="modal-box-confirm"
                disabled={newHookFiles.length === 0 || newHookFiles.some(f => !f.type)}
                onClick={async () => {
                  const filesToUpload = [...newHookFiles];
                  window._hookCfgIdx = 0;
                  setShowAddHookModal(false);
                  setNewHookFiles([]);
                  for (const hf of filesToUpload) {
                    const fd = new FormData();
                    fd.append('hook', hf.file);
                    fd.append('name', hf.name.trim());
                    fd.append('hookType', hf.type);
                    fd.append('tags', JSON.stringify(hf.features));
                    setHookUploadProgress({ name: hf.name.trim(), percent: 0 });
                    try {
                      const data = await new Promise((resolve, reject) => {
                        const xhr = new XMLHttpRequest();
                        xhr.open('POST', `${API}/api/upload-hook`);
                        xhr.upload.onprogress = (e) => {
                          if (e.lengthComputable) setHookUploadProgress({ name: hf.name.trim(), percent: Math.round((e.loaded / e.total) * 100) });
                        };
                        xhr.onload = () => { try { const body = JSON.parse(xhr.responseText); if (xhr.status >= 400 || !body.success) { const e = new Error(body.message || body.error || `Upload failed (HTTP ${xhr.status})`); e.body = body; reject(e); } else { resolve(body); } } catch(e) { reject(e); } };
                        xhr.onerror = () => reject(new Error('Network error'));
                        xhr.send(fd);
                      });
                      console.log('[hook upload] response', data);
                      if (data.filename) {
                        const newHook = { id: data.filename, serverFilename: data.filename, name: hf.name.trim(), hookType: hf.type, tags: hf.features };
                        saveHooks(prev => [...prev, newHook]);
                        setHookChecked(prev => ({ ...prev, [data.filename]: true }));
                      } else {
                        console.error('[hook upload] no filename in response', data);
                      }
                    } catch(e) { console.error('[hook upload] error', e); alert(e.message || 'Hook upload failed: please try again.'); }
                  }
                  setHookUploadProgress(null);
                }}>
                {newHookFiles.length === 0 ? 'Select files first'
                  : newHookFiles.some(f => !f.type) ? `${newHookFiles.filter(f => !f.type).length} hook(s) still to configure`
                  : `Upload ${newHookFiles.length} hook${newHookFiles.length > 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {showFeatureManager && (
        <FeatureManager
          initialFeature={feature}
          onClose={() => setShowFeatureManager(false)}
          onSave={(configs) => { setFeatureConfigs(configs); loadConcepts().then(c => setFeatureConcepts(c)).catch(() => {}); setShowFeatureManager(false); }}
          anthropicKey={anthropicKey}
        />
      )}
      {showComboModal && (
        <ComboDetailModal combos={combos} totalCount={totalCombos}
          onClose={() => setShowComboModal(false)} onConfirm={generate} generating={generating} />
      )}
      {titleTestJpeg && (
        <TitleTestModal jpeg={titleTestJpeg} onClose={() => setTitleTestJpeg(null)} />
      )}


      <header className="header">
        <div className="header-left" onClick={() => setActiveTab('studio')} style={{ cursor: 'pointer' }}>

          <img src={`data:image/png;base64,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`} alt="timbo" className="app-logo-img" />
        </div>
        <div className="header-center">
          {appLogoBase64 && appLogoAlign === 'center' && (
            <img src={appLogoBase64} alt="logo" style={{ height: appLogoSizePx, maxHeight: 44, objectFit: 'contain', display: 'block' }} />
          )}
        </div>
        <div className="header-right">
          {appLogoBase64 && appLogoAlign === 'right' && (
            <img src={appLogoBase64} alt="logo" style={{ height: appLogoSizePx, maxHeight: 44, objectFit: 'contain', display: 'block' }} />
          )}
          {brandSubtitle && <div className="header-subtitle">{brandSubtitle}</div>}
        </div>
      </header>

      <nav className="tab-bar">
        <button className={`tab-btn ${activeTab==='studio'?'active':''}`} onClick={()=>setActiveTab('studio')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="2" y="3" width="15" height="13" rx="2"/><circle cx="9" cy="9.5" r="2.5"/><path d="m2 16 4-4 2 2 4-4 5 5"/></svg>
          {t('nav_studio')}
        </button>
        <button className={`tab-btn ${activeTab==='export'?'active':''} ${results.length>0?'has-results':''}`} onClick={()=>setActiveTab('export')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
          {t('nav_export')}{results.length>0&&<span className="tab-badge">{results.length}</span>}
        </button>
        <button className={`tab-btn ${activeTab==='scan'?'active':''}`} onClick={()=>setActiveTab('scan')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5" opacity=".5"/><circle cx="12" cy="12" r="1" fill="currentColor" stroke="none"/><line x1="12" y1="12" x2="18" y2="6"/></svg>
          Scan
        </button>
        {FEATURE_FLAGS.localize && (
        <button className={`tab-btn ${activeTab==='localize'?'active':''}`} onClick={()=>setActiveTab('localize')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
          {t('nav_localize')}
        </button>
        )}
        {isOwnerUser && (
        <button className={`tab-btn ${activeTab==='performance'?'active':''}`} onClick={()=>setActiveTab('performance')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
          Performance
        </button>
        )}
        <button className={`tab-btn ${activeTab==='settings'?'active':''}`} onClick={()=>{setActiveTab('settings');setApiKeyDraft(localStorage.getItem('el_api_key')||'');setAnthropicDraft(localStorage.getItem('anthropic_key')||'');setBrandDraft({name:brandName,subtitle:brandSubtitle});}}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
          {t('nav_settings')}
          {healthWorst && <span className={`tab-health-dot tab-health-dot--${healthWorst}`} title="A service needs attention" />}
        </button>
      </nav>

      <ServiceBanner
        apiBase={API}
        showServer={isOwnerUser}
        clientIssues={elSyncIssue ? [elSyncIssue] : []}
        onIssues={setHealthWorst}
        onOpenService={(key) => { setActiveTab('settings'); setApiKeyDraft(localStorage.getItem('el_api_key')||''); setAnthropicDraft(localStorage.getItem('anthropic_key')||''); setBrandDraft({name:brandName,subtitle:brandSubtitle}); setSettingsFocus({ key, at: Date.now() }); }}
      />

      <main className={`main${activeTab === 'scan' ? ' main--hov' : ''}${activeTab === 'performance' ? ' main--perf' : ''}${activeTab === 'export' ? ' main--export' : ''}${activeTab === 'settings' ? ' main--settings' : ''}`}>

        {activeTab === 'settings' && (
          <SettingsTab
            apiBase={API}
            appTheme={appTheme} setAppTheme={setAppTheme}
            appLang={appLang} setAppLang={setAppLang}
            apiKeyDraft={apiKeyDraft} setApiKeyDraft={setApiKeyDraft}
            anthropicDraft={anthropicDraft} setAnthropicDraft={setAnthropicDraft}
            savedFeedback={savedFeedback} saveApiKeys={saveApiKeys}
            wordingPhrases={wordingPhrases} saveWordingPhrases={saveWordingPhrases}
            brandName={brandName} setBrandName={setBrandName}
            brandSubtitle={brandSubtitle} setBrandSubtitle={setBrandSubtitle}
            onNavigate={(tab) => setActiveTab(tab)}
            focusService={settingsFocus}
          />
        )}

        {activeTab === 'export' && (
          <TabErrorBoundary name="Export"><ExportTab remixResults={results} feature={feature} onBackToStudio={() => setActiveTab('studio')} onClearAll={clearStudioSession} onExportFinished={clearRenderResults} /></TabErrorBoundary>
        )}

        {activeTab === 'scan' && <HOVTab hovState={hovState} setHovState={setHovState} />}
        {FEATURE_FLAGS.localize && activeTab === 'localize' && <LocalizeTab apiBase={API} elApiKey={apiKey} anthropicKey={anthropicKey} selectedVoiceIds={selectedVoiceIds} voices={voices} />}
        {isOwnerUser && activeTab === 'performance' && <PerformanceTab onToast={(m) => console.log(m)} onNavigateToStudio={(sourceId, featureKey, fromPerf) => { setActiveTab('studio'); }} />}
        {activeTab === 'studio' && (
        <TabErrorBoundary name="Studio">
        <div className="workspace studio-zones">
          {/* ── LEFT COLUMN (22%): the preview on top (9:16, capped at 34vh), the queue underneath taking the rest and scrolling inside itself ── */}
          <div className="zone-left">
            <div className="zone-preview">
              <div className="zone-head"><span className="zone-title">Preview</span><span className="zone-meta">{activeItem ? activeItem.file.name : ''}</span></div>
          <div className="col-upload">
            <div className="preview-wrap" style={activeItem?.status === 'uploading' ? { borderColor: '#F97316', borderStyle: 'solid' } : undefined}>
              {activeItem?.localUrl ? (
                <div
                  className="preview-inner"
                  ref={previewInnerRef}
                  onClick={overlayEnabled ? handlePreviewClick : undefined}
                  style={{ cursor: overlayEnabled ? 'crosshair' : 'default', position: 'relative' }}
                >
                  <video ref={videoRef} key={activeItem.id} src={activeItem.localUrl} controls className="video-preview"
                    onLoadedMetadata={e => {
                      const d = Math.floor(e.target.duration);
                      const vw = e.target.videoWidth  || 1080;
                      const vh = e.target.videoHeight || 1920;
                      updateItem(activeItem.id, { duration: d, endTime: d, videoWidth: vw, videoHeight: vh });
                      setVideoDurationState(d);
                      // Sync videoCurrentTime with the fresh <video> element (which starts at 0)
                      // so that titleVisible/overlayVisible recompute correctly when returning from another tab.
                      setVideoCurrentTime(e.target.currentTime || 0);
                    }}
                    onTimeUpdate={e => setVideoCurrentTime(e.target.currentTime)} />

                  {titleEnabled && activePreviewTitle && titleVisible && (
                    <div className="title-preview-overlay" style={titleOverlayStyle}>
                      {titleMode === 'emoji' ? (
                        <span style={{
                          fontSize: `clamp(14px, ${emojiSize * 0.22}px, 40px)`,
                          lineHeight: 1,
                          filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.3))',
                        }}>
                          {activePreviewTitle}
                        </span>
                      ) : titlePreviewData ? (
                        <img
                          src={titlePreviewData.png}
                          alt="title"
                          style={{ width: `${Math.round(titlePreviewData.cardW * 100 / ((BANNER_SIZES[bannerSize] || 82) / 100 * 1080))}%`, height: 'auto', display: 'block' }}
                        />
                      ) : null}
                    </div>
                  )}

                  {logoBase64 && logoEnabled && (
                    <img src={logoBase64} alt="watermark" className={`logo-video-preview logo-${logoPosition}`} style={logoPreviewStyle} />
                  )}

                  {/* ── Multi-overlay SVG items ── */}
                  {overlayVisible && overlayItems.map(item => (
                    <OverlayItemPreview
                      key={item.id}
                      item={item}
                      containerRef={previewInnerRef}
                      onMove={moveOverlayItem}
                      onSelect={setSelectedOverlay}
                      isSelected={selectedOverlay === item.id}
                    />
                  ))}

                  {overlayEnabled && (
                    <div style={{
                      position: 'absolute', bottom: 6, left: '50%', transform: 'translateX(-50%)',
                      fontSize: 10, color: 'rgba(255,255,255,0.7)', background: 'rgba(0,0,0,0.4)',
                      padding: '2px 8px', borderRadius: 10, pointerEvents: 'none', whiteSpace: 'nowrap',
                    }}>
                      🎯 Click to place · drag to move · ⌘Z undo
                    </div>
                  )}
                </div>
              ) : (
                <label className="upload-placeholder-label">
                  <input type="file" accept="video/*" multiple onChange={e => addVideosToQueue(e.target.files)} hidden />
                  <div className="upload-placeholder">
                    <div className="upload-icon">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                        <path d="M4 14.5V17a3 3 0 003 3h10a3 3 0 003-3v-2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        <path d="M12 3v11M8.5 9.5L12 13l3.5-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <p>{t('studio_drop')}</p>
                    <small>{t('studio_formats')}</small>
                    <small style={{ color: 'var(--text-dim)', fontSize: 10 }}>{t('studio_multi')}</small>
                  </div>
                </label>
              )}
            </div>



          </div>
            </div>
          <div className="zone-queue">
              <div className="queue">
                <div className="queue-header">
                  <span className="queue-title">QUEUE · {queue.filter(v => v && inQueue(v.studio)).length}</span>
                  <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                    <button className={`queue-multiselect-btn ${queueSelectMode ? 'active' : ''}`}
                      title="Multi-select mode — pick videos in the queue"
                      onClick={() => { setQueueSelectMode(v => !v); if (queueSelectMode) clearQueueSelection(); }}>
                      {queueSelectMode ? (selectedQueueIds.size > 0 ? `✓ ${selectedQueueIds.size} selected` : '✕ Cancel') : '☑ Select'}
                    </button>
                    {queueSelectMode && (
                      <button className="queue-multiselect-btn" onClick={selectAllQueue} style={{ fontSize: 10 }}>All</button>
                    )}
                    <button className="queue-multiselect-btn" title="Empty the queue and the render results"
                      onClick={() => { if ((results.length === 0 && renderQueue.length === 0) || window.confirm('Clear the queue and the rendered videos?')) clearStudioSession(); }}>Clear</button>
                    <label className="add-more-btn" title="Add videos to the queue"><input type="file" accept="video/*" multiple onChange={e => addVideosToQueue(e.target.files)} hidden />+ Add</label>
                  </div>
                </div>
                <div className="queue-sum">{studioSum.footer}</div>
                {queueSelectMode && (
                  <div className="queue-select-info">
                    {selectedQueueIds.size > 0
                      ? `${selectedQueueIds.size} video${selectedQueueIds.size > 1 ? 's' : ''} selected`
                      : 'Click videos ✓ to select them'}
                  </div>
                )}
                <div className="queue-list">
                  {queue.filter(item => item && inQueue(item.studio)).length === 0 && (
                    <div className="sq-empty">Queue is empty — exported videos are in the bar below.</div>
                  )}
                  {queue.filter(item => item && inQueue(item.studio)).map(item => {
                    const isSelected = selectedQueueIds.has(item.id);
                    const uploadFailed = item.status === 'error';
                    const color = uploadFailed ? STATUS_COLOR.failed : STATUS_COLOR[item.studio];
                    return (
                      <div key={item.id}
                        className={`queue-item ${item.id === (activeItem && activeItem.id) && !queueSelectMode ? 'active' : ''} status-${item.status} sq-item sq-item--${item.studio} ${queueSelectMode && isSelected ? 'queue-selected' : ''}`}
                        onClick={() => { if (queueSelectMode && item.status === 'done') toggleQueueSelect(item.id); else pickItem(item); }}>
                        {queueSelectMode && item.status === 'done' && (
                          <div className={`queue-checkbox ${isSelected ? 'checked' : ''}`}>{isSelected ? '✓' : ''}</div>
                        )}
                        <video src={item.localUrl} className="queue-thumb" muted />
                        <div className="queue-info">
                          <div className="queue-name">{item.file.name.length > 22 ? item.file.name.slice(0,22)+'…' : item.file.name}</div>
                          <div className="queue-status sq-status" style={{ color }}>
                            <i className="sq-dot" style={{ background: color }} />
                            {uploadFailed ? 'Upload failed' : STATUS_LABEL[item.studio]}
                          </div>
                          {item.studio === 'uploading' && !uploadFailed && (
                            <UploadBar id={item.id} />
                          )}
                        </div>
                        {item.status === 'done' && studioSum.perClip[item.id] && (
                          <span className={`sq-count sq-count--${studioSum.perClip[item.id].tone}`} title={`${studioSum.perClip[item.id].count} videos`}>{studioSum.perClip[item.id].count}</span>
                        )}
                        <button className="queue-remove" onClick={e => { e.stopPropagation(); removeItem(item.id); }}>✕</button>
                      </div>
                    );
                  })}
                </div>
              </div>
          </div>
          </div>

          {/* ── EDITING (~78%): the long editor scrolls; the footer keeps the recap, Done editing and Export all in view ── */}
          <div className="zone-work">
            <div className="zone-work-body">
          <div className="col-controls">
            <div className="section-card">

            {/* ── FEATURE ── */}
            <FeatureBar
              activeFeature={feature}
              onFeatureChange={(key) => { setFeature(key); }}
              onManage={() => setShowFeatureManager(true)}
              allScripts={COPY_STYLES}
              allVoices={voices}
              allMusic={musicBank}
            />

            {/* ── HOOKS ── */}
            <div className="control-section">
              <div className="section-title-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="section-title" style={{ marginBottom: 0 }}>{t('studio_hooks')} <span style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 400 }}>{hooksEnabled ? 'on' : 'off'}</span></span>
                <button
                  className={`pill-toggle ${hooksEnabled ? 'pill-on' : 'pill-off'}`}
                  onClick={() => setHooksEnabled(p => !p)}
                >
                  <span className="pill-track"><span className="pill-thumb" /></span>
                </button>
              </div>
              <div style={{ opacity: hooksEnabled ? 1 : 0.4, pointerEvents: hooksEnabled ? 'auto' : 'none', transition: 'opacity .15s' }}>
              {/* Upload progress bar */}
              {hookUploadProgress && (
                <div style={{ padding: '6px 0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 10, color: 'var(--text-dim)', marginBottom: 4 }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{hookUploadProgress.name}</span>
                    <span style={{ marginLeft: 'auto', flexShrink: 0 }}>{hookUploadProgress.percent}%</span>
                  </div>
                  <div style={{ height: 4, borderRadius: 2, background: 'var(--border)', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${hookUploadProgress.percent}%`, background: '#ff7900', borderRadius: 2, transition: 'width .15s' }} />
                  </div>
                </div>
              )}
              {hooks.length === 0 && !hookUploadProgress ? (
                <div style={{ fontSize: 11, color: 'var(--text-dim)', padding: '10px 0', textAlign: 'center' }}>
                  {t('studio_no_hooks')}
                </div>
              ) : hooks.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'row', gap: 8, overflowX: 'auto', paddingBottom: 4, paddingTop: 6, scrollbarWidth: 'thin' }}>
                  {(() => {
                    const matchesFeature = (h) => {
                      if (!feature || !h.tags || !h.tags.length) return true;
                      const labelToKey = Object.fromEntries(FEATURES.map(f => [f.label, f.key]));
                      const tags = h.tags.map(t => labelToKey[t] || t);
                      return tags.includes('all') || tags.includes('Universal') || tags.includes(feature);
                    };
                    const visual = hooks.filter(h => h.hookType !== 'talking' && matchesFeature(h));
                    const talking = hooks.filter(h => h.hookType === 'talking' && matchesFeature(h));
                    const items = visual.length > 0 && talking.length > 0
                      ? [...visual, { _divider: true }, ...talking]
                      : [...visual, ...talking];
                    return items;
                  })().map((h, idx) => {
                    if (h._divider) return (
                      <div key="__divider" style={{ flex: '0 0 1px', background: 'var(--border)', alignSelf: 'stretch', margin: '0 2px' }} />
                    );
                    const on = !!hookChecked[h.id];
                    const isPreviewing = hookPreviewId === h.id;
                    const src = hookVideoSrc(h);
                    const isTalking = h.hookType === 'talking';
                    const popoverOpen = hookPopoverId === h.id;
                    return (
                      <div key={h.id} style={{
                        flex: '0 0 120px', width: 120, minWidth: 120, maxWidth: 120, borderRadius: 8, position: 'relative',
                      }}>
                        <div style={{
                          borderRadius: 8, overflow: 'hidden',
                          border: `1.5px solid ${on ? 'var(--accent)' : 'transparent'}`,
                          outline: on ? 'none' : '0.5px solid var(--border)',
                          background: on ? 'rgba(255,121,0,.04)' : 'var(--surface)',
                          transition: 'border-color .15s',
                        }}>
                          {/* Top bar */}
                          {isTalking ? (
                            <div style={{ background: '#fff', color: '#ff7900', fontSize: 8, fontWeight: 700, textAlign: 'center', padding: '2px 0', letterSpacing: '.06em', borderBottom: '1px solid #ff7900' }}>
                              TALKING HOOK
                            </div>
                          ) : (
                            <div style={{ background: '#ff7900', color: '#fff', fontSize: 8, fontWeight: 700, textAlign: 'center', padding: '2px 0', letterSpacing: '.06em' }}>
                              VISUAL HOOK
                            </div>
                          )}
                          {/* Thumbnail */}
                          <div style={{ width: '100%', paddingTop: '177%', position: 'relative', background: '#111', overflow: 'hidden' }}
                            onClick={() => { if (isTalking) { toggleHookCheck(h.id); } else { setHookPreviewId(isPreviewing ? null : h.id); } }}>
                            {src
                              ? <video
                                  key={h.id + '-' + isPreviewing}
                                  src={src}
                                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', pointerEvents: isPreviewing ? 'auto' : 'none' }}
                                  muted={!isPreviewing || isTalking}
                                  controls={isPreviewing && !isTalking}
                                  autoPlay={isPreviewing && !isTalking}
                                  playsInline
                                />
                              : <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>🎬</div>
                            }
                            {!isPreviewing && src && !isTalking && (
                              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,.25)', pointerEvents: 'none' }}>
                                <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'rgba(255,255,255,.85)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <span style={{ fontSize: 10, marginLeft: 2 }}>▶</span>
                                </div>
                              </div>
                            )}
                            {/* Selection badge */}
                            <div className={`hook-check-circle ${on ? 'on' : ''}`}
                              style={{ position: 'absolute', top: 6, right: 6, zIndex: 3 }}
                              onClick={e => { e.stopPropagation(); toggleHookCheck(h.id); }} />
                          </div>
                          {/* Bottom bar — name */}
                          <div style={{ background: '#fff', padding: '5px 8px', position: 'relative' }} onClick={() => toggleHookCheck(h.id)}>
                            <div style={{ fontSize: 9, color: 'var(--text-dim)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingRight: 18 }}>
                              {h.name}
                            </div>
                            {/* "..." menu button */}
                            <div
                              style={{ position: 'absolute', top: '50%', right: 4, transform: 'translateY(-50%)', zIndex: 3, width: 20, height: 20, borderRadius: '50%', background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, cursor: 'pointer', lineHeight: 1 }}
                              onClick={e => { e.stopPropagation(); setHookPopoverId(popoverOpen ? null : h.id); }}>
                              &#8230;
                            </div>
                          </div>
                        </div>
                        {/* Inline popover */}
                        {popoverOpen && (
                          <>
                            <div style={{ position: 'fixed', inset: 0, zIndex: 19 }} onClick={() => setHookPopoverId(null)} />
                            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: 8, zIndex: 20, boxShadow: '0 4px 12px rgba(0,0,0,.15)' }}
                              onClick={e => e.stopPropagation()}>
                              <input
                                autoFocus
                                style={{ width: '100%', boxSizing: 'border-box', fontSize: 10, padding: '4px 6px', border: '1px solid var(--border)', borderRadius: 4, marginBottom: 6, fontFamily: 'DM Sans, sans-serif', background: 'var(--bg)', color: 'var(--text)' }}
                                value={hookNameDrafts[h.id] ?? h.name}
                                onChange={e => setHookNameDrafts(p => ({ ...p, [h.id]: e.target.value }))}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    const newName = (hookNameDrafts[h.id] ?? h.name).trim();
                                    if (newName) { saveHooks(prev => prev.map(x => x.id === h.id ? { ...x, name: newName } : x)); }
                                    setHookNameDrafts(p => { const n = { ...p }; delete n[h.id]; return n; });
                                    setHookPopoverId(null);
                                  }
                                }}
                                placeholder="Hook name"
                              />
                              <div style={{ display: 'flex', gap: 4 }}>
                                <button
                                  style={{ flex: 1, fontSize: 10, padding: '4px 0', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-dim)', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}
                                  onClick={() => {
                                    const newName = (hookNameDrafts[h.id] ?? h.name).trim();
                                    if (newName) { saveHooks(prev => prev.map(x => x.id === h.id ? { ...x, name: newName } : x)); }
                                    setHookNameDrafts(p => { const n = { ...p }; delete n[h.id]; return n; });
                                    setHookPopoverId(null);
                                  }}>
                                  Save
                                </button>
                                <button
                                  style={{ flex: 1, fontSize: 10, padding: '4px 0', borderRadius: 4, border: 'none', background: '#e53935', color: '#fff', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}
                                  onClick={async () => {
                                    setHookPopoverId(null);
                                    try { await axios.delete(`${API}/api/hook/${encodeURIComponent(h.serverFilename || h.id)}`); } catch(e) { console.error('[hook delete]', e); }
                                    saveHooks(prev => prev.filter(x => x.id !== h.id));
                                    setHookChecked(prev => { const n = { ...prev }; delete n[h.id]; localStorage.setItem('timbo_hook_checked', JSON.stringify(n)); return n; });
                                  }}>
                                  Delete
                                </button>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {/* Add hooks bar */}
              <div
                onClick={() => setShowAddHookModal(true)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: 36, marginTop: 8, background: '#fff', color: '#1a1a1a', border: '0.5px solid #e5e5e5', borderRadius: 8, fontFamily: "'DM Sans', sans-serif", fontSize: 11, cursor: 'pointer', boxSizing: 'border-box' }}>
                + Add your hooks
              </div>
              </div>
            </div>
            {/* ── SCRIPTS ── */}
            <div className="control-section">
              <div className="section-title-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="section-title" style={{ marginBottom: 0 }}>Scripts <span style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 400 }}>{scriptEnabled ? 'on' : 'off'}</span></span>
                <button
                  className={`pill-toggle ${scriptEnabled ? 'pill-on' : 'pill-off'}`}
                  onClick={() => { const next = !scriptEnabled; setScriptEnabled(next); if (next) setSoundEnabled(true); }}
                >
                  <span className="pill-track"><span className="pill-thumb" /></span>
                </button>
              </div>

              <div style={{ opacity: scriptEnabled ? 1 : 0.4, pointerEvents: scriptEnabled ? 'auto' : 'none', transition: 'opacity .15s' }}>
              {(() => {
                const cardStyle = { borderRadius: 8, border: '1px solid var(--border)', padding: '8px 10px', background: 'var(--surface)', position: 'relative', height: 200, display: 'flex', flexDirection: 'column', overflow: 'hidden' };
                const btn = { fontSize: 10, padding: '0 8px', height: 26, border: '0.5px solid var(--border-dark, var(--border))', borderRadius: 5, background: 'var(--surface)', color: 'var(--text-dim)', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', lineHeight: '26px' };
                const saveBtn = { ...btn, borderColor: '#ff7900', color: '#ff7900' };
                const selCircle = (on, onClick) => (
                  <div onClick={onClick} style={{ position: 'absolute', top: 8, right: 8, width: 18, height: 18, borderRadius: '50%', border: `2px solid ${on ? '#ff7900' : 'var(--border)'}`, background: on ? '#ff7900' : 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
                    {on && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
                  </div>
                );
                const taStyle = { width: '100%', boxSizing: 'border-box', fontSize: 13, padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 4, resize: 'vertical', fontFamily: 'sans-serif', lineHeight: 1.6, background: 'var(--bg)', color: 'var(--text)', marginTop: 6 };
                const scriptCards = [
                  { key: 'classic', label: '🏆 Classic Winning', sub: 'Proven structure · direct' },
                  { key: 'wild',    label: '🔥 Wild Voice',      sub: 'Unexpected · viral energy' },
                  { key: 'story',   label: '🎬 Storytelling',    sub: 'Problem → action → result' },
                ];
                const lastAsmrVo = savedVOs.filter(v => v.origin === 'asmr' || (!v.origin && v.name === 'ASMR')).slice(-1)[0];
                const lastVnVo = savedVOs.filter(v => v.origin === 'vn' || (!v.origin && v.name === 'WhatsApp Voice')).slice(-1)[0];
                return (<>
                {/* Row 1 — Scripts */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, alignItems: 'stretch', marginTop: 8 }}>
                  {(() => { const validatedCount = scriptCards.filter(s => getScript(s.key).trim()).length; return scriptCards.map(style => {
                    const isClassic = style.key === 'classic';
                    const text = getScript(style.key);
                    const isSelected = selectedStyles.includes(style.key);
                    return (
                      <div key={style.key} style={cardStyle}>
                        {validatedCount >= 2 && selCircle(isSelected, () => toggleStyle(style.key))}
                        <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--text)', paddingRight: 24 }}>{style.label}</div>
                        <div style={{ fontSize: 9, color: 'var(--text-dim)', marginBottom: 4 }}>{style.sub}</div>
                        <div style={{ display: 'flex', gap: 4 }} />
                        <textarea value={text} readOnly onChange={() => {}} rows={3} style={{ ...taStyle, flex: 1, caretColor: 'transparent', background: 'var(--surface)', color: 'var(--text-muted)', fontStyle: 'italic', cursor: 'default', resize: 'none' }}
                          placeholder={isClassic ? 'Write your winning script here...' : 'Write or paste your script here...'} />
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}><button onClick={() => setShowFeatureManager(true)} style={{ fontSize: 8, color: 'var(--text-dim)', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', background: 'none', border: 'none', padding: '2px 0' }}>Edit in Feature Manager {'\u2192'}</button></div>
                      </div>
                    );
                  }); })()}
                </div>
                </>);
              })()}
              </div>
            </div>

            {/* ── CONCEPTS ── */}
            <div className="control-section">
              <div className="section-title-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="section-title" style={{ marginBottom: 0 }}>Concept <span style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 400 }}>{conceptsEnabled ? 'on' : 'off'}</span></span>
                <button
                  className={`pill-toggle ${conceptsEnabled ? 'pill-on' : 'pill-off'}`}
                  onClick={() => setConceptsEnabled(v => !v)}
                >
                  <span className="pill-track"><span className="pill-thumb" /></span>
                </button>
              </div>
              <div style={{ opacity: conceptsEnabled ? 1 : 0.4, pointerEvents: conceptsEnabled ? 'auto' : 'none', transition: 'opacity .15s' }}>
              {(() => {
                const cardStyle = { borderRadius: 8, border: '1px solid var(--border)', padding: '8px 10px', background: 'var(--surface)', position: 'relative', height: 200, display: 'flex', flexDirection: 'column', overflow: 'hidden' };
                const selCircle = (on, onClick) => (
                  <div onClick={onClick} style={{ position: 'absolute', top: 8, right: 8, width: 18, height: 18, borderRadius: '50%', border: `2px solid ${on ? '#ff7900' : 'var(--border)'}`, background: on ? '#ff7900' : 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
                    {on && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
                  </div>
                );
                return (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, alignItems: 'stretch', marginTop: 8 }}>
                  {/* ASMR */}
                  <div style={cardStyle}>
                    {selCircle(selectedConcepts.includes('asmr'), () => toggleConcept('asmr'))}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 16, lineHeight: 1, flexShrink: 0 }}>🎙️</span>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--text)', lineHeight: 1.2 }}>ASMR</div>
                        <div style={{ fontSize: 9, color: 'var(--text-dim)', marginTop: 2 }}>Whisper · calm voice</div>
                      </div>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto' }}>
                      {savedVOs.filter(v => v.origin === 'asmr' && v.fromFM).length === 0
                        ? <div style={{ fontSize: 9, color: 'var(--accent)', paddingTop: 4, cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setShowFeatureManager(true)}>Add in Feature Manager →</div>
                        : savedVOs.filter(v => v.origin === 'asmr' && v.fromFM).map((vo, idx) => {
                          const itemId = vo.id || vo.key || `asmr_${idx}`;
                          const isSelected = selectedConceptAudio['asmr'] ? selectedConceptAudio['asmr'] === itemId : idx === 0;
                          return (
                            <div key={itemId} style={{ marginBottom: 8, cursor: 'pointer', borderRadius: 6, border: (isSelected && selectedConcepts.includes('asmr') && savedVOs.filter(v => v.origin === 'asmr' && v.fromFM).length > 1) ? '1px solid rgba(255,121,0,.35)' : '1px solid var(--border)', background: (isSelected && selectedConcepts.includes('asmr') && savedVOs.filter(v => v.origin === 'asmr' && v.fromFM).length > 1) ? 'rgba(255,121,0,.04)' : 'transparent', padding: '6px 8px', opacity: (!isSelected && savedVOs.filter(v => v.origin === 'asmr' && v.fromFM).length > 1) ? 0.45 : 1, transition: 'all .12s' }}
                              onClick={() => { setSelectedConceptAudio(prev => { const upd = {...prev, asmr: itemId}; try { localStorage.setItem('timbo_concept_audio_sel', JSON.stringify(upd)); } catch {} return upd; }); }}>
                              <audio src={vo.data || ''} style={{ display: 'none' }} ref={el => { if (el) el._conceptRef = true; }} onTimeUpdate={e => { const p = e.target.parentNode?.querySelector('.cp-prog'); const d = e.target.parentNode?.querySelector('.cp-dur'); if (p) p.style.width = (e.target.duration ? (e.target.currentTime/e.target.duration*100) : 0)+'%'; if (d) { const t=e.target.currentTime,dur=e.target.duration||0; const fmt=s=>`${Math.floor(s/60)}:${String(Math.floor(s%60)).padStart(2,'0')}`; d.textContent = fmt(t)+' / '+fmt(dur); } }} onLoadedMetadata={e => { const d = e.target.parentNode?.querySelector('.cp-dur'); const dur=e.target.duration||0; const fmt=s=>`${Math.floor(s/60)}:${String(Math.floor(s%60)).padStart(2,'0')}`; if(d) d.textContent='0:00 / '+fmt(dur); }} /><div style={{ display: 'flex', alignItems: 'center', gap: 7, height: 24, padding: '0 2px' }} onClick={e => { e.stopPropagation(); const a = e.currentTarget.previousSibling; if(a.paused){if(conceptAudioRef.current && conceptAudioRef.current !== a){conceptAudioRef.current.pause();conceptAudioRef.current.currentTime=0;}conceptAudioRef.current=a;a.play();}else{a.pause();conceptAudioRef.current=null;} const btn=e.currentTarget.querySelector('.cp-play'); if(btn){btn.style.opacity=a.paused?'1':'0.5';} }}>  <div className='cp-play' style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--surface)', border: '0.5px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer' }}>    <div style={{ width: 0, height: 0, borderStyle: 'solid', borderWidth: '4px 0 4px 7px', borderColor: 'transparent transparent transparent var(--text-muted)', marginLeft: 2 }} /></div>  <div style={{ flex: 1, height: 3, background: 'var(--border)', borderRadius: 2, position: 'relative', overflow: 'hidden' }}><div className='cp-prog' style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '0%', background: '#ff7900', borderRadius: 2 }} /></div>  <span className='cp-dur' style={{ fontSize: 9, color: 'var(--text-dim)', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>0:00 / --:--</span></div>
                              <div style={{ fontSize: 9, color: 'var(--text-dim)', marginTop: 3, paddingLeft: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 400 }}>{vo.name}</div>
                            </div>
                          );
                        })
                      }
                    </div>
                  </div>
{/* Musical Voiceover */}
                  <div style={cardStyle}>
                    {selCircle(selectedConcepts.includes('rap'), () => {
                      toggleConcept('rap');
                    })}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 16, lineHeight: 1, flexShrink: 0 }}>🎵</span>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--text)', lineHeight: 1.2 }}>Musical Voiceover</div>
                        <div style={{ fontSize: 9, color: 'var(--text-dim)', marginTop: 2 }}>Vocal · stylized tracks</div>
                      </div>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto' }}>
                      {raps.filter(r => !feature || !r.tags || r.tags.includes('all') || r.tags.includes('Universal') || r.tags.includes(feature)).length === 0
                        ? <div style={{ fontSize: 9, color: 'var(--accent)', paddingTop: 4, cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setShowFeatureManager(true)}>Add in Feature Manager →</div>
                        : raps.filter(r => !feature || !r.tags || r.tags.includes('all') || r.tags.includes('Universal') || r.tags.includes(feature)).map((r, idx) => {
                          const rapId = r.serverFilename || r.id || r.name;
                          const src = r.fromFM ? r.data : `${API}/api/rap/stream/${encodeURIComponent(r.serverFilename)}`;
                          const isSelected = selectedConceptAudio['rap'] ? selectedConceptAudio['rap'] === rapId : idx === 0;
                          return (
                            <div key={rapId} style={{ marginBottom: 8, cursor: 'pointer', borderRadius: 6, border: (isSelected && selectedConcepts.includes('rap') && raps.filter(r3 => r3.fromFM).length > 1) ? '1px solid rgba(255,121,0,.35)' : '1px solid var(--border)', background: (isSelected && selectedConcepts.includes('rap') && raps.filter(r3 => r3.fromFM).length > 1) ? 'rgba(255,121,0,.04)' : 'transparent', padding: '6px 8px', opacity: (!isSelected && raps.filter(r2 => !feature || !r2.tags || r2.tags.includes('all') || r2.tags.includes('Universal') || r2.tags.includes(feature)).length > 1) ? 0.45 : 1, transition: 'all .12s' }}
                              onClick={() => { setSelectedConceptAudio(prev => { const upd = {...prev, rap: rapId}; try { localStorage.setItem('timbo_concept_audio_sel', JSON.stringify(upd)); } catch {} return upd; }); setSelectedRap(rapId); localStorage.setItem('timbo_selected_rap', rapId || ''); if (!r.fromFM) setSelectedMusic(r.serverFilename); setMusicEnabled(true); }}>
                              <audio src={src || ''} style={{ display: 'none' }} onTimeUpdate={e => { const p=e.target.parentNode?.querySelector('.cp-prog'); const d=e.target.parentNode?.querySelector('.cp-dur'); if(p)p.style.width=(e.target.duration?(e.target.currentTime/e.target.duration*100):0)+'%'; if(d){const t=e.target.currentTime,dur=e.target.duration||0;const fmt=s=>`${Math.floor(s/60)}:${String(Math.floor(s%60)).padStart(2,'0')}`;d.textContent=fmt(t)+' / '+fmt(dur);}}} onLoadedMetadata={e=>{const d=e.target.parentNode?.querySelector('.cp-dur');const dur=e.target.duration||0;const fmt=s=>`${Math.floor(s/60)}:${String(Math.floor(s%60)).padStart(2,'0')}`;if(d)d.textContent='0:00 / '+fmt(dur);}} /><div style={{ display: 'flex', alignItems: 'center', gap: 7, height: 24, padding: '0 2px' }} onClick={e=>{ e.stopPropagation(); const a=e.currentTarget.previousSibling; if(a.paused){if(conceptAudioRef.current && conceptAudioRef.current !== a){conceptAudioRef.current.pause();conceptAudioRef.current.currentTime=0;}conceptAudioRef.current=a;a.play();}else{a.pause();conceptAudioRef.current=null;} }}>  <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--surface)', border: '0.5px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer' }}>    <div style={{ width: 0, height: 0, borderStyle: 'solid', borderWidth: '4px 0 4px 7px', borderColor: 'transparent transparent transparent var(--text-muted)', marginLeft: 2 }} /></div>  <div style={{ flex: 1, height: 3, background: 'var(--border)', borderRadius: 2, position: 'relative', overflow: 'hidden' }}><div className='cp-prog' style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '0%', background: '#ff7900', borderRadius: 2 }} /></div>  <span className='cp-dur' style={{ fontSize: 9, color: 'var(--text-dim)', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>0:00 / --:--</span></div>
                              <div style={{ fontSize: 9, color: 'var(--text-dim)', marginTop: 3, paddingLeft: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 400 }}>{r.name}</div>
                            </div>
                          );
                        })
                      }
                    </div>
                  </div>
{/* WhatsApp Voice */}
                  <div style={cardStyle}>
                    {selCircle(selectedConcepts.includes('whatsapp'), () => toggleConcept('whatsapp'))}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 16, lineHeight: 1, flexShrink: 0 }}>👵</span>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--text)', lineHeight: 1.2 }}>WhatsApp Voice</div>
                        <div style={{ fontSize: 9, color: 'var(--text-dim)', marginTop: 2 }}>Tech Mami · enthusiastic</div>
                      </div>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto' }}>
                      {savedVOs.filter(v => v.origin === 'whatsapp' && v.fromFM).length === 0
                        ? <div style={{ fontSize: 9, color: 'var(--accent)', paddingTop: 4, cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setShowFeatureManager(true)}>Add in Feature Manager →</div>
                        : savedVOs.filter(v => v.origin === 'whatsapp' && v.fromFM).map((vo, idx) => {
                          const itemId = vo.id || vo.key || `wa_${idx}`;
                          const isSelected = selectedConceptAudio['whatsapp'] ? selectedConceptAudio['whatsapp'] === itemId : idx === 0;
                          return (
                            <div key={itemId} style={{ marginBottom: 8, cursor: 'pointer', borderRadius: 6, border: (isSelected && selectedConcepts.includes('whatsapp') && savedVOs.filter(v => v.origin === 'whatsapp' && v.fromFM).length > 1) ? '1px solid rgba(255,121,0,.35)' : '1px solid var(--border)', background: (isSelected && selectedConcepts.includes('whatsapp') && savedVOs.filter(v => v.origin === 'whatsapp' && v.fromFM).length > 1) ? 'rgba(255,121,0,.04)' : 'transparent', padding: '6px 8px', opacity: (!isSelected && savedVOs.filter(v => v.origin === 'whatsapp' && v.fromFM).length > 1) ? 0.45 : 1, transition: 'all .12s' }}
                              onClick={() => { setSelectedConceptAudio(prev => { const upd = {...prev, whatsapp: itemId}; try { localStorage.setItem('timbo_concept_audio_sel', JSON.stringify(upd)); } catch {} return upd; }); }}>
                              <audio src={vo.data || ''} style={{ display: 'none' }} ref={el => { if (el) el._conceptRef = true; }} onTimeUpdate={e => { const p = e.target.parentNode?.querySelector('.cp-prog'); const d = e.target.parentNode?.querySelector('.cp-dur'); if (p) p.style.width = (e.target.duration ? (e.target.currentTime/e.target.duration*100) : 0)+'%'; if (d) { const t=e.target.currentTime,dur=e.target.duration||0; const fmt=s=>`${Math.floor(s/60)}:${String(Math.floor(s%60)).padStart(2,'0')}`; d.textContent = fmt(t)+' / '+fmt(dur); } }} onLoadedMetadata={e => { const d = e.target.parentNode?.querySelector('.cp-dur'); const dur=e.target.duration||0; const fmt=s=>`${Math.floor(s/60)}:${String(Math.floor(s%60)).padStart(2,'0')}`; if(d) d.textContent='0:00 / '+fmt(dur); }} /><div style={{ display: 'flex', alignItems: 'center', gap: 7, height: 24, padding: '0 2px' }} onClick={e => { e.stopPropagation(); const a = e.currentTarget.previousSibling; if(a.paused){if(conceptAudioRef.current && conceptAudioRef.current !== a){conceptAudioRef.current.pause();conceptAudioRef.current.currentTime=0;}conceptAudioRef.current=a;a.play();}else{a.pause();conceptAudioRef.current=null;} const btn=e.currentTarget.querySelector('.cp-play'); if(btn){btn.style.opacity=a.paused?'1':'0.5';} }}>  <div className='cp-play' style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--surface)', border: '0.5px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer' }}>    <div style={{ width: 0, height: 0, borderStyle: 'solid', borderWidth: '4px 0 4px 7px', borderColor: 'transparent transparent transparent var(--text-muted)', marginLeft: 2 }} /></div>  <div style={{ flex: 1, height: 3, background: 'var(--border)', borderRadius: 2, position: 'relative', overflow: 'hidden' }}><div className='cp-prog' style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '0%', background: '#ff7900', borderRadius: 2 }} /></div>  <span className='cp-dur' style={{ fontSize: 9, color: 'var(--text-dim)', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>0:00 / --:--</span></div>
                              <div style={{ fontSize: 9, color: 'var(--text-dim)', marginTop: 3, paddingLeft: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 400 }}>{vo.name}</div>
                            </div>
                          );
                        })
                      }
                    </div>
                  </div>
                </div>
                );
              })()}
              </div>
            </div>

            {/* Rap upload modal */}
            {showAddRapModal && (
              <div className="modal-overlay" onClick={() => setShowAddRapModal(false)}>
                <div className="modal-box" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
                  <div className="modal-box-header">
                    <span className="modal-box-title">+ Rap / Song Track</span>
                    <button className="modal-box-close" onClick={() => setShowAddRapModal(false)}>&#10005;</button>
                  </div>
                  <div className="modal-box-body">
                    <label className="modal-field-label">Track name</label>
                    <input id="rap-name-input" className="modal-field-input" placeholder="My track" style={{ marginBottom: 10 }} />
                    <label className="modal-field-label">Audio file</label>
                    <label className="upload-music-btn" style={{ display: 'inline-block', cursor: 'pointer', marginBottom: 10, padding: '6px 14px', fontSize: 11 }}>
                      Choose file
                      <input id="rap-file-input" type="file" accept="audio/*" style={{ display: 'none' }}
                        onChange={e => { const lbl = e.target.closest('label'); if (lbl && e.target.files[0]) lbl.dataset.chosen = e.target.files[0].name; }} />
                    </label>
                    <label className="modal-field-label" style={{ marginTop: 8 }}>Feature tag</label>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 10 }}>
                      {['Universal', ...FEATURES.map(f => f.key)].map(tag => (
                        <button key={tag} id={`rap-tag-${tag}`}
                          onClick={e => { document.querySelectorAll('[id^=rap-tag-]').forEach(b => b.style.background = 'none'); e.target.style.background = '#fff3e0'; e.target.dataset.selected = 'true'; }}
                          style={{ fontSize: 10, padding: '3px 8px', borderRadius: 3, cursor: 'pointer', border: '0.5px solid var(--border)', background: tag === 'Universal' ? '#fff3e0' : 'none', color: 'var(--text-dim)', fontFamily: 'DM Sans, sans-serif' }}>
                          {tag === 'Universal' ? 'Universal' : FEATURES.find(f => f.key === tag)?.label || tag}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="modal-box-footer">
                    <button className="modal-box-cancel" onClick={() => setShowAddRapModal(false)}>Cancel</button>
                    <button className="modal-box-confirm" onClick={async () => {
                      const nameEl = document.getElementById('rap-name-input');
                      const fileEl = document.getElementById('rap-file-input');
                      if (!fileEl?.files[0]) { alert('Select an audio file'); return; }
                      const name = (nameEl?.value || '').trim() || fileEl.files[0].name.replace(/\.[^.]+$/, '');
                      const selectedTag = document.querySelector('[id^=rap-tag-][data-selected=true]');
                      const tag = selectedTag ? selectedTag.textContent.trim() : 'Universal';
                      const fd = new FormData();
                      fd.append('rap', fileEl.files[0]);
                      fd.append('name', name);
                      fd.append('tags', JSON.stringify([tag === 'Universal' ? 'all' : tag]));
                      setShowAddRapModal(false);
                      setRapUploadProgress({ name, percent: 0 });
                      try {
                        const data = await new Promise((resolve, reject) => {
                          const xhr = new XMLHttpRequest();
                          xhr.open('POST', `${API}/api/upload-rap`);
                          xhr.upload.onprogress = (e) => { if (e.lengthComputable) setRapUploadProgress({ name, percent: Math.round((e.loaded / e.total) * 100) }); };
                          xhr.onload = () => { try { resolve(JSON.parse(xhr.responseText)); } catch(err) { reject(err); } };
                          xhr.onerror = () => reject(new Error('Network error'));
                          xhr.send(fd);
                        });
                        if (data.filename) { setRaps(prev => [...prev, { serverFilename: data.filename, name, tags: [tag === 'Universal' ? 'all' : tag] }]); }
                      } catch(e) { console.error('[rap upload]', e); }
                      setRapUploadProgress(null);
                    }}>Upload</button>
                  </div>
                </div>
              </div>
            )}

            {/* ── TITLE ── */}
            <div className="control-section title-section">
              <div className="section-title-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="section-title" style={{ marginBottom: 0 }}>{t('studio_title')} <span style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 400 }}>{titleEnabled ? 'on' : 'off'}</span></span>
                <button
                  className={`pill-toggle ${titleEnabled ? 'pill-on' : 'pill-off'}`}
                  onClick={() => setTitleEnabled(v => !v)}
                >
                  <span className="pill-track"><span className="pill-thumb" /></span>
                </button>
              </div>

              <div className="title-mode-toggle" style={{ marginBottom: 10 }}>
                <button
                  className={`title-mode-btn ${titleMode === 'text' ? 'active' : ''}`}
                  onClick={() => setTitleMode('text')}
                  disabled={!titleEnabled}
                >
                  Text
                </button>
                <button
                  className={`title-mode-btn ${titleMode === 'emoji' ? 'active' : ''}`}
                  onClick={() => setTitleMode('emoji')}
                  disabled={!titleEnabled}
                >
                  Emoji only
                </button>
              </div>

              {titleMode === 'text' && (
                <div className="title-top-grid">
                  <div className="title-top-col">
                    <span className="title-top-label">WINNING TITLES</span>
                    <input
                      list="winning-titles-list"
                      className="title-top-input"
                      disabled={!titleEnabled}
                      placeholder="Search..."
                      onChange={e => {
                        const val = e.target.value;
                        if (winningTitles.includes(val)) { handlePredefinedSelect(val); e.target.value = ''; }
                      }}
                    />
                    <datalist id="winning-titles-list">
                      {winningTitles.map((t, i) => (
                        <option key={i} value={t} />
                      ))}
                    </datalist>
                  </div>
                  <div className="title-top-col" style={{ justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => setShowTitleManager(true)}
                      disabled={!titleEnabled}
                      title="Manage titles"
                      className="title-gear-btn"
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#888" strokeWidth="1.5">
                        <circle cx="12" cy="12" r="3"/>
                        <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
                      </svg>
                    </button>
                  </div>
                  <div className="title-top-col">
                    <span className="title-top-label">TYPE YOUR OWN</span>
                    <input
                      className={`title-top-input ${!titleEnabled ? 'disabled' : ''}`}
                      value={customTitleDraft}
                      onChange={e => { setCustomTitleDraft(e.target.value); setTitleText(e.target.value); }}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && customTitleDraft.trim()) {
                          addTitleTag((customTitleDraft.trim() + (titleEmoji ? ' ' + titleEmoji : '')).trim());
                          setCustomTitleDraft('');
                          setTitleText('');
                        }
                      }}
                      placeholder="Type here…"
                      maxLength={60}
                      disabled={!titleEnabled}
                    />
                  </div>
                  <div className="title-top-col">
                    <span className="title-top-label">EMOJI</span>
                    <select
                      className="title-top-input"
                      disabled={!titleEnabled}
                      value={titleEmoji}
                      onChange={e => setTitleEmoji(e.target.value)}
                    >
                      {TITLE_EMOJI_OPTIONS.map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                    </select>
                  </div>
                </div>
              )}

              {titleMode === 'text' && selectedTitles.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  {selectedTitles.map((t, i) => (
                    <span
                      key={i}
                      onMouseEnter={() => setPreviewTitle(t)}
                      onMouseLeave={() => setPreviewTitle(null)}
                      onClick={() => setPreviewTitle(prev => prev === t ? null : t)}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        background: previewTitle === t ? '#c05a00' : 'var(--accent)',
                        border: previewTitle === t ? '2px solid #fff' : '2px solid transparent',
                        borderRadius: 20, padding: '4px 10px 4px 12px',
                        fontSize: 12, color: '#fff', maxWidth: '100%', fontWeight: 500,
                        cursor: 'pointer', transition: 'background 0.12s, border 0.12s',
                        userSelect: 'none',
                      }}
                      title="Hover to preview"
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>{t}</span>
                      <button
                        onClick={e => { e.stopPropagation(); setSelectedTitles(prev => prev.filter(x => x !== t)); if (previewTitle === t) setPreviewTitle(null); }}
                        style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)', cursor: 'pointer', fontSize: 14, padding: 0, lineHeight: 1, marginLeft: 2 }}
                      >×</button>
                    </span>
                  ))}
                  <button onClick={() => { setSelectedTitles([]); setPreviewTitle(null); }}
                    style={{ fontSize: 11, color: 'var(--text-muted)', background: 'none',
                      border: '1px solid var(--text-muted)', borderRadius: 12, padding: '2px 8px', cursor: 'pointer' }}>
                    Clear all
                  </button>
                </div>
              )}
              {titleMode === 'text' && selectedTitles.length > 0 && (
                <div style={{ fontSize: 11, color: previewTitle ? 'var(--accent)' : 'var(--text-muted)', marginBottom: 4 }}>
                  {previewTitle
                    ? `👁 Preview: ${previewTitle.slice(0, 30)}${previewTitle.length > 30 ? '…' : ''}`
                    : `${selectedTitles.length} title${selectedTitles.length > 1 ? 's' : ''} selected · survol = preview`
                  }
                </div>
              )}

              {titleMode === 'emoji' && (
                <>
                  <div className="emoji-grid-picker">
                    {TITLE_EMOJI_OPTIONS.filter(o => o.value).map(opt => (
                      <button key={opt.value}
                        className={`emoji-grid-btn ${titleEmoji === opt.value ? 'active' : ''}`}
                        onClick={() => setTitleEmoji(opt.value)} disabled={!titleEnabled}
                        title={opt.label}>
                        {opt.value}
                      </button>
                    ))}
                  </div>
                </>
              )}

              <div className="title-grid">
                {titleMode === 'text' && (<>
                  <span className="title-grid-label">BANNER</span>
                  <div className="title-grid-btns">
                    {Object.keys(BANNER_SIZES).map(k => (
                      <button key={k} className={`title-grid-btn ${bannerSize === k ? 'active' : ''}`}
                        onClick={() => setBannerSize(k)} disabled={!titleEnabled}>{k}</button>
                    ))}
                  </div>
                  <span className="title-grid-label">TEXT</span>
                  <div className="title-grid-btns">
                    {Object.keys(TEXT_SIZES).map(k => (
                      <button key={k} className={`title-grid-btn ${textSize === k ? 'active' : ''}`}
                        onClick={() => setTextSize(k)} disabled={!titleEnabled}>{k}</button>
                    ))}
                  </div>
                  <span className="title-grid-label">LINES</span>
                  <div className="title-grid-btns" style={{ maxWidth: '50%' }}>
                    <button className={`title-grid-btn ${titleMaxLines === 1 ? 'active' : ''}`}
                      onClick={() => setTitleMaxLines(1)} disabled={!titleEnabled}
                      title="Single line — font auto-shrinks if too long">1</button>
                    <button className={`title-grid-btn ${titleMaxLines === 2 ? 'active' : ''}`}
                      onClick={() => setTitleMaxLines(2)} disabled={!titleEnabled}
                      title="Up to 2 lines">2</button>
                  </div>
                </>)}
                {titleMode === 'emoji' && (<>
                  <span className="title-grid-label">SIZE</span>
                  <div className="title-grid-btns">
                    {Object.entries({ S: 40, M: 72, L: 120, XL: 180 }).map(([k, v]) => (
                      <button key={k} className={`title-grid-btn ${emojiSize === v ? 'active' : ''}`}
                        onClick={() => setEmojiSize(v)} disabled={!titleEnabled}>{k}</button>
                    ))}
                  </div>
                </>)}
                <span className="title-grid-label">HORIZONTAL</span>
                <div className="title-grid-slider-row">
                  <div className="title-grid-track" onClick={e => { if (!titleEnabled) return; const rect = e.currentTarget.getBoundingClientRect(); const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)); setTitleX(Math.round(5 + pct * 90)); }}>
                    <div className="title-grid-track-bg" />
                    <div className="title-grid-track-fill" style={{ width: `${((titleX - 5) / 90) * 100}%` }} />
                    <div className="title-grid-track-dot" style={{ left: `${((titleX - 5) / 90) * 100}%` }}
                      onMouseDown={e => { if (!titleEnabled) return; const bar = e.currentTarget.parentNode; const onMove = ev => { const rect = bar.getBoundingClientRect(); const pct = Math.max(0, Math.min(1, (ev.clientX - rect.left) / rect.width)); setTitleX(Math.round(5 + pct * 90)); }; const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); }; document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp); }} />
                  </div>
                  <span className="title-grid-val">{titleX}%</span>
                </div>
                <span className="title-grid-label">VERTICAL</span>
                <div className="title-grid-slider-row">
                  <div className="title-grid-track" onClick={e => { if (!titleEnabled) return; const rect = e.currentTarget.getBoundingClientRect(); const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)); setTitleY(Math.round(3 + pct * 92)); }}>
                    <div className="title-grid-track-bg" />
                    <div className="title-grid-track-fill" style={{ width: `${((titleY - 3) / 92) * 100}%` }} />
                    <div className="title-grid-track-dot" style={{ left: `${((titleY - 3) / 92) * 100}%` }}
                      onMouseDown={e => { if (!titleEnabled) return; const bar = e.currentTarget.parentNode; const onMove = ev => { const rect = bar.getBoundingClientRect(); const pct = Math.max(0, Math.min(1, (ev.clientX - rect.left) / rect.width)); setTitleY(Math.round(3 + pct * 92)); }; const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); }; document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp); }} />
                  </div>
                  <span className="title-grid-val">{titleY}%</span>
                </div>
              </div>
            </div>

            {/* ── OVERLAY ── */}
            {FEATURE_FLAGS.overlay && (
            <div className="control-section overlay-section">
              <div className="section-title-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span className="section-title" style={{ marginBottom: 0 }}>{t('studio_overlay')} <span style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 400 }}>{overlayEnabled ? 'on' : 'off'}</span></span>
                <button
                  className={`pill-toggle ${overlayEnabled ? 'pill-on' : 'pill-off'}`}
                  onClick={() => setOverlayEnabled(v => !v)}
                >
                  <span className="pill-track"><span className="pill-thumb" /></span>
                </button>
              </div>
              <div className={overlayEnabled ? '' : 'voice-section-muted'}>

                {/* ── Type: Arrow / Cross ── */}
                <div className="title-mode-toggle" style={{ marginBottom: 12 }}>
                  <button className={`title-mode-btn ${overlayType === 'arrow' ? 'active' : ''}`}
                    onClick={() => { setOverlayType('arrow'); if (selectedOverlay) updateOverlayItem(selectedOverlay, { type: 'arrow' }); }} disabled={!overlayEnabled}>
                    Arrow →
                  </button>
                  <button className={`title-mode-btn ${overlayType === 'cross' ? 'active' : ''}`}
                    onClick={() => { setOverlayType('cross'); if (selectedOverlay) updateOverlayItem(selectedOverlay, { type: 'cross' }); }} disabled={!overlayEnabled}>
                    Cross ✕
                  </button>
                </div>

                {/* ── Color ── */}
                <div style={{ marginBottom: 12 }}>
                  <div className="section-title" style={{ marginBottom: 6 }}>{t('studio_color')}</div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {OVERLAY_COLORS.map(col => (
                      <button key={col.key}
                        title={col.label}
                        onClick={() => {
                          setOverlayColor(col.key);
                          if (selectedOverlay) {
                            updateOverlayItem(selectedOverlay, { color: col.key });
                          }
                        }}
                        disabled={!overlayEnabled}
                        style={{
                          width: 28, height: 28, borderRadius: '50%',
                          background: col.hex,
                          border: overlayColor === col.key ? '2.5px solid var(--text)' : '1.5px solid var(--border-dark)',
                          cursor: 'pointer', padding: 0, flexShrink: 0,
                          boxShadow: overlayColor === col.key ? '0 0 0 2px var(--bg), 0 0 0 4px var(--text)' : 'none',
                          transition: 'all .12s',
                        }}
                      />
                    ))}
                  </div>
                </div>

                {/* ── Size slider ── */}
                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <div className="section-title" style={{ marginBottom: 0 }}>{t('studio_size')}</div>
                    <span style={{ fontSize: 11, fontFamily: 'DM Sans, sans-serif', color: 'var(--accent)', fontWeight: 700 }}>{overlaySize}px</span>
                  </div>
                  <input type="range" min={30} max={300} step={5} value={overlaySize}
                    onChange={e => { setOverlaySize(+e.target.value); if (selectedOverlay) updateOverlayItem(selectedOverlay, { size: +e.target.value }); }}
                    disabled={!overlayEnabled} style={{ width: '100%', accentColor: 'var(--accent)' }} />
                </div>

                {/* ── Direction: ligne horizontale (arrow only, en dernier) ── */}
                {overlayType === 'arrow' && (
                  <div style={{ marginBottom: 10 }}>
                    <div className="section-title" style={{ marginBottom: 6 }}>{t('studio_direction')}</div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {['nw','n','ne','w','e','sw','s','se'].map(key => (
                        <button key={key}
                          onClick={() => { setOverlayDir(key); if (selectedOverlay) updateOverlayItem(selectedOverlay, { dir: key }); }}
                          disabled={!overlayEnabled}
                          style={{
                            flex: 1, height: 34, borderRadius: 7, border: '1px solid',
                            borderColor: overlayDir === key ? 'var(--active-bg)' : 'var(--border-dark)',
                            background: overlayDir === key ? 'var(--active-bg)' : 'var(--surface)',
                            color: overlayDir === key ? 'var(--active-text)' : 'var(--text-muted)',
                            cursor: 'pointer', fontSize: 14, fontWeight: 700,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'all .1s',
                          }}>
                          {OVERLAY_DIRS[key].sym}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── Placed overlays list with per-item timing ── */}
                {overlayItems.length > 0 && (
                  <div style={{ marginTop: 4 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontSize: 10, color: 'var(--text-dim)', fontFamily: 'DM Sans, sans-serif', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Placed overlays
                      </span>
                      <button
                        onClick={() => { setOverlayItems([]); setSelectedOverlay(null); overlayHistoryRef.current = []; }}
                        style={{ background: 'none', border: 'none', fontSize: 10, color: 'var(--text-dim)', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}
                      >Clear all</button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {overlayItems.map(item => {
                        const col = (OVERLAY_COLORS.find(c => c.key === item.color) || OVERLAY_COLORS[0]).hex;
                        const isSel = selectedOverlay === item.id;
                        const videoDur = videoCurrentTime > 0 ? Math.max(videoCurrentTime, 5) : 30;
                        return (
                          <div key={item.id}
                            style={{
                              border: isSel ? '1.5px solid var(--accent)' : '1px solid var(--border)',
                              borderRadius: 7, padding: '7px 10px',
                              background: isSel ? 'var(--surface2)' : 'var(--surface)',
                              cursor: 'pointer',
                            }}
                            onClick={() => setSelectedOverlay(prev => prev === item.id ? null : item.id)}
                          >
                            {/* Item header */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ color: col, fontSize: 14 }}>
                                {item.type === 'arrow'
                                  ? <ArrowSVG color={col} size={14} />
                                  : <CrossSVG color={col} size={14} />}
                              </span>
                              <span style={{ fontSize: 11, fontFamily: 'DM Sans, sans-serif', color: 'var(--text-muted)', flex: 1 }}>
                                {item.type}{item.type === 'arrow' && item.dir ? ` · ${OVERLAY_DIRS[item.dir]?.sym}` : ''} · {item.size}px
                              </span>
                              {/* Per-item color dots */}
                              <div style={{ display: 'flex', gap: 3 }}>
                                {OVERLAY_COLORS.map(c => (
                                  <button key={c.key}
                                    onClick={e => { e.stopPropagation(); updateOverlayItem(item.id, { color: c.key }); }}
                                    style={{
                                      width: 13, height: 13, borderRadius: '50%', background: c.hex,
                                      border: item.color === c.key ? '2px solid var(--text)' : '1px solid transparent',
                                      cursor: 'pointer', padding: 0,
                                    }}
                                  />
                                ))}
                              </div>
                              <button
                                onClick={e => { e.stopPropagation(); removeOverlayItem(item.id); }}
                                style={{ background: 'none', border: 'none', color: 'var(--text-dim)', fontSize: 11, cursor: 'pointer', padding: '0 2px', fontFamily: 'DM Sans, sans-serif' }}
                              >✕</button>
                            </div>

                            {/* Per-item timing — always visible */}
                            <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}
                              onClick={e => e.stopPropagation()}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontSize: 10, color: 'var(--text-dim)', fontFamily: 'DM Sans, sans-serif', width: 20, flexShrink: 0 }}>In</span>
                                <input type="range" min={0} max={60} step={0.5} value={item.timeIn ?? 0}
                                  onChange={e => { e.stopPropagation(); updateOverlayItem(item.id, { timeIn: +e.target.value }); }}
                                  className="title-ctrl-slider" style={{ flex: 1, accentColor: '#F97316' }} />
                                <span style={{ fontSize: 10, color: 'var(--accent)', fontFamily: 'DM Sans, sans-serif', width: 28, textAlign: 'right', flexShrink: 0 }}>{(item.timeIn ?? 0).toFixed(1)}s</span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontSize: 10, color: 'var(--text-dim)', fontFamily: 'DM Sans, sans-serif', width: 20, flexShrink: 0 }}>Out</span>
                                <input type="range" min={0} max={60} step={0.5} value={item.timeOut ?? 30}
                                  onChange={e => { e.stopPropagation(); updateOverlayItem(item.id, { timeOut: +e.target.value }); }}
                                  className="title-ctrl-slider" style={{ flex: 1, accentColor: '#F97316' }} />
                                <span style={{ fontSize: 10, color: 'var(--accent)', fontFamily: 'DM Sans, sans-serif', width: 28, textAlign: 'right', flexShrink: 0 }}>{(item.timeOut ?? 30).toFixed(1)}s</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
            )}

            {/* ── VOICE + MUSIC ── */}
            <div className="voice-music-row">
              {/* ── VOICE ── */}
              <VoiceSection
                selectedVoiceIds={selectedVoiceIds} setSelectedVoiceIds={setSelectedVoiceIds}
                soundEnabled={soundEnabled} setSoundEnabled={setSoundEnabled}
                scriptEnabled={scriptEnabled}
                elApiKey={apiKey} t={t}
              />

              {/* ── MUSIC ── */}
              <div className="control-section vm-col">
                <div className="section-title-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span className="section-title" style={{ marginBottom: 0 }}>{t('studio_music')} <span style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 400 }}>{musicEnabled ? 'on' : 'off'}</span></span>
                  <button
                    className={`pill-toggle ${musicEnabled ? 'pill-on' : 'pill-off'}`}
                    onClick={() => setMusicEnabled(v => !v)}
                  >
                    <span className="pill-track"><span className="pill-thumb" /></span>
                  </button>
                </div>
                {/* music storage badge removed — count shown in search pill */}
                <div className={musicEnabled ? '' : 'voice-section-muted'}>
                  {musicMissing && selectedMusic !== 'none' && (
                    <div className="music-missing-banner">⚠ Track not found<button className="music-missing-clear" onClick={() => handleSelectMusic('none')}>✕</button></div>
                  )}
                  {(() => {
                    const allTracks = musicBank.filter(t => !t.fromFM && !t.filename?.startsWith('fm_'));
                    return (<>
                  <MusicLibrary tracks={allTracks} selected={selectedMusic} missing={musicMissing}
                    localFilenames={new Set(localTracks.map(lt => lt.filename))}
                    uploading={musicUploading} onUpload={handleMusicBatchUpload}
                    onSelect={handleSelectMusic} onDelete={deleteMusic} fuzzyMatch={fuzzyMatch} />
                  <div className="music-lib-wrap">
                    {musicUploadProgress.active && (
                      <div className={`music-upload-strip ${musicUploadProgress.error ? 'error' : ''}`}
                        onClick={musicUploadProgress.error ? () => setMusicUploadProgress({ active: false, filename: '', pct: 0, error: null }) : undefined}>
                        <span className="music-upload-strip-name" style={{ color: musicUploadProgress.error ? '#e53935' : '#1a1a1a' }}>
                          {musicUploadProgress.error ? '\u2717 ' + musicUploadProgress.error : musicUploadProgress.filename}
                        </span>
                        {!musicUploadProgress.error && (
                          <>
                            <div className="music-upload-strip-bar">
                              <div className="music-upload-strip-fill" style={{ width: musicUploadProgress.pct + '%' }} />
                            </div>
                            <span className="music-upload-strip-pct">{musicUploadProgress.pct}%</span>
                          </>
                        )}
                        {musicUploadProgress.error && <span className="music-upload-strip-pct" style={{ color: '#e53935' }}>Dismiss</span>}
                      </div>
                    )}
                  </div>
                  </>);
                  })()}
                  {musicBatchProgress && (
                    <div className="music-upload-row">
                      <div className="music-batch-progress">
                        <div className="music-batch-bar" style={{ width: `${Math.round((musicBatchProgress.done / musicBatchProgress.total) * 100)}%` }} />
                        <span className="music-batch-label">{musicBatchProgress.done} / {musicBatchProgress.total} uploading…</span>
                      </div>
                    </div>
                  )}

                  {selectedMusic !== 'none' && selectedMusicInfo && !musicMissing && (
                    <>
                      <div style={{ height: 0.5, background: '#e5e5e5', margin: '12px 0' }} />
                      <div>
                        <SectionLabel>TRIM</SectionLabel>
                        <MusicTrimmer filename={selectedMusic} duration={selectedMusicInfo.duration} musicStart={musicStartTime} onMusicStartChange={setMusicStartTime} videoDuration={videoDuration} />
                      </div>
                    </>
                  )}
                  <div style={{ height: 0.5, background: '#e5e5e5', margin: '12px 0' }} />
                  <div>
                    <SectionLabel>MIX</SectionLabel>
                    <div style={{ display: 'grid', gridTemplateColumns: '72px 1fr 40px 36px', rowGap: 6, columnGap: 8, alignItems: 'center' }}>
                      {/* Voice row */}
                      <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: '#1a1a1a' }}>voice</span>
                      <div style={{ position: 'relative', height: 14, display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                        onClick={e => { const rect = e.currentTarget.getBoundingClientRect(); const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)); handleLiveVoiceVol(+(pct * 1.5).toFixed(2)); }}>
                        <div style={{ position: 'absolute', left: 0, right: 0, height: 4, borderRadius: 2, background: '#1a1a1a' }} />
                        <div style={{ position: 'absolute', left: 0, width: `${Math.min(100, (liveVoiceVol / 1.5) * 100)}%`, height: 4, borderRadius: 2, background: '#F97316' }} />
                        <div style={{ position: 'absolute', left: `${Math.min(100, (liveVoiceVol / 1.5) * 100)}%`, width: 14, height: 14, borderRadius: '50%', background: '#F97316', border: '2px solid #fff', transform: 'translate(-50%, 0)', cursor: 'grab', zIndex: 1 }}
                          onMouseDown={e => {
                            const bar = e.currentTarget.parentNode;
                            const onMove = ev => { const rect = bar.getBoundingClientRect(); const pct = Math.max(0, Math.min(1, (ev.clientX - rect.left) / rect.width)); handleLiveVoiceVol(+(pct * 1.5).toFixed(2)); };
                            const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
                            document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp);
                          }} />
                      </div>
                      <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: '#F97316', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{Math.round(liveVoiceVol * 100)}%</span>
                      <span />
                      {/* Music row */}
                      <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: '#1a1a1a' }}>music</span>
                      <div style={{ position: 'relative', height: 14, display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                        onClick={e => { const rect = e.currentTarget.getBoundingClientRect(); const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)); handleLiveMusicVol(+pct.toFixed(2)); }}>
                        <div style={{ position: 'absolute', left: 0, right: 0, height: 4, borderRadius: 2, background: '#1a1a1a' }} />
                        <div style={{ position: 'absolute', left: 0, width: `${Math.min(100, liveMusicVol * 100)}%`, height: 4, borderRadius: 2, background: '#F97316' }} />
                        <div style={{ position: 'absolute', left: `${Math.min(100, liveMusicVol * 100)}%`, width: 14, height: 14, borderRadius: '50%', background: '#F97316', border: '2px solid #fff', transform: 'translate(-50%, 0)', cursor: 'grab', zIndex: 1 }}
                          onMouseDown={e => {
                            const bar = e.currentTarget.parentNode;
                            const onMove = ev => { const rect = bar.getBoundingClientRect(); const pct = Math.max(0, Math.min(1, (ev.clientX - rect.left) / rect.width)); handleLiveMusicVol(+pct.toFixed(2)); };
                            const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
                            document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp);
                          }} />
                      </div>
                      <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: '#F97316', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{Math.round(liveMusicVol * 100)}%</span>
                      {canPreviewMix ? (
                        <button onClick={playPreview}
                          style={{ width: 32, height: 32, borderRadius: '50%', background: previewPlaying ? '#F97316' : 'white', border: '1px solid #e5e5e5', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: 0 }}>
                          {previewPlaying
                            ? <div style={{ width: 8, height: 8, background: '#1a1a1a', borderRadius: 1 }} />
                            : <svg width="12" height="12" viewBox="0 0 24 24" fill="#1a1a1a"><path d="M8 5v14l11-7z"/></svg>}
                        </button>
                      ) : <span />}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {!scriptEnabled && (
              <div className="script-off-banner">
                <span>📝 Script off — title + music only, no voiceover</span>
              </div>
            )}


            </div>{/* end section-card */}
            {/* timing sliders for the clip being edited (they used to sit under the preview and took the queue's room) */}
            <div className="zone-timings">
            {titleEnabled && fullTitle && (
              <div className="title-timing-box">
                <div className="title-timing-header">
                  <span className="title-timing-label">📝 Title timing</span>
                </div>
                <div
                  style={{ position: 'relative', height: 28, margin: '6px 0 4px', cursor: 'pointer', userSelect: 'none' }}
                  ref={titleTimelineRef}
                  onMouseDown={e => {
                    if (!titleTimelineRef.current || clipDuration <= 0) return;
                    const rect = titleTimelineRef.current.getBoundingClientRect();
                    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
                    const t = Math.round(x * clipDuration * 10) / 10;
                    const dIn = Math.abs(t - titleTimeIn);
                    const dOut = Math.abs(t - titleOutVal);
                    setDraggingTitleHandle(dIn <= dOut ? 'in' : 'out');
                  }}
                >
                  <div style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', width: '100%', height: 5, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{ position: 'absolute', height: '100%', background: 'var(--active-bg)', left: `${(titleTimeIn / Math.max(clipDuration, 0.1)) * 100}%`, width: `${((titleOutVal - titleTimeIn) / Math.max(clipDuration, 0.1)) * 100}%` }} />
                  </div>
                  {clipDuration > 0 && <div style={{ position: 'absolute', top: 0, bottom: 0, width: 2, background: 'var(--text-dim)', borderRadius: 1, left: `${(videoCurrentTime / clipDuration) * 100}%`, pointerEvents: 'none' }} />}
                  {[{ val: titleTimeIn, which: 'in' }, { val: titleOutVal, which: 'out' }].map(({ val, which }) => (
                    <div key={which} style={{ position: 'absolute', top: '50%', transform: 'translate(-50%, -50%)', left: `${(val / Math.max(clipDuration, 0.1)) * 100}%`, width: 13, height: 22, background: 'var(--bg)', border: '1.5px solid var(--active-bg)', borderRadius: 4, cursor: 'ew-resize', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={{ width: 2, height: 10, background: 'var(--active-bg)', borderRadius: 1 }} />
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <button
                    className="title-timing-in"
                    title="Press I to mark in at current video time"
                    onClick={() => setTitleTimeIn(Math.min(Math.round(videoCurrentTime * 10) / 10, titleOutVal - 0.1))}
                    style={{ flex: 1, height: 32, border: '0.5px solid #e5e5e5', borderRadius: 8, background: '#fff', fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, color: '#1a1a1a', transition: 'background .15s, border-color .15s', padding: '0 10px' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f5f5f5'; e.currentTarget.style.borderColor = '#1a1a1a'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e5e5e5'; }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e' }} />
                    <span>IN</span>
                    <span style={{ color: '#F97316', fontWeight: 500 }}>{titleTimeIn.toFixed(1)}s</span>
                    <span style={{ fontSize: 9, color: '#888', background: '#f5f5f5', borderRadius: 3, padding: '1px 4px', marginLeft: 'auto' }}>I</span>
                  </button>
                  <button
                    className="title-timing-out"
                    title="Press O to mark out at current video time"
                    onClick={() => { const v = Math.round(videoCurrentTime * 10) / 10; setTitleTimeOut(Math.max(v, titleTimeIn + 0.1) >= clipDuration ? null : Math.max(v, titleTimeIn + 0.1)); }}
                    style={{ flex: 1, height: 32, border: '0.5px solid #e5e5e5', borderRadius: 8, background: '#fff', fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, color: '#1a1a1a', transition: 'background .15s, border-color .15s', padding: '0 10px' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f5f5f5'; e.currentTarget.style.borderColor = '#1a1a1a'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e5e5e5'; }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#e53935' }} />
                    <span>OUT</span>
                    <span style={{ color: '#F97316', fontWeight: 500 }}>{titleTimeOut != null ? `${titleTimeOut.toFixed(1)}s` : 'end'}</span>
                    <span style={{ fontSize: 9, color: '#888', background: '#f5f5f5', borderRadius: 3, padding: '1px 4px', marginLeft: 'auto' }}>O</span>
                  </button>
                </div>
              </div>
            )}



            {overlayEnabled && selectedOverlay && (() => {
              const item = overlayItems.find(it => it.id === selectedOverlay);
              if (!item) return null;
              return (
                <div className="title-timing-box" style={{ borderColor: 'var(--accent)', marginTop: 0 }}>
                  <div className="title-timing-header">
                    <span className="title-timing-label">🎯 Overlay timing</span>
                    <span className="title-timing-val">{(item.timeIn ?? 0).toFixed(1)}s → {item.timeOut != null ? `${item.timeOut.toFixed(1)}s` : 'end'}</span>
                  </div>
                  <div className="title-timing-row">
                    <span className="title-timing-key">In</span>
                    <input type="range" min={0} max={clipDuration} step={0.5} value={item.timeIn ?? 0}
                      onChange={e => updateOverlayItem(item.id, { timeIn: +e.target.value })}
                      className="title-ctrl-slider" style={{ flex: 1, accentColor: '#F97316' }} />
                    <span className="title-timing-num">{(item.timeIn ?? 0).toFixed(1)}s</span>
                  </div>
                  <div className="title-timing-row">
                    <span className="title-timing-key">Out</span>
                    <input type="range" min={0} max={clipDuration} step={0.5} value={item.timeOut ?? clipDuration}
                      onChange={e => updateOverlayItem(item.id, { timeOut: +e.target.value >= clipDuration ? null : +e.target.value })}
                      className="title-ctrl-slider" style={{ flex: 1, accentColor: '#F97316' }} />
                    <span className="title-timing-num">{item.timeOut != null ? `${item.timeOut.toFixed(1)}s` : 'end'}</span>
                  </div>
                </div>
              );
            })()}
            </div>
          </div>{/* end col-controls */}
            </div>
            <div className="zone-foot">{renderStudioBottom('foot')}</div>
          </div>

          {/* ── EXPORT: full width, compact; only videos whose export started ── */}
          <div className="zone-export">{renderStudioBottom('bar')}</div>
            <BatchStudioPanel queue={queue} results={results} renderQueue={renderQueue} reeditTarget={reeditTarget} setReeditTarget={setReeditTarget} confirmReedit={confirmReedit} />
        </div>{/* end workspace */}
        </TabErrorBoundary>
        )}

        {showTitleManager && (
          <div onClick={() => setShowTitleManager(false)}
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
            <div onClick={e => e.stopPropagation()}
              style={{ background: 'white', borderRadius: 12, width: 480, maxWidth: '90vw', padding: '20px 24px', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 500, color: '#1a1a1a' }}>Manage winning titles</span>
                <button onClick={() => setShowTitleManager(false)}
                  style={{ width: 32, height: 32, border: '0.5px solid #e5e5e5', borderRadius: 8, background: 'white', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: '#888', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>×</button>
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <input
                  value={titleManagerDraft}
                  onChange={e => setTitleManagerDraft(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && titleManagerDraft.trim()) {
                      saveWinningTitles([titleManagerDraft.trim(), ...winningTitles.filter(x => x !== titleManagerDraft.trim())]);
                      setTitleManagerDraft('');
                    }
                  }}
                  placeholder="Add a new title..."
                  style={{ flex: 1, height: 36, border: '0.5px solid #e5e5e5', borderRadius: 8, padding: '0 12px', fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: '#1a1a1a', outline: 'none' }}
                />
                <button
                  onClick={() => {
                    if (titleManagerDraft.trim()) {
                      saveWinningTitles([titleManagerDraft.trim(), ...winningTitles.filter(x => x !== titleManagerDraft.trim())]);
                      setTitleManagerDraft('');
                    }
                  }}
                  style={{ height: 36, padding: '0 16px', background: '#1a1a1a', color: 'white', border: 'none', borderRadius: 8, fontFamily: "'DM Sans', sans-serif", fontSize: 11, cursor: 'pointer' }}>
                  Add
                </button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 320, overflowY: 'auto' }}>
                {winningTitles.length === 0 && (
                  <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: '#888', padding: '12px 0', textAlign: 'center' }}>No titles yet</div>
                )}
                {winningTitles.map((t, i) => (
                  <div key={`${i}-${t}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: '0.5px solid #e5e5e5', borderRadius: 8 }}>
                    <span style={{ flex: 1, fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t}</span>
                    <button onClick={() => saveWinningTitles(winningTitles.filter((_, j) => j !== i))}
                      style={{ width: 28, height: 28, border: '0.5px solid #e5e5e5', borderRadius: 6, background: 'white', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: '#888', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, flexShrink: 0 }}>×</button>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 16, paddingTop: 12, borderTop: '0.5px solid #e5e5e5', display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={() => setShowTitleManager(false)}
                  style={{ height: 36, padding: '0 20px', background: '#1a1a1a', color: 'white', border: 'none', borderRadius: 8, fontFamily: "'DM Sans', sans-serif", fontSize: 11, cursor: 'pointer' }}>
                  Done
                </button>
              </div>
            </div>
          </div>
        )}

      </main>
    </div>
  );
}
